<template>
  <div class="framework-container">
    <div v-if="loading" class="loading-state">
      <div class="loader"></div>
      <span>Loading framework...</span>
    </div>

    <v-card v-else-if="levelling_framework?.levelling_framework_id" class="framework-content">
        <v-card-title class="page-title d-flex align-center" v-if="!hideTitle">
          Levelling Framework {{ levelling_framework.is_overall_default ? ' (RoleMapper Default)' : ''}}
          <v-spacer></v-spacer>
          <button v-if="hasAdminPermission" class="action-button-small" @click="openImportFrameworkDialog" title="Import Framework">
            <v-icon small left>mdi-import</v-icon> Import
          </button>
        </v-card-title>
        <v-card-text>
            <!-- Stable host for the tab bar. When this component renders inside
                 LevellingPageShell the row below is moved into the page head on
                 mount (design: the tabs live in the header, not on a row of
                 their own), leaving this host empty; elsewhere it stays put. -->
            <div class="rm-tabs-host" :class="{ 'is-inhead': tabsInHead }">
              <div ref="tabsRow" class="rm-tabs-row" :class="{ 'lvl-tabs--inhead': tabsInHead }">
                <div class="lvl-tabs" data-tour="fw-tabs">
                  <!-- Disabled rather than conditional, so the tabs that carry no
                       explainer keep exactly one markup path. -->
                  <v-tooltip
                    v-for="(t,ti) in tabViews.filter(x => x.visible && x.permitted)"
                    :key="'t' + ti"
                    bottom
                    content-class="fw-tip"
                    :disabled="!t.tip"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <button
                        type="button"
                        class="lvl-tab"
                        :class="{ on: selectedTab === t }"
                        v-bind="attrs"
                        v-on="on"
                        @click="selectedTab = t"
                      >{{t.name}}</button>
                    </template>
                    <span>{{ t.tip }}</span>
                  </v-tooltip>
                </div>
                <v-tooltip bottom content-class="fw-tip">
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="lvl-tab lvl-tab--gloss"
                      data-tour="fw-terms"
                      v-bind="attrs"
                      v-on="on"
                      @click="glossaryOpen = true"
                    >
                      <v-icon size="15">mdi-help-circle-outline</v-icon> Terms
                    </button>
                  </template>
                  <span>Look up any levelling term used on these tabs &mdash; anchors, coverage, read-across, divergence.</span>
                </v-tooltip>
                <v-tooltip v-if="activeTour" bottom content-class="fw-tip">
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="lvl-pagehead-tut"
                      data-testid="lvl-fw-tutorial"
                      v-bind="attrs"
                      v-on="on"
                      @click="activeTour.start()"
                    >
                      <v-icon size="16">mdi-school-outline</v-icon> Run tutorial
                    </button>
                  </template>
                  <span>{{ activeTourTip }}</span>
                </v-tooltip>
                <template v-if="hasAdminPermission">
                <v-icon v-show="!hideAllSettings" x-small color="DDDDDD" class="tabs-settings-icon" @click="openTabSettingsDialog">mdi-cog</v-icon>
                </template>
              </div>
            </div>

            <div v-if="selectedTab.viewType === 'level_track'" class="tab-grid-wrapper">
            <v-row class="filter-row" align="center" data-tour="fw-filter">
                <!-- Discipline: exclusive. One primary discipline is read at a
                     time; a second is opted into through "Compare with" so the
                     two sit side by side, rather than an unbounded multi-select
                     that silently widened the grid. -->
                <v-col cols="auto" class="filter-col">
                  <div class="filter-label">{{ levelling_framework.function_label }}</div>
                  <div class="lvl-fwseg" role="tablist" :aria-label="levelling_framework.function_label">
                    <button
                      v-for="f in levelling_framework.functions"
                      :key="'fn-' + f.lf_function_id"
                      type="button"
                      role="tab"
                      class="lvl-fwseg-btn"
                      :class="{ on: primaryFunctionId === f.lf_function_id }"
                      :aria-selected="primaryFunctionId === f.lf_function_id"
                      @click="setPrimaryFunction(f.lf_function_id)"
                    >{{ f.name }}</button>
                  </div>
                </v-col>
                <v-col v-if="compareFunctionOptions.length" cols="auto" class="filter-col">
                  <div class="filter-label">Compare with</div>
                  <div class="lvl-fwseg" role="tablist" aria-label="Compare with">
                    <button
                      type="button"
                      role="tab"
                      class="lvl-fwseg-btn"
                      :class="{ on: !compareFunctionId }"
                      :aria-selected="!compareFunctionId"
                      @click="setCompareFunction(null)"
                    >None</button>
                    <button
                      v-for="f in compareFunctionOptions"
                      :key="'cmp-' + f.lf_function_id"
                      type="button"
                      role="tab"
                      class="lvl-fwseg-btn"
                      :class="{ on: compareFunctionId === f.lf_function_id }"
                      :aria-selected="compareFunctionId === f.lf_function_id"
                      @click="setCompareFunction(f.lf_function_id)"
                    >{{ f.name }}</button>
                  </div>
                </v-col>
                <!-- View replaces the old "Levels and Tracks" / "Level
                     Descriptors" tab pair: same grid, descriptors toggled on. -->
                <v-col cols="auto" class="filter-col">
                  <div class="filter-label">View</div>
                  <div class="lvl-fwseg" role="tablist" aria-label="View">
                    <button
                      type="button"
                      role="tab"
                      class="lvl-fwseg-btn"
                      :class="{ on: !selectedTab.settings.showDescriptions }"
                      :aria-selected="!selectedTab.settings.showDescriptions"
                      @click="selectedTab.settings.showDescriptions = false"
                    >Compact</button>
                    <button
                      type="button"
                      role="tab"
                      class="lvl-fwseg-btn"
                      :class="{ on: selectedTab.settings.showDescriptions }"
                      :aria-selected="selectedTab.settings.showDescriptions"
                      @click="selectedTab.settings.showDescriptions = true"
                    >Descriptions</button>
                  </div>
                </v-col>
                <!-- Level windows are derived from the levels the tracks
                     actually occupy (see levelRangeOptions), not a fixed split. -->
                <v-col cols="auto" class="filter-col">
                  <div class="filter-label">{{ levelling_framework.level_label }}s</div>
                  <div class="lvl-fwseg" role="tablist" :aria-label="levelling_framework.level_label + ' range'">
                    <button
                      v-for="r in levelRangeOptions"
                      :key="'lr-' + r.value"
                      type="button"
                      role="tab"
                      class="lvl-fwseg-btn"
                      :class="{ on: levelRange === r.value }"
                      :aria-selected="levelRange === r.value"
                      :title="r.title"
                      @click="levelRange = r.value"
                    >{{ r.label }}</button>
                  </div>
                </v-col>
                <v-col cols="auto" class="filter-col">
                  <div class="filter-label">{{ levelling_framework.track_label }}s</div>
                  <div class="lvl-fw-trackchips" :role="isComparing ? 'radiogroup' : 'group'" :aria-label="'Filter ' + levelling_framework.track_label + 's'">
                    <button
                      v-for="track in sortedTracks"
                      :key="'tc-' + track.lf_track_id"
                      type="button"
                      class="lvl-fw-trackchip"
                      :class="{
                        'is-off': !isComparing && isTrackHidden(track.lf_track_id),
                        'is-muted': isComparing && isTrackHidden(track.lf_track_id),
                      }"
                      :role="isComparing ? 'radio' : undefined"
                      :aria-checked="isComparing ? !isTrackHidden(track.lf_track_id) : undefined"
                      :aria-pressed="isComparing ? undefined : !isTrackHidden(track.lf_track_id)"
                      :title="trackChipTitle(track)"
                      @click="toggleTrack(track.lf_track_id)"
                    >
                      <span
                        class="tk"
                        :style="{ backgroundColor: trackChipTint(track) }"
                      >{{ track.name.charAt(0) }}</span>{{ track.name }}
                    </button>
                  </div>
                </v-col>
                <v-col class="filter-col">
                    <v-spacer></v-spacer>
                  <v-menu v-if="hasAdminPermission && !hideAllSettings" offset-y :close-on-content-click="false">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        outlined
                        small class="settings-btn"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon  small>mdi-cog</v-icon>
                        <v-icon  small>mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="selectedTab.settings.fixedCellHeight = !selectedTab.settings.fixedCellHeight">
                        <v-list-item-action>
                          <v-checkbox v-model="selectedTab.settings.fixedCellHeight" dense hide-details></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>Fixed Cell Height</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
            </v-row>

            <!-- design: .lvl-fw-wnote / .lvl-fw-cfgnote (see frameworkNotes) -->
            <div class="fw-filter-notes">
              <v-tooltip v-for="(n, ni) in frameworkNotes" :key="'fwn-' + ni" bottom content-class="fw-tip">
                <template v-slot:activator="{ on, attrs }">
                  <span class="fw-note" v-bind="attrs" v-on="on">
                    <v-icon size="14">{{ n.icon }}</v-icon>
                    {{ n.text }}
                  </span>
                </template>
                <span>{{ n.tip }}</span>
              </v-tooltip>
            </div>

      <!-- Frameworks Display -->
      <div
        v-if="selectedFunctions.length > 0"
        class="function-section"
      >
        <!-- design: warning note above the grid card, edit mode only. Says what
             editing a cell actually costs, which is not obvious from the grid. -->
        <div v-if="editMode" class="fw-editnote">
          <v-icon size="17" class="fw-editnote-ic">mdi-alert-outline</v-icon>
          <div class="fw-editnote-main">
            <span class="t">Editing the framework</span>
            <span class="s">Names, descriptors and score ranges here define what every levelled role is read against. Changing a cell changes how future evaluations land on it; roles already levelled keep their level until they are re-levelled. Removing a cell takes that {{ (levelling_framework.level_track_label || 'job level').toLowerCase() }} off this {{ (levelling_framework.function_label || 'discipline').toLowerCase() }}&rsquo;s ladder.</span>
          </div>
        </div>

        <!-- design: the grid card's own header - what you are looking at on the
             left, the read-only/edit control on the right. -->
        <div class="fw-tablehead">
          <span class="fw-tablehead-title">{{ gridScopeTitle }}</span>
          <span class="fw-tablehead-meta">{{ gridScopeMeta }}</span>
          <span class="fw-tablehead-spacer"></span>
          <span class="fw-tablehead-state" :class="{ 'is-editing': editMode }">
            <v-icon size="14">{{ editMode ? 'mdi-pencil-outline' : 'mdi-lock-outline' }}</v-icon>
            {{ editMode ? 'Edit mode' : 'Read-only' }}
          </span>
          <button
            v-if="canEditFramework"
            type="button"
            class="fw-tablehead-btn"
            :title="editMode ? 'Return to read-only' : 'Make changes to this ' + (levelling_framework.function_label || 'discipline').toLowerCase() + '\'s ladder'"
            @click="setEditMode(!editMode)"
          >
            <v-icon size="15">{{ editMode ? 'mdi-check' : 'mdi-pencil-outline' }}</v-icon>
            {{ editMode ? 'Done editing' : 'Edit framework' }}
          </button>
        </div>

        <div class="framework-grid-wrapper" data-tour="fw-grid">
          <div class="framework-grid">
            <table class="grid-table">
              <thead>
                <!-- Track Header Row -->
                <tr class="header-row">
                  <th class="header-cell level-header">
                    <button
                      type="button"
                      class="level-sort"
                      :title="levelSortDesc
                        ? 'Showing the most senior ' + (levelling_framework.level_label || 'level').toLowerCase() + ' first. Click to start from the first.'
                        : 'Showing the first ' + (levelling_framework.level_label || 'level').toLowerCase() + ' first. Click to start from the most senior.'"
                      @click="levelSortDesc = !levelSortDesc"
                    >
                      {{ levelling_framework.level_label }}
                      <v-icon size="14">{{ levelSortDesc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                    </button>
                  </th>
                  <template v-for="f in selectedFunctions">
                    <th
                      v-for="track in filteredTracks"
                      :key="f.lf_function_id + '-' + track.lf_track_id"
                      class="header-cell track-header"
                    >
                      <div class="track-header-row">
                      <div class="track-code-chip is-tinted" :style="{ backgroundColor: chipColour(track.background_colour) }">{{ track.name.charAt(0) }}</div>
                      <div class="track-header-content">
                        <span>{{ isComparing ? f.name : track.name }}</span>
                      </div>
                      <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon small class="track-header-menu" v-bind="attrs" v-on="on">mdi-dots-horizontal</v-icon>
                        </template>
                        <v-list dense>
                          <v-list-item v-if="is_editable" @click="openTrackDialog(f, track, 'edit')">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>{{ levelling_framework.track_label }} Details</v-list-item-title>
                          </v-list-item>
                          <v-list-item v-else @click="openTrackDialog(f, track, 'view')">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>{{ levelling_framework.track_label }} Details</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="hideTrack(f, track)">
                            <v-list-item-icon class="mr-2"><v-icon small>mdi-eye-off-outline</v-icon></v-list-item-icon>
                            <v-list-item-title>Hide {{ levelling_framework.track_label }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      </div>
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <!-- Level Rows -->
                <tr
                  v-for="level in filteredLevels"
                  :key="level.lf_level_id"
                  class="level-row"
                >
                  <td class="level-cell" style="cursor: pointer;" @click="openLevelDescriptorDialog(level)">
                    <div class="level-name">{{ level.name }}</div>
                  </td>

                  <template v-for="f in selectedFunctions">
                    <td
                      v-for="track in filteredTracks"
                      :key="f.lf_function_id + '-' + level.lf_level_id + '-' + track.lf_track_id"
                      class="track-cell"
                    >
                      <div
                        v-if="getFunctionLevelTrack(f, level, track)"
                        :class="['track-content', { 'track-content-fixed': selectedTab.settings.fixedCellHeight }]"
                      >
                        <div class="track-cell-header">
                          <div class="track-title" :class="{ 'track-title--inline': selectedTab.settings.showDescriptions }"
                            :title="is_editable ? 'Edit this ' + levelling_framework.level_label + ' ' + levelling_framework.track_label
                              : (selectedTab.settings.showDescriptions ? 'Back to codes only' : 'Show the descriptor for every ' + levelling_framework.level_label)"
                            @click.stop="onTrackCellClick(f, level, track)">
                            <div class="track-code-chip is-tinted" :style="{ backgroundColor: chipColour(track.background_colour) }">
                              <!-- <GridInlineEdit
                                :editing="isGridInlineEditing('lf_level_function_track', 'abbreviation', getFunctionLevelTrack(f, level, track))"
                                :value="gridInlineEdit.value"
                                :editable="is_editable"
                                @input="gridInlineEdit.value = $event"
                                @save="saveGridInlineEdit(getFunctionLevelTrack(f, level, track))"
                                @cancel="cancelGridInlineEdit"
                                @start="startGridInlineEdit('lf_level_function_track', 'abbreviation', getFunctionLevelTrack(f, level, track))"
                              >{{ getFunctionLevelTrack(f, level, track).abbreviation }}</GridInlineEdit> -->
                              {{ getFunctionLevelTrack(f, level, track).abbreviation }}
                            </div>
                            <!-- <GridInlineEdit
                              :editing="isGridInlineEditing('lf_level_function_track', 'name', getFunctionLevelTrack(f, level, track))"
                              :value="gridInlineEdit.value"
                              :editable="is_editable"
                              @input="gridInlineEdit.value = $event"
                              @save="saveGridInlineEdit(getFunctionLevelTrack(f, level, track))"
                              @cancel="cancelGridInlineEdit"
                              @start="startGridInlineEdit('lf_level_function_track', 'name', getFunctionLevelTrack(f, level, track))"
                            >{{ getFunctionLevelTrack(f, level, track).name }}</GridInlineEdit> -->
                            <span class="track-name">{{ getFunctionLevelTrack(f, level, track).name }}</span>
                          </div>
                          <div class="track-cell-icons">
                            <v-tooltip bottom v-if="incompleteFactorCount(f, level, track)">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  small
                                  color="orange"
                                  v-bind="attrs"
                                  v-on="on"
                                >mdi-alert-outline</v-icon>
                              </template>
                              <span>{{ incompleteFactorCount(f, level, track) }} missing sub-{{ levelling_framework.factor_label || 'factor' }} assignment{{ incompleteFactorCount(f, level, track) > 1 ? 's' : '' }}</span>
                            </v-tooltip>
                            <v-menu offset-y left>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  small
                                  class="track-menu-icon"
                                  v-bind="attrs"
                                  v-on="on"
                                >mdi-dots-horizontal</v-icon>
                              </template>
                              <v-list dense>
                                <v-list-item v-if="is_editable" @click="openLftDialog(f, level, track, 'edit')">
                                  <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                                  <v-list-item-title>Edit {{ levelling_framework.level_label }} {{ levelling_framework.track_label }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-else @click="openLftDialog(f, level, track, 'view')">
                                  <v-list-item-icon class="mr-2"><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                                  <v-list-item-title>View {{ levelling_framework.level_label }} {{ levelling_framework.track_label }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="is_editable" @click="openFactorsDialog(f, level, track, 'edit')">
                                  <v-list-item-icon class="mr-2"><v-icon small>mdi-tune</v-icon></v-list-item-icon>
                                  <v-list-item-title>Edit {{ levelling_framework.factor_label }}s</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-else @click="openFactorsDialog(f, level, track, 'view')">
                                  <v-list-item-icon class="mr-2"><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                                  <v-list-item-title>View {{ levelling_framework.factor_label }}s</v-list-item-title>
                                </v-list-item>
                                <v-list-item v-if="is_editable" @click="deleteLevelFunctionTrack(getFunctionLevelTrack(f, level, track))">
                                  <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                                  <v-list-item-title style="color: #e74c3c;">Delete</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                        </div>
                        <div
                          v-if="selectedTab.settings.showDescriptions"
                          class="track-description"
                        >
                          <GridInlineEdit
                            type="textarea"
                            :editing="isGridInlineEditing('lf_level_function_track', 'description', getFunctionLevelTrack(f, level, track))"
                            :value="gridInlineEdit.value"
                            :editable="is_editable"
                            @input="gridInlineEdit.value = $event"
                            @save="saveGridInlineEdit(getFunctionLevelTrack(f, level, track))"
                            @cancel="cancelGridInlineEdit"
                            @start="startGridInlineEdit('lf_level_function_track', 'description', getFunctionLevelTrack(f, level, track))"
                          >{{ getFunctionLevelTrack(f, level, track).description || 'No description available' }}</GridInlineEdit>
                        </div>
                      </div>
                      <div
                        v-else
                        :class="['track-empty', { 'track-empty-edge': shouldShowAddButtonForFramework(f, level, track) }]"
                      >
                        <button v-if="shouldShowAddButtonForFramework(f, level, track)" class="add-track-btn" @click="openLftDialog(f, level, track)">+ Add</button>
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
            </div><!-- end shared grid for tabs 0 & 1 -->

            <!-- Tab 2: Factor Descriptors -->
            <div v-show="selectedTab.viewType === 'factors'" class="descriptor-tab-content pa-4">
                <v-row>
                    <v-col
                        v-for="factor in levelling_framework.factors"
                        :key="factor.lf_factor_id"
                        cols="12"
                        md="6"
                    >
                        <v-card outlined class="factor-descriptor-card mb-4" :style="{ borderLeft: '4px solid ' + factor.background_colour }">
                            <v-card-title class="subtitle-1 font-weight-bold pb-2">
                                <v-icon left small :color="factor.background_colour">mdi-circle</v-icon>
                                {{ factor.name }}
                            </v-card-title>
                            <v-card-text class="text-body-2">
                                {{ factor.description }}
                            </v-card-text>
                            <v-card-text class="pt-0">
                                <div class="caption font-weight-medium mb-1">Sub-Factors:</div>
                                <v-chip
                                    v-for="sf in factor.sub_factors"
                                    :key="sf.lf_sub_factor_id"
                                    small
                                    outlined
                                    class="mr-1 mb-1"
                                >
                                    {{ sf.name }}
                                </v-chip>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </div>

            <!-- Tab 3: Sub-Factor Descriptors -->
            <div v-show="selectedTab.viewType === 'sub_factors'" class="descriptor-tab-content pa-4">
                <!-- design: .lvl-fw-wnote / .lvl-fw-cfgnote (see frameworkNotes) -->
                <div class="fw-filter-notes">
                  <v-tooltip v-for="(n, ni) in frameworkNotes" :key="'fwn-' + ni" bottom content-class="fw-tip">
                    <template v-slot:activator="{ on, attrs }">
                      <span class="fw-note" v-bind="attrs" v-on="on">
                        <v-icon size="14">{{ n.icon }}</v-icon>
                        {{ n.text }}
                      </span>
                    </template>
                    <span>{{ n.tip }}</span>
                  </v-tooltip>
                </div>

                <!-- design: LvlFactorList - one card per factor group, a row per
                     sub-factor, opening into the right-hand drill panel. -->
                <div class="sf-groups">
                    <div
                        v-for="factor in levelling_framework.factors"
                        :key="'sf-' + factor.lf_factor_id"
                        class="sf-group"
                    >
                        <div class="sf-group-tag">
                            <span class="sf-group-dot" :style="{ backgroundColor: chipColour(factor.background_colour) }"></span>
                            {{ factor.name }}
                        </div>
                        <div class="sf-card">
                            <button
                                v-for="sf in factor.sub_factors"
                                :key="sf.lf_sub_factor_id"
                                type="button"
                                class="sf-row"
                                :class="{ 'is-open': (factorDrill.subFactor || {}).lf_sub_factor_id === sf.lf_sub_factor_id }"
                                @click="openFactorDrill(factor, sf)"
                            >
                                <span class="sf-row-main">
                                    <span class="sf-row-name">{{ sf.name }}</span>
                                    <span v-if="sf.description" class="sf-row-blurb">{{ sf.description }}</span>
                                </span>
                                <span class="sf-row-val">
                                    <span class="sf-row-count">{{ (sf.sub_factor_levels || []).length }} levels</span>
                                    <v-icon size="18">mdi-chevron-right</v-icon>
                                </span>
                            </button>
                            <div v-if="!factor.sub_factors || !factor.sub_factors.length" class="sf-row sf-row--empty">
                                No sub-{{ (levelling_framework.factor_label || 'factor').toLowerCase() }}s recorded.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- design: LvlFactorDrill - the levels for one sub-factor, in a
                 right-hand panel rather than expanded inline, so the list stays
                 in place while a sub-factor is read. -->
            <div v-if="factorDrill.isOpen" class="sf-drill" role="dialog" :aria-label="(factorDrill.subFactor || {}).name">
                <div class="sf-drill-scrim" @click="closeFactorDrill"></div>
                <div class="sf-drill-panel">
                    <div class="sf-drill-head">
                        <div class="sf-drill-head-top">
                            <div>
                                <span class="sf-group-tag">
                                    <span class="sf-group-dot" :style="{ backgroundColor: chipColour((factorDrill.factor || {}).background_colour) }"></span>
                                    {{ (factorDrill.factor || {}).name }}
                                </span>
                                <h3>{{ (factorDrill.subFactor || {}).name }}</h3>
                            </div>
                            <button type="button" class="sf-drill-close" aria-label="Close" @click="closeFactorDrill">&times;</button>
                        </div>
                        <p v-if="(factorDrill.subFactor || {}).description" class="sf-drill-blurb">
                            {{ factorDrill.subFactor.description }}
                        </p>
                    </div>
                    <div class="sf-drill-body">
                        <div
                            v-for="level in ((factorDrill.subFactor || {}).sub_factor_levels || [])"
                            :key="level.lf_sub_factor_level_id"
                            class="sf-level"
                        >
                            <span class="sf-level-n">{{ level.position }}</span>
                            <span class="sf-level-main">
                                <span class="sf-level-t">{{ level.name }}</span>
                                <span v-if="level.description" class="sf-level-d">{{ level.description }}</span>
                                <ul v-if="scopeLines(level.scope).length" class="sf-level-scope">
                                    <li v-for="(line, li) in scopeLines(level.scope)" :key="'sc-' + li">{{ line }}</li>
                                </ul>
                            </span>
                        </div>
                        <div v-if="!((factorDrill.subFactor || {}).sub_factor_levels || []).length" class="sf-drill-empty">
                            No proficiency levels recorded for this sub-{{ (levelling_framework.factor_label || 'factor').toLowerCase() }}.
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tab 4: Level Track Factors -->
            <div v-show="selectedTab.viewType === 'level_track_factors'" class="tab-grid-wrapper">
                <v-row class="filter-row" align="center">
                    <!-- Discipline: exclusive, matching Levels and tracks. -->
                    <v-col cols="auto" class="filter-col">
                        <div class="filter-label">{{ levelling_framework.function_label }}</div>
                        <div class="lvl-fwseg" role="tablist" :aria-label="levelling_framework.function_label">
                            <button
                                v-for="f in levelling_framework.functions"
                                :key="'ltf-fn-' + f.lf_function_id"
                                type="button"
                                role="tab"
                                class="lvl-fwseg-btn"
                                :class="{ on: (ltfFunction || {}).lf_function_id === f.lf_function_id }"
                                :aria-selected="(ltfFunction || {}).lf_function_id === f.lf_function_id"
                                @click="ltfFunctionId = f.lf_function_id"
                            >{{ f.name }}</button>
                        </div>
                    </v-col>
                    <v-col cols="auto" class="filter-col">
                        <div class="filter-label">{{ levelling_framework.level_label }}s</div>
                        <div class="lvl-fwseg" role="tablist" :aria-label="levelling_framework.level_label + ' range'">
                            <button
                                v-for="r in levelRangeOptions"
                                :key="'ltf-lr-' + r.value"
                                type="button"
                                role="tab"
                                class="lvl-fwseg-btn"
                                :class="{ on: ltfLevelRange === r.value }"
                                :aria-selected="ltfLevelRange === r.value"
                                :title="r.title"
                                @click="ltfLevelRange = r.value"
                            >{{ r.label }}</button>
                        </div>
                    </v-col>
                    <!-- Track: one at a time - the grid is levels x factor groups
                         for a single ladder. Unpicked tracks are desaturated
                         rather than struck through; they are not hidden. -->
                    <v-col cols="auto" class="filter-col">
                        <div class="filter-label">{{ levelling_framework.track_label }}</div>
                        <div class="lvl-fw-trackchips" role="radiogroup" :aria-label="levelling_framework.track_label">
                            <button
                                v-for="track in sortedTracks"
                                :key="'ltf-tc-' + track.lf_track_id"
                                type="button"
                                class="lvl-fw-trackchip"
                                :class="{ 'is-muted': !isLtfTrackSelected(track.lf_track_id) }"
                                role="radio"
                                :aria-checked="isLtfTrackSelected(track.lf_track_id)"
                                :title="'Every ' + (levelling_framework.factor_label || 'factor').toLowerCase() + ' for ' + track.name"
                                @click="selectLtfTrack(track.lf_track_id)"
                            >
                                <span class="tk" :style="{ backgroundColor: ltfTrackChipTint(track) }">{{ track.name.charAt(0) }}</span>{{ track.name }}
                            </button>
                        </div>
                    </v-col>
                    <!-- Or pin a factor group instead, and read it across every
                         track. One selection across both pill groups. -->
                    <v-col cols="auto" class="filter-col">
                        <div class="filter-label">{{ levelling_framework.factor_label }}</div>
                        <div class="lvl-fw-trackchips" role="radiogroup" :aria-label="levelling_framework.factor_label">
                            <button
                                v-for="factor in ltfWeightedFactors"
                                :key="'ltf-fc-' + factor.lf_factor_id"
                                type="button"
                                class="lvl-fw-trackchip"
                                :class="{ 'is-muted': !isLtfFactorSelected(factor.lf_factor_id) }"
                                role="radio"
                                :aria-checked="isLtfFactorSelected(factor.lf_factor_id)"
                                :title="factor.name + ' \u2014 across every ' + (levelling_framework.track_label || 'track').toLowerCase()"
                                @click="selectLtfFactor(factor.lf_factor_id)"
                            >
                                <span class="tk" :style="{ backgroundColor: chipColour(factor.background_colour) }">{{ factor.name.charAt(0) }}</span>{{ shortName(factor.name) || factor.name }}
                            </button>
                        </div>
                    </v-col>
                    <v-col class="filter-col">
                        <v-spacer></v-spacer>
                        <v-menu v-if="hasAdminPermission && !hideAllSettings" offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                outlined
                                small class="settings-btn"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon  small>mdi-cog</v-icon>
                                <v-icon  small>mdi-chevron-down</v-icon>
                              </v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item @click="selectedTab.settings.fixedCellHeight = !selectedTab.settings.fixedCellHeight">
                                    <v-list-item-action>
                                        <v-checkbox v-model="selectedTab.settings.fixedCellHeight" dense hide-details></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>Fixed Cell Height</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-title>Min Column Width</v-list-item-title>
                                        <v-text-field
                                            v-model.number="selectedTab.settings.minColumnWidth"
                                            type="number"
                                            dense
                                            hide-details
                                            suffix="px"
                                            placeholder="0 = no limit"
                                            style="max-width: 140px;"
                                        ></v-text-field>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-title>Max Column Width</v-list-item-title>
                                        <v-text-field
                                            v-model.number="selectedTab.settings.maxColumnWidth"
                                            type="number"
                                            dense
                                            hide-details
                                            suffix="px"
                                            placeholder="0 = no limit"
                                            style="max-width: 140px;"
                                        ></v-text-field>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-col>
                </v-row>

                <!-- design: .lvl-fw-wnote / .lvl-fw-cfgnote (see frameworkNotes) -->
                <div class="fw-filter-notes">
                  <v-tooltip v-for="(n, ni) in frameworkNotes" :key="'fwn-' + ni" bottom content-class="fw-tip">
                    <template v-slot:activator="{ on, attrs }">
                      <span class="fw-note" v-bind="attrs" v-on="on">
                        <v-icon size="14">{{ n.icon }}</v-icon>
                        {{ n.text }}
                      </span>
                    </template>
                    <span>{{ n.tip }}</span>
                  </v-tooltip>
                </div>

                <div v-if="ltfColumnGroups.length > 0" class="function-section" style="position: relative;">
                    <!-- What this grid is actually showing: the filters above say what
                         was chosen, not what came back from it. -->
                    <p v-if="ltfScopeNote" class="ltf-scope-note">{{ ltfScopeNote }}</p>
                    <div :class="['framework-grid-wrapper', { 'ltf-dimmed': ltfCellPopup.isOpen }]">
                        <table class="grid-table ltf-table">
                            <thead>
                                <!-- Factor header row -->
                                <tr class="header-row">
                                    <th class="header-cell level-header">
                                      <button
                                        type="button"
                                        class="level-sort"
                                        :title="levelSortDesc
                                          ? 'Showing the most senior ' + (levelling_framework.level_label || 'level').toLowerCase() + ' first. Click to start from the first.'
                                          : 'Showing the first ' + (levelling_framework.level_label || 'level').toLowerCase() + ' first. Click to start from the most senior.'"
                                        @click="levelSortDesc = !levelSortDesc"
                                      >
                                        {{ levelling_framework.level_label }}
                                        <v-icon size="14">{{ levelSortDesc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                                      </button>
                                    </th>
                                    <template v-for="group in ltfColumnGroups">
                                        <th
                                            v-for="(factor,fi) in ltfFactorTypes"
                                            :key="'ltf-h-' + group.lf_function_id + '-' + group.lf_track_id + '-' + factor.lf_factor_id"
                                            class="header-cell ltf-factor-header"
                                            :class="{ 'ltf-factor-last': fi === ltfFactorTypes.length - 1 }"
                                        >
                                            {{ isLtfByFactor ? group.lf_track_name : factor.name }}
                                        </th>
                                    </template>
                                </tr>
                            </thead>
                            <tbody>
                                <!-- Filtering empty levels out makes "no rows" reachable:
                                     say which combination came up empty rather than
                                     leaving a headed table with nothing under it. -->
                                <tr v-if="!ltfLevels.length" class="level-row">
                                    <td :colspan="ltfColumnGroups.length * ltfFactorTypes.length + 1" class="ltf-empty">
                                        {{ ltfEmptyMessage }}
                                    </td>
                                </tr>
                                <tr
                                    v-for="level in ltfLevels"
                                    :key="'ltf-' + level.lf_level_id"
                                    class="level-row"
                                >
                                    <td class="level-cell">
                                        <div class="level-name">{{ level.name }}</div>
                                        <div v-if="ltfLevelAbbreviation(level)" class="ltf-level-abbr" :style="{ color: ltfAbbrColour }">{{ ltfLevelAbbreviation(level) }}</div>
                                    </td>
                                    <template v-for="group in ltfColumnGroups">
                                        <td
                                            v-for="(factor,fi) in ltfFactorTypes" style="height: 100px;"
                                            :key="'ltf-' + level.lf_level_id + '-' + group.lf_function_id + '-' + group.lf_track_id + '-' + factor.lf_factor_id"
                                            :class="['ltf-cell', { 'ltf-cell-fixed': selectedTab.settings.fixedCellHeight, 'ltf-factor-last': fi === ltfFactorTypes.length - 1 }]"
                                            :style="{ ...(selectedTab.settings.minColumnWidth ? { minWidth: selectedTab.settings.minColumnWidth + 'px' } : {}), ...(selectedTab.settings.maxColumnWidth ? { maxWidth: selectedTab.settings.maxColumnWidth + 'px' } : {}) }"
                                            @click="openLtfCellPopup($event, group, level, factor)"
                                        >
                                            <div :class="{ 'ltf-cell-fixed-inner': selectedTab.settings.fixedCellHeight }">{{ getLtfDescription(group, level, factor) }}</div>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Cell popup overlay -->
                    <div v-if="ltfCellPopup.isOpen" class="ltf-popup-overlay" @click="closeLtfCellPopup">
                        <div class="ltf-popup-card" :style="{ top: ltfCellPopup.top + 'px', left: ltfCellPopup.left + 'px' }" @click.stop>
                            <div class="ltf-popup-header">
                                <div>
                                    <div class="ltf-popup-title">{{ ltfCellPopup.factorName }}</div>
                                    <div class="ltf-popup-subtitle">Level {{ ltfCellPopup.levelPosition }} &mdash; {{ ltfCellPopup.groupLabel }}</div>
                                </div>
                                <button class="ltf-popup-close" @click="closeLtfCellPopup">&times;</button>
                            </div>
                            <div class="ltf-popup-body">
                              <GridInlineEdit
                                v-if="ltfCellPopup.trackFactor"
                                type="textarea"
                                :rows="4"
                                :editing="isGridInlineEditing('lf_level_function_track_factor', 'lft_factor_description', ltfCellPopup.trackFactor, 'lf_factor_id')"
                                :value="gridInlineEdit.value"
                                :editable="is_editable"
                                @input="gridInlineEdit.value = $event"
                                @save="saveGridInlineEdit(ltfCellPopup.trackFactor)"
                                @cancel="cancelGridInlineEdit"
                                @start="startGridInlineEdit('lf_level_function_track_factor', 'lft_factor_description', ltfCellPopup.trackFactor, 'lf_factor_id')"
                              >{{ ltfCellPopup.trackFactor.lft_factor_description || (is_editable ? 'Enter description...' : '') }}</GridInlineEdit>
                              <div v-else class="ltf-popup-empty">No factor data available</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sub-Factor Descriptions tab -->
            <div v-if="selectedTab.viewType === 'sub_factor_descriptions'" class="tab-grid-wrapper">
                <v-row class="filter-row" align="center">
                    <v-col cols="auto" class="filter-col">
                        <div class="filter-label">Filter Factors</div>
                        <RmSelect
                            v-model="sfdSelectedFactorIds"
                            :items="sfdFactorOptions"
                            multiple
                            clearable
                            placeholder="All Factors"
                            placeholder-color="#000000"
                            class="filter-select"
                        />
                    </v-col>
                    <v-col class="filter-col">
                        <v-spacer></v-spacer>
                        <v-menu v-if="!hideAllSettings" offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                outlined
                                small class="settings-btn"
                                v-bind="attrs"
                                v-on="on"
                              >
                                <v-icon  small>mdi-cog</v-icon>
                                <v-icon  small>mdi-chevron-down</v-icon>
                              </v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item @click="selectedTab.settings.showHeaderFactorColours = !selectedTab.settings.showHeaderFactorColours">
                                    <v-list-item-action>
                                        <v-checkbox v-model="selectedTab.settings.showHeaderFactorColours" dense hide-details></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>Header Factor Colours</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item @click="selectedTab.settings.showHeaderSubFactorColours = !selectedTab.settings.showHeaderSubFactorColours">
                                    <v-list-item-action>
                                        <v-checkbox v-model="selectedTab.settings.showHeaderSubFactorColours" dense hide-details></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>Header Sub-Factor Colours</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item @click="selectedTab.settings.showInfoIcons = !selectedTab.settings.showInfoIcons">
                                    <v-list-item-action>
                                        <v-checkbox v-model="selectedTab.settings.showInfoIcons" dense hide-details></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>Info Icons</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item @click="selectedTab.settings.showDetailDescription = !selectedTab.settings.showDetailDescription">
                                    <v-list-item-action>
                                        <v-checkbox v-model="selectedTab.settings.showDetailDescription" dense hide-details></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>Show Detail Description</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-title>Min Column Width</v-list-item-title>
                                        <v-text-field
                                            v-model.number="selectedTab.settings.minColumnWidth"
                                            type="number"
                                            dense
                                            hide-details
                                            suffix="px"
                                            placeholder="0 = no limit"
                                            style="max-width: 140px;"
                                        ></v-text-field>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-title>Max Column Width</v-list-item-title>
                                        <v-text-field
                                            v-model.number="selectedTab.settings.maxColumnWidth"
                                            type="number"
                                            dense
                                            hide-details
                                            suffix="px"
                                            placeholder="0 = no limit"
                                            style="max-width: 140px;"
                                        ></v-text-field>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-col>
                </v-row>
                <div class="function-section">
                    <div class="framework-grid-wrapper">
                        <table class="grid-table ltf-table">
                            <thead>
                                <!-- Factor grouping row -->
                                <tr class="framework-name-row">
                                    <th class="header-cell level-header"></th>
                                    <th
                                        v-for="factor in sfdFilteredFactorTypes"
                                        :key="'sfd-grp-' + factor.lf_factor_id"
                                        class="header-cell framework-name-header sfd-header-cell"
                                        :colspan="factor.sub_factors ? factor.sub_factors.length : 1"
                                            :style="selectedTab.settings.showHeaderFactorColours ? { backgroundImage: 'linear-gradient(' + hexToRgba(factor.background_colour, 0.2) + ',' + hexToRgba(factor.background_colour, 0.2) + ')' } : {}"
                                    >
                                        {{ factor.name }}
                                        <v-tooltip bottom max-width="400" v-if="selectedTab.settings.showInfoIcons && factor.description">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon x-small v-bind="attrs" v-on="on" class="sfd-cell-info-icon">mdi-information-outline</v-icon>
                                            </template>
                                            <div>{{ factor.description }}</div>
                                        </v-tooltip>
                                    </th>
                                </tr>
                                <!-- Sub-factor header row -->
                                <tr class="header-row">
                                    <th class="header-cell level-header">Proficiency</th>
                                    <template v-for="factor in sfdFilteredFactorTypes">
                                        <th
                                            v-for="(sf, si) in factor.sub_factors"
                                            :key="'sfd-h-' + sf.lf_sub_factor_id"
                                            class="header-cell ltf-factor-header sfd-header-cell"
                                            :class="{ 'ltf-factor-last': si === factor.sub_factors.length - 1 }"
                                            :style="selectedTab.settings.showHeaderSubFactorColours ? { backgroundImage: 'linear-gradient(' + hexToRgba(factor.background_colour, 0.2) + ',' + hexToRgba(factor.background_colour, 0.2) + ')' } : {}"
                                        >
                                            {{ sf.name }}
                                            <v-tooltip bottom max-width="400" v-if="selectedTab.settings.showInfoIcons && sf.description">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon x-small v-bind="attrs" v-on="on" class="sfd-cell-info-icon">mdi-information-outline</v-icon>
                                                </template>
                                                <div>{{ sf.description }}</div>
                                            </v-tooltip>
                                        </th>
                                    </template>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="levelNum in subFactorLevels"
                                    :key="'sfd-' + levelNum"
                                    class="level-row"
                                >
                                    <td class="level-cell">
                                        <div class="level-name">{{ levelNum }}</div>
                                    </td>
                                    <template v-for="factor in sfdFilteredFactorTypes">
                                        <td
                                            v-for="(sf, si) in factor.sub_factors"
                                            :key="'sfd-' + levelNum + '-' + sf.lf_sub_factor_id"
                                            class="ltf-cell sfd-cell"
                                            :class="{ 'ltf-factor-last': si === factor.sub_factors.length - 1 }"
                                            :style="{ ...(selectedTab.settings.minColumnWidth ? { minWidth: selectedTab.settings.minColumnWidth + 'px' } : {}), ...(selectedTab.settings.maxColumnWidth ? { maxWidth: selectedTab.settings.maxColumnWidth + 'px' } : {}) }"
                                            @click="sfdGetLevel(sf, levelNum) && openSfdEditDialog(sf, factor.name, levelNum)"
                                        >
                                            <template v-if="sfdGetLevel(sf, levelNum)">
                                                <v-tooltip bottom max-width="400" v-if="selectedTab.settings.showInfoIcons && !selectedTab.settings.showDetailDescription && sfdGetLevel(sf, levelNum).description">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon x-small v-bind="attrs" v-on="on" class="sfd-cell-info-icon">mdi-information-outline</v-icon>
                                                    </template>
                                                    <div>{{ sfdGetLevel(sf, levelNum).description }}</div>
                                                </v-tooltip>
                                                <span class="sfd-cell-name">{{ sfdGetLevel(sf, levelNum).name }}</span>
                                                <div v-if="selectedTab.settings.showDetailDescription && sfdGetLevel(sf, levelNum).description" class="sfd-cell-description">{{ sfdGetLevel(sf, levelNum).description }}</div>
                                            </template>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Grade Alignment + Framework in Use are MOUNTED ONCE (on first visit) and then
                 shown and hidden, rather than created and destroyed per tab switch. -->
            <!-- Grade Alignment Tab -->
            <grade-alignment-view
              v-if="visitedTabs.grade_alignment"
              v-show="selectedTab.viewType === 'grade_alignment'"
              :active="selectedTab.viewType === 'grade_alignment'"
              :levelling-framework="levelling_framework"
            />

            <!-- Framework in Use Tab -->
            <framework-in-use-view
              v-if="visitedTabs.framework_in_use"
              v-show="selectedTab.viewType === 'framework_in_use'"
              :active="selectedTab.viewType === 'framework_in_use'"
              :levelling-framework="levelling_framework"
              :anchors="anchors"
              @evaluate="goEvaluateRole"
            />

            <!-- Reports Tab -->
            <levelling-reports v-if="selectedTab.viewType === 'reports'" :levelling-framework="levelling_framework" />

            <!-- Administration Tab -->
            <div v-if="selectedTab.viewType === 'administration'" class="admin-tab">
              <div v-if="adminSaving" class="admin-saving-overlay">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>

              <!-- Framework Selector -->
              <div class="admin-section">
                <div class="admin-section-header">
                  <!-- <div> -->
                    <div class="admin-framework-selector">
                      <span class="admin-section-label">Framework:</span>
                      <RmSelect
                        v-if="adminFrameworkList.length > 1"
                        :items="adminFrameworkList.map(f => ({ text: f.name, value: f.levelling_framework_id }))"
                        :value="levelling_framework?.levelling_framework_id"
                        @input="switchFramework($event)"
                        label="Select Framework"
                        style="max-width: 400px;"
                      />
                      <div v-else class="admin-framework-name">{{ levelling_framework?.name }}{{ levelling_framework?.is_overall_default ? ' (RoleMapper Default)' : levelling_framework?.is_client_default ? ' (Client Default)' : ''}}</div>
                    </div>
                  <!-- </div> -->
                  <div class="admin-section-actions">
                    <div class="admin-framework-meta" v-if="is_editable">
                      <button class="action-button" @click="openAdminFrameworkDialog('edit')" title="Rename this framework and the terms it uses for levels, tracks, disciplines and factors">
                        <v-icon left>mdi-text-box-edit-outline</v-icon> Wording
                      </button>
                      <button class="action-button" @click="openAdminDeleteConfirm('framework', levelling_framework)" v-if="adminFrameworkList.length > 1 && !levelling_framework?.is_overall_default">
                        <v-icon left>mdi-delete</v-icon> Delete
                      </button>
                      <!-- <v-chip v-if="levelling_framework?.is_client_default" x-small color="primary" text-color="white">Default</v-chip> -->
                    </div>
                    <button v-if="hasAdminPermission && hideTitle" class="action-button" @click="openImportFrameworkDialog" title="Import Framework">
                      <v-icon left>mdi-import</v-icon> Import
                    </button>
                    <button v-if="levelling_framework?.is_overall_default && is_editable" class="action-button" @click="openPublishDialog">
                      <v-icon left>mdi-publish</v-icon> Publish
                    </button>
                    <button class="action-button" @click="openAdminFrameworkDialog('add')">
                      <v-icon left>mdi-plus</v-icon> New Framework
                    </button>
                  </div>
                </div>
              </div>

              <!-- design: .lvl-note--info - what editing a register does and does
                   not do to roles already levelled. -->
              <div class="fw-infonote">
                <v-icon size="18" class="fw-infonote-ic">mdi-shield-outline</v-icon>
                <span>Changes are <strong>audited and versioned</strong>, and apply to future evaluations only &mdash; existing grades are never silently re-levelled.</span>
              </div>

              <!-- design: the register picker is a segmented control, not tabs -
                   these are four registers of one framework, not four views. -->
              <div class="admin-entity-seg">
                <div class="lvl-fwseg" role="tablist" aria-label="Framework register">
                  <button
                    v-for="(reg, ri) in adminRegisters"
                    :key="'reg-' + ri"
                    type="button"
                    role="tab"
                    class="lvl-fwseg-btn"
                    :class="{ on: adminActiveEntityTab === ri }"
                    :aria-selected="adminActiveEntityTab === ri"
                    @click="adminActiveEntityTab = ri"
                  >{{ reg }}</button>
                </div>
                <span class="admin-entity-seg-spacer"></span>
                <button
                  v-if="is_editable"
                  type="button"
                  class="admin-add-btn"
                  @click="openAdminEntityDialog(adminActiveRegisterType, 'add')"
                >
                  <v-icon size="16">mdi-plus</v-icon> {{ adminAddLabel }}
                </button>
              </div>

              <!-- Plain panels rather than v-tabs-items: its internal
                   .v-window__container / .v-window-item wrappers have no height
                   of their own, which breaks the flex chain the table needs in
                   order to scroll inside a bounded box with a sticky header. -->
              <div class="admin-entity-panels">
                <!-- Levels -->
                <div v-show="adminActiveEntityTab === 0" class="admin-entity-panel">
                  <div class="admin-entity-split">
                    <div class="admin-entity-list">
                      <table class="admin-table">
                        <thead>
                          <tr><th>Pos</th><th>Name</th><th>Group</th><th>Description</th>
                          <th v-if="is_editable" class="admin-th-actions">Actions</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in (levelling_framework?.levels || []).slice().sort((a,b) => a.position - b.position)" :key="item.lf_level_id">
                            <td class="admin-td-pos">{{ item.position }}</td>
                            <td>{{ item.name }}</td>
                            <td class="admin-td-desc">{{ item.level_group }}</td>
                            <td class="admin-td-desc">{{ item.description }}</td>
                            <td class="admin-td-actions" v-if="is_editable">
                              <v-icon small @click.stop="openAdminEntityDialog('level', 'edit', item)" title="Edit" class="mr-1">mdi-pencil</v-icon>
                              <v-icon small color="error" @click.stop="openAdminDeleteConfirm('level', item)" title="Delete">mdi-delete</v-icon>
                            </td>
                          </tr>
                          <tr v-if="!levelling_framework?.levels?.length"><td colspan="4" class="admin-td-empty">No items</td></tr>
                        </tbody>
                      </table>
                    </div>
                                      </div>
                </div>

                <!-- Functions -->
                <div v-show="adminActiveEntityTab === 1" class="admin-entity-panel">
                  <div class="admin-entity-split">
                    <div class="admin-entity-list">
                      <table class="admin-table">
                        <thead>
                          <tr><th>Pos</th><th>Name</th><th>Description</th><th>Core</th><th v-if="is_editable" class="admin-th-actions">Actions</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in (levelling_framework?.functions || []).slice().sort((a,b) => a.position - b.position)" :key="item.lf_function_id">
                            <td class="admin-td-pos">{{ item.position }}</td>
                            <td>{{ item.name }}</td>
                            <td class="admin-td-desc">{{ item.description }}</td>
                            <td><v-icon v-if="item.is_core" x-small color="green">mdi-check</v-icon></td>
                            <td class="admin-td-actions" v-if="is_editable">
                              <v-icon small @click.stop="openAdminEntityDialog('function', 'edit', item)" title="Edit" class="mr-1">mdi-pencil</v-icon>
                              <v-icon small @click.stop="openCopyFunctionDialog(item)" title="Copy" class="mr-1">mdi-content-copy</v-icon>
                              <v-icon small color="error" @click.stop="openAdminDeleteConfirm('function', item)" title="Delete">mdi-delete</v-icon>
                            </td>
                          </tr>
                          <tr v-if="!levelling_framework?.functions?.length"><td colspan="5" class="admin-td-empty">No items</td></tr>
                        </tbody>
                      </table>
                    </div>
                                      </div>
                </div>

                <!-- Tracks -->
                <div v-show="adminActiveEntityTab === 2" class="admin-entity-panel">
                  <div class="admin-entity-split">
                    <div class="admin-entity-list">
                      <table class="admin-table">
                        <thead>
                          <tr><th>Pos</th><th>Name</th><th>Abbrev</th><th>Colour</th><th v-if="is_editable" class="admin-th-actions">Actions</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in (levelling_framework?.tracks || []).slice().sort((a,b) => a.position - b.position)" :key="item.lf_track_id">
                            <td class="admin-td-pos">{{ item.position }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.abbreviation }}</td>
                            <td><div class="admin-colour-chip" :style="{ backgroundColor: item.background_colour }"></div></td>
                            <td class="admin-td-actions" v-if="is_editable">
                              <v-icon small @click.stop="openAdminEntityDialog('track', 'edit', item)" title="Edit" class="mr-1">mdi-pencil</v-icon>
                              <v-icon small color="error" @click.stop="openAdminDeleteConfirm('track', item)" title="Delete">mdi-delete</v-icon>
                            </td>
                          </tr>
                          <tr v-if="!levelling_framework?.tracks?.length"><td colspan="5" class="admin-td-empty">No items</td></tr>
                        </tbody>
                      </table>
                    </div>
                                      </div>
                </div>

                <!-- Factors -->
                <div v-show="adminActiveEntityTab === 3" class="admin-entity-panel">
                  <div class="admin-entity-split">
                    <div class="admin-entity-list">
                      <table class="admin-table">
                        <thead>
                          <tr><th></th><th>#</th><th>Factor Name</th><th>Weight</th><th>Curve Factor</th><th>Colour</th><th v-if="is_editable" class="admin-th-actions">Actions</th></tr>
                        </thead>
                        <tbody>
                          <template v-for="item in (levelling_framework?.factors || []).slice().sort((a,b) => a.position - b.position)">
                          <tr :key="item.lf_factor_id">
                            <td class="admin-td-expand" @click.stop="subFactorExpanded.includes(item.lf_factor_id) ? subFactorExpanded.splice(subFactorExpanded.indexOf(item.lf_factor_id), 1) : subFactorExpanded.push(item.lf_factor_id)">
                              <v-icon small color="black">{{ subFactorExpanded.includes(item.lf_factor_id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                            </td>
                            <td class="admin-td-pos">{{ item.position }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.weighting }}</td>
                            <td>{{ item.curve_factor }}</td>
                            <td><div class="admin-colour-chip" :style="{ backgroundColor: item.background_colour }"></div></td>
                            <td class="admin-td-actions" v-if="is_editable">
                              <v-icon small @click.stop="openAdminEntityDialog('factor', 'edit', item)" title="Edit" class="mr-1">mdi-pencil</v-icon>
                              <v-icon small color="error" @click.stop="openAdminDeleteConfirm('factor', item)" title="Delete">mdi-delete</v-icon>
                            </td>
                          </tr>
                          <tr v-if="subFactorExpanded.includes(item.lf_factor_id)" :key="'sf-' + item.lf_factor_id" class="admin-sf-expansion-row">
                            <td colspan="6" class="admin-sf-expansion-cell">
                              <div class="admin-sf-expansion">
                                <table class="admin-table">
                                  <thead>
                                    <tr><th></th><th>#</th><th>Sub Factor Name</th><th>Weight</th><th>
                                      <button class="action-button-small" @click.stop="addSubFactor(item)" title="Add Sub Factor" :disabled="subFactorSaving"><v-icon small>mdi-plus</v-icon></button>
                                    </th></tr>
                                  </thead>
                                  <tbody>
                                    <template v-for="sf in (item.sub_factors || []).slice().sort((a,b) => a.position - b.position)">
                                    <tr :key="sf.lf_sub_factor_id">
                                      <td class="admin-td-expand" @click.stop="subFactorExpanded.includes('sfl-' + sf.lf_sub_factor_id) ? subFactorExpanded.splice(subFactorExpanded.indexOf('sfl-' + sf.lf_sub_factor_id), 1) : subFactorExpanded.push('sfl-' + sf.lf_sub_factor_id)">
                                        <v-icon small color="black">{{ subFactorExpanded.includes('sfl-' + sf.lf_sub_factor_id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                                      </td>
                                      <td class="admin-td-pos">{{ sf.position }}</td>
                                      <td>{{ sf.name || '(unnamed)' }}</td>
                                      <td>{{ sf.weighting }}</td>
                                      <td class="admin-td-actions">
                                        <v-icon small @click.stop="startEditSubFactor(sf)" title="Edit" class="mr-1">mdi-pencil</v-icon>
                                        <v-icon small color="error" @click.stop="deleteSubFactor(sf, item)" title="Delete">mdi-delete</v-icon>
                                      </td>
                                    </tr>
                                    <tr v-if="subFactorExpanded.includes('sfl-' + sf.lf_sub_factor_id)" :key="'sfl-' + sf.lf_sub_factor_id" class="admin-sf-expansion-row">
                                      <td colspan="5" class="admin-sf-expansion-cell">
                                        <div class="admin-sf-expansion">
                                          <table class="admin-table admin-sfl-table">
                                            <thead>
                                              <tr><th>#</th><th>Sub Factor Level Name</th><th>Score</th><th>Wt</th><th>
                                                <button class="action-button-small" @click.stop="addSubFactorLevel(sf)" title="Add Sub Factor Level" :disabled="subFactorSaving"><v-icon small>mdi-plus</v-icon></button>
                                              </th></tr>
                                            </thead>
                                            <tbody>
                                              <tr v-for="sfl in (sf.sub_factor_levels || []).slice().sort((a,b) => a.position - b.position)" :key="sfl.lf_sub_factor_level_id">
                                                <td class="admin-td-pos">{{ sfl.position }}</td>
                                                <td>{{ sfl.name || '(unnamed)' }}</td>
                                                <td>{{ sfl.score }}</td>
                                                <td>{{ sfl.weighting }}</td>
                                                <td class="admin-td-actions">
                                                  <v-icon small @click.stop="startEditSubFactorLevel(sfl, sf)" title="Edit" class="mr-1">mdi-pencil</v-icon>
                                                  <v-icon small color="error" @click.stop="deleteSubFactorLevel(sf, sfl)" title="Delete">mdi-delete</v-icon>
                                                </td>
                                              </tr>
                                              <tr v-if="!(sf.sub_factor_levels || []).length"><td colspan="5" class="admin-td-empty">No levels</td></tr>
                                            </tbody>
                                          </table>
                                        </div>
                                      </td>
                                    </tr>
                                    </template>
                                    <tr v-if="!(item.sub_factors || []).length"><td colspan="5" class="admin-td-empty">No {{ (levelling_framework?.sub_factor_label || 'sub-factor').toLowerCase() }}s yet</td></tr>
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                          </template>
                          <tr v-if="!levelling_framework?.factors?.length"><td colspan="6" class="admin-td-empty">No items</td></tr>
                        </tbody>
                      </table>
                    </div>
                                                                              </div>
                </div>
              </div>
            </div>

        </v-card-text>
    </v-card>
    <div v-else class="empty-state">
      <p>No framework data available</p>
          <button v-if="hasAdminPermission" class="action-button-small" @click="openImportFrameworkDialog" title="Import Framework">
            <v-icon small left>mdi-import</v-icon> Import
          </button>
    </div>

    <!-- Admin Framework Dialog -->
    <div v-if="adminFrameworkDialog.isOpen" class="dialog-overlay" @click="closeAdminFrameworkDialog">
      <div class="dialog-content" style="max-width: 550px;" @click.stop>
        <div class="dialog-header">
          <h3>{{ adminFrameworkDialog.mode === 'add' ? 'New Framework' : 'Framework Wording' }}</h3>
          <button class="dialog-close-btn" @click="closeAdminFrameworkDialog">&times;</button>
        </div>
        <div class="dialog-body" style="flex-direction: column; align-items: stretch;">
          <div class="admin-form-field">
            <label>Name</label>
            <input v-model="adminFrameworkDialog.form.name" class="admin-input" />
          </div>
          <div class="admin-form-field">
            <label>Description</label>
            <textarea v-model="adminFrameworkDialog.form.description" class="admin-input" rows="3"></textarea>
          </div>
          <div class="admin-form-row">
            <div class="admin-form-field">
              <label>Level Label</label>
              <input v-model="adminFrameworkDialog.form.level_label" class="admin-input" />
            </div>
            <div class="admin-form-field">
              <label>Function Label</label>
              <input v-model="adminFrameworkDialog.form.function_label" class="admin-input" />
            </div>
          </div>
          <div class="admin-form-row">
            <div class="admin-form-field">
              <label>Track Label</label>
              <input v-model="adminFrameworkDialog.form.track_label" class="admin-input" />
            </div>
            <div class="admin-form-field">
              <label>Level Track Label</label>
              <input v-model="adminFrameworkDialog.form.level_track_label" class="admin-input" />
            </div>
          </div>
          <div class="admin-form-row">
            <div class="admin-form-field">
              <label>Factor Label</label>
              <input v-model="adminFrameworkDialog.form.factor_label" class="admin-input" />
            </div>
            <div class="admin-form-field">
              <label>Sub-Factor Label</label>
              <input v-model="adminFrameworkDialog.form.sub_factor_label" class="admin-input" style="max-width: 200px;" />
            </div>
          </div>
          <div class="admin-form-field" v-if="adminFrameworkDialog.mode === 'edit'">
            <label class="admin-checkbox-label">
              <input type="checkbox" v-model="adminFrameworkDialog.form.is_client_default" :true-value="1" :false-value="0" /> Client Default
            </label>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn dialog-btn-secondary" @click="closeAdminFrameworkDialog">Cancel</button>
          <button class="dialog-btn dialog-btn-primary" @click="saveAdminFrameworkDialog" :disabled="adminSaving">Save</button>
        </div>
      </div>
    </div>

    <!-- Admin Delete Confirmation Dialog -->
    <div v-if="adminDeleteConfirm.isOpen" class="dialog-overlay" @click="closeAdminDeleteConfirm">
      <div class="dialog-content" style="max-width: 400px;" @click.stop>
        <div class="dialog-header">
          <h3>Confirm Delete</h3>
          <button class="dialog-close-btn" @click="closeAdminDeleteConfirm">&times;</button>
        </div>
        <div class="dialog-body" style="flex-direction: column; align-items: stretch;">
          <p>Are you sure you want to delete this {{ adminDeleteConfirm.entityLabel }}?</p>
          <p v-if="adminDeleteConfirm.item" style="font-weight: 600;">{{ adminDeleteConfirm.item.name }}</p>
          <p style="color: #e74c3c; font-size: 13px;" v-if="adminDeleteConfirm.entityType !== 'framework'">
            This will also remove any associated {{ adminDeleteConfirm.relatedLabel }} for this {{ adminDeleteConfirm.entityLabel }}.
          </p>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn dialog-btn-secondary" @click="closeAdminDeleteConfirm">Cancel</button>
          <button class="dialog-btn" style="background-color: #e74c3c; color: white;" @click="adminDeleteConfirm.entityType === 'framework' ? deleteFramework() : confirmAdminDelete()" :disabled="adminSaving">Delete</button>
        </div>
      </div>
    </div>

    <!-- Copy Function Dialog -->
    <div v-if="adminCopyDialog.isOpen" class="dialog-overlay" @click="adminCopyDialog.isOpen = false">
      <div class="dialog-content" style="max-width: 450px;" @click.stop>
        <div class="dialog-header">
          <h3>Copy {{ levelling_framework?.function_label || 'Function' }}</h3>
          <button class="dialog-close-btn" @click="adminCopyDialog.isOpen = false">&times;</button>
        </div>
        <div class="dialog-body" style="flex-direction: column; align-items: stretch;">
          <div class="admin-form-field">
            <label>Name</label>
            <input v-model="adminCopyDialog.form.name" class="admin-input" />
          </div>
          <div class="admin-form-field">
            <label>Description</label>
            <textarea v-model="adminCopyDialog.form.description" class="admin-input" rows="3"></textarea>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="dialog-btn dialog-btn-secondary" @click="adminCopyDialog.isOpen = false">Cancel</button>
          <button class="dialog-btn dialog-btn-primary" @click="confirmCopyFunction" :disabled="adminSaving || !adminCopyDialog.form.name.trim()">Copy</button>
        </div>
      </div>
    </div>

    <!-- Import Framework Dialog -->
    <div v-if="importDialog.isOpen" class="dialog-overlay" @click="importDialog.isOpen = false">
      <div class="dialog-content" style="max-width: 550px;" @click.stop>
        <div class="dialog-header">
          <h3>Import Framework</h3>
          <button class="dialog-close-btn" @click="importDialog.isOpen = false">&times;</button>
        </div>
        <div class="dialog-body" style="flex-direction: column; align-items: stretch;">
          <div v-if="importDialog.loading" style="text-align: center; padding: 24px;">
            <div class="loader"></div>
            <span>Loading frameworks...</span>
          </div>
          <div v-else-if="!importDialog.clients.length" style="padding: 16px; color: #888;">
            No frameworks found.
          </div>
          <div v-else class="import-framework-list">
            <div v-for="client in importDialog.clients" :key="client.client_id" class="import-client-group">
              <div class="import-client-name">{{ client.client_name }}</div>
              <div v-for="fw in client.frameworks" :key="fw.levelling_framework_id"
                   class="import-framework-item"
                   :class="{ 'import-framework-selected': importDialog.selectedFrameworkId === fw.levelling_framework_id }"
                   @click="selectImportFramework(fw)">
                <div class="import-framework-name">{{ fw.name }}</div>
                <div v-if="fw.description" class="import-framework-desc">{{ fw.description }}</div>
              </div>
            </div>
          </div>
          <div v-if="importDialog.selectedFrameworkId" style="margin-top: 12px;">
            <div class="admin-form-field">
              <label>Name</label>
              <input v-model="importDialog.form.name" class="admin-input" />
            </div>
            <div class="admin-form-field">
              <label>Description</label>
              <textarea v-model="importDialog.form.description" class="admin-input" rows="3"></textarea>
            </div>
          </div>
        </div>
        <div v-if="importDialog.importing" class="dialog-body" style="flex-direction: column; align-items: center; padding: 24px;">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
          <span style="margin-top: 12px; font-size: 14px; color: #555;">Importing framework, please wait...</span>
        </div>
        <div v-else class="dialog-footer">
          <button class="dialog-btn dialog-btn-secondary" @click="importDialog.isOpen = false">Cancel</button>
          <button class="dialog-btn dialog-btn-primary" @click="confirmImportFramework" :disabled="adminSaving || !importDialog.selectedFrameworkId || !importDialog.form.name.trim()">Import</button>
        </div>
      </div>
    </div>

    <!-- Publish Dialog -->
    <div v-if="publishDialog.isOpen" class="dialog-overlay" @click="publishDialog.isOpen = false">
      <div class="dialog-content" style="max-width: 900px;" @click.stop>
        <div class="dialog-header">
          <h3>Publish Framework</h3>
          <button class="dialog-close-btn" @click="publishDialog.isOpen = false">&times;</button>
        </div>
        <div class="dialog-body" style="flex-direction: column; align-items: stretch;">
          <div v-if="publishDialog.loading" style="text-align: center; padding: 24px;">
            <div class="loader"></div>
            <span>Finding matching frameworks...</span>
          </div>
          <div v-else-if="!publishDialog.frameworks.length" style="padding: 16px; color: #888;">
            No matching frameworks found.
          </div>
          <div v-else>
            <p style="margin: 0 0 8px; font-size: 13px; color: #666;">
              Select which entities to push to each framework. Counts show linked/total.
            </p>
            <div style="overflow-x: auto;">
              <table class="admin-table" style="width: 100%; font-size: 13px;">
                <thead>
                  <tr>
                    <th>Framework</th>
                    <th style="text-align: center;">Levels</th>
                    <th style="text-align: center;">Functions</th>
                    <th style="text-align: center;">Tracks</th>
                    <th style="text-align: center;">Factors</th>
                    <th style="text-align: center;">LFTs</th>
                    <th style="text-align: center;">LFTFs</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="fw in publishDialog.frameworks" :key="fw.levelling_framework_id">
                    <td>{{ fw.name }}</td>
                    <td v-for="key in ['levels', 'functions', 'tracks', 'factors', 'lfts', 'lftfs']" :key="key"
                        style="text-align: center; white-space: nowrap;">
                      <label style="cursor: pointer; display: inline-flex; align-items: center; gap: 4px;">
                        <input type="checkbox" v-model="fw.entities[key].selected"
                          :disabled="fw.entities[key].copied === 0" />
                        <span :style="{ color: fw.entities[key].copied === 0 ? '#bbb' : 'inherit' }">
                          {{ fw.entities[key].copied }}/{{ fw.entities[key].count }}
                        </span>
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-if="publishDialog.publishing" class="dialog-body" style="flex-direction: column; align-items: center; padding: 24px;">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
          <span style="margin-top: 12px; font-size: 14px; color: #555;">Publishing to selected frameworks...</span>
        </div>
        <div v-else class="dialog-footer">
          <button class="dialog-btn dialog-btn-secondary" @click="publishDialog.isOpen = false">Cancel</button>
          <button class="dialog-btn dialog-btn-primary" @click="confirmPublish"
            :disabled="!publishDialog.frameworks.some(fw => Object.values(fw.entities).some(e => e.selected))">
            Publish
          </button>
        </div>
      </div>
    </div>

    <!-- track_factors Dialog -->
    <div v-if="factorsDialog.isOpen" class="dialog-overlay" @click="handleDialogOutsideClick">
      <div class="dialog-content" @click.stop style="max-width: 680px;">
        <div class="dialog-header factors-header">
          <div class="factors-heading">
            <h3>Factors for {{ factorsDialog.levelTrackTitle }}</h3>
            <span class="factors-subtitle">{{ factorsDialog.subtitle }}</span>
          </div>
          <div class="dialog-header-controls">
            <button class="dialog-close-btn" @click="handleDialogClose">&times;</button>
          </div>
        </div>
        <div class="dialog-body factors-body">
          <!-- design: one row per factor group, opening to its descriptor. -->
          <div
            v-for="templateFactor in factorsDialog.factors"
            :key="templateFactor.lf_factor_id"
            class="factor-acc"
            :class="{ 'is-open': isFactorExpanded(templateFactor) }"
          >
            <button
              type="button"
              class="factor-acc-head"
              :aria-expanded="isFactorExpanded(templateFactor)"
              :title="templateFactor.description"
              @click="toggleFactorExpanded(templateFactor)"
            >
              <span
                class="factor-acc-pill"
                :style="{ backgroundColor: hexToRgba(templateFactor.background_colour, 0.35) }"
              >{{ templateFactor.name }}</span>
              <span class="factor-acc-spacer"></span>
              <span
                class="factor-acc-count"
                :class="{ 'is-empty': !(templateFactor.lft_factor_description || '').trim() }"
              >{{ factorDescriptorSummary(templateFactor) }}</span>
              <v-icon size="18" class="factor-acc-chev">
                {{ isFactorExpanded(templateFactor) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </button>

            <div v-if="isFactorExpanded(templateFactor)" class="factor-acc-panel">
              <label class="factor-acc-label">{{ levelling_framework.level_track_label || 'Level track' }} {{ (levelling_framework.factor_label || 'factor').toLowerCase() }} descriptor</label>
              <div class="descriptor-text-outline">
                <GridInlineEdit
                  type="textarea"
                  :rows="5"
                  :editing="isGridInlineEditing('lf_level_function_track_factor', 'lft_factor_description', templateFactor, 'lf_factor_id')"
                  :value="gridInlineEdit.value"
                  :editable="is_editable"
                  @input="gridInlineEdit.value = $event"
                  @save="saveGridInlineEdit(templateFactor)"
                  @cancel="cancelGridInlineEdit"
                  @start="startGridInlineEdit('lf_level_function_track_factor', 'lft_factor_description', templateFactor, 'lf_factor_id')"
                >{{ templateFactor.lft_factor_description || (is_editable ? 'Enter description...' : 'No descriptor recorded.') }}</GridInlineEdit>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button
            v-if="is_editable"
            class="dialog-btn dialog-btn-secondary"
            @click="cancelFactorsDialog"
          >
            Cancel
          </button>
          <button
            v-if="is_editable"
            class="dialog-btn dialog-btn-primary"
            @click="saveFactorsDialog"
            :disabled="factorsDialog.saving"
          >
            Save
          </button>
          <button
            v-if="!is_editable"
            class="dialog-btn dialog-btn-primary"
            @click="closeFactorsDialogWithoutSaving"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    <CcTour v-if="activeTour" :tour="activeTour" />

  <!-- Levelling terms. The definitions otherwise live only in tooltips, which
       are invisible on touch and unreachable once read. -->
  <div v-if="glossaryOpen" class="dialog-overlay" @click="closeGlossary">
    <div class="dialog-content" style="max-width: 620px;" @click.stop role="dialog" aria-label="Levelling terms">
      <div class="dialog-header">
        <h3>Levelling terms</h3>
        <button class="dialog-close-btn" @click="closeGlossary">&times;</button>
      </div>
      <div class="dialog-body lvl-gloss-body">
        <dl class="lvl-gloss-list">
          <template v-for="term in LEVELLING_GLOSSARY">
            <dt :key="term.term + '-t'">{{ term.term }}</dt>
            <dd :key="term.term + '-d'">{{ term.definition }}</dd>
          </template>
        </dl>
      </div>
    </div>
  </div>

  <!-- Admin register dialogs. One dialog per depth (entity / sub-factor /
       sub-factor level), reached from the row's pencil. The four top-level
       registers share one dialog and vary only by which fields show, rather
       than four near-identical panels. -->
  <div v-if="adminEntityDialog.isOpen && !subFactorEditing._active && !subFactorLevelEditing._active" class="dialog-overlay dialog-overlay--form" @click="closeAdminEntityDialog">
    <div class="dialog-content dialog-content--form" style="max-width: 520px;" @click.stop>
      <div class="dialog-header">
        <h3>{{ adminEntityDialog.mode === 'add' ? 'Add' : 'Edit' }} {{ adminEntityDialog.entityLabel }}</h3>
        <button class="dialog-close-btn" @click="closeAdminEntityDialog">&times;</button>
      </div>
      <div class="dialog-body admin-dialog-body">
        <div class="admin-form-field">
          <label>Name</label>
          <input v-model="adminEntityDialog.form.name" class="admin-input" />
        </div>
        <div v-if="adminEntityDialog.entityType === 'level'" class="admin-form-field">
          <label>{{ levelling_framework?.level_label || 'Level' }} Group</label>
          <input v-model="adminEntityDialog.form.level_group" class="admin-input" />
        </div>
        <div class="admin-form-field">
          <label>Description</label>
          <textarea v-model="adminEntityDialog.form.description" class="admin-input" rows="4"></textarea>
        </div>
        <div class="admin-form-row">
          <div class="admin-form-field">
            <label>Position <span class="admin-required">*</span></label>
            <input v-model.number="adminEntityDialog.form.position" type="number" class="admin-input" :class="{ 'admin-input-error': adminEntityDialog.errors.position }" style="width: 80px;" />
            <span v-if="adminEntityDialog.errors.position" class="admin-field-error">{{ adminEntityDialog.errors.position }}</span>
          </div>
          <div v-if="adminEntityDialog.entityType === 'track'" class="admin-form-field">
            <label>Abbreviation</label>
            <input v-model="adminEntityDialog.form.abbreviation" class="admin-input" style="width: 120px;" />
          </div>
          <template v-if="adminEntityDialog.entityType === 'factor'">
            <div class="admin-form-field">
              <label>Weighting</label>
              <input v-model.number="adminEntityDialog.form.weighting" type="number" step="0.1" class="admin-input" style="width: 80px;" />
            </div>
            <div class="admin-form-field">
              <label>Curve Factor</label>
              <input v-model.number="adminEntityDialog.form.curve_factor" type="number" step="0.1" class="admin-input" style="width: 80px;" />
            </div>
          </template>
        </div>
        <div v-if="adminEntityDialog.entityType === 'function'" class="admin-form-field">
          <label class="admin-checkbox-label">
            <input type="checkbox" v-model="adminEntityDialog.form.is_core" :true-value="1" :false-value="0" /> Core
          </label>
        </div>
        <template v-if="adminEntityDialog.entityType === 'track' || adminEntityDialog.entityType === 'factor'">
                <div class="admin-form-field">
                  <label>Colour <span class="admin-required">*</span></label>
                  <div class="admin-colour-picker">
                    <div class="admin-colour-selected admin-input" :class="{ 'admin-input-error': adminEntityDialog.errors.display_colour_id }" @click="adminEntityDialog.colourDropdownOpen = !adminEntityDialog.colourDropdownOpen">
                      <template v-if="adminEntityDialog.form.display_colour_id">
                        <div class="admin-colour-chip" :style="{ backgroundColor: (levelling_framework?.display_colours || []).find(c => c.display_colour_id === adminEntityDialog.form.display_colour_id)?.background_colour }"></div>
                        <span>{{ (levelling_framework?.display_colours || []).find(c => c.display_colour_id === adminEntityDialog.form.display_colour_id)?.colour_name }}</span>
                      </template>
                      <span v-else class="admin-colour-none">None</span>
                      <v-icon small class="admin-colour-arrow">mdi-chevron-down</v-icon>
                    </div>
                    <span v-if="adminEntityDialog.errors.display_colour_id" class="admin-field-error">{{ adminEntityDialog.errors.display_colour_id }}</span>
                    <div v-if="adminEntityDialog.colourDropdownOpen" class="admin-colour-dropdown">
                      <div class="admin-colour-option" @click="adminEntityDialog.form.display_colour_id = null; adminEntityDialog.colourDropdownOpen = false">
                        <span class="admin-colour-none">None</span>
                      </div>
                      <div v-for="dc in (levelling_framework?.display_colours || [])" :key="dc.display_colour_id"
                           class="admin-colour-option"
                           :class="{ 'admin-colour-option-active': adminEntityDialog.form.display_colour_id === dc.display_colour_id }"
                           @click="adminEntityDialog.form.display_colour_id = dc.display_colour_id; adminEntityDialog.colourDropdownOpen = false">
                        <div class="admin-colour-chip" :style="{ backgroundColor: dc.background_colour }"></div>
                        <span>{{ dc.colour_name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
        </template>
      </div>
      <div class="dialog-footer">
        <button class="dialog-btn dialog-btn-secondary" @click="closeAdminEntityDialog">Cancel</button>
        <button class="dialog-btn dialog-btn-primary" @click="saveAdminEntityDialog" :disabled="adminSaving">Save</button>
      </div>
    </div>
  </div>

  <!-- Sub-factor -->
  <div v-if="subFactorEditing._active && subFactorEditing[subFactorEditing._active] && !subFactorLevelEditing._active" class="dialog-overlay dialog-overlay--form" @click="cancelEditSubFactor(subFactorEditing._activeObj)">
    <div class="dialog-content dialog-content--form" style="max-width: 520px;" @click.stop>
      <div class="dialog-header">
        <h3>{{ subFactorEditing._activeObj?._isNew ? 'Add' : 'Edit' }} {{ levelling_framework?.sub_factor_label || 'Sub-Factor' }}</h3>
        <button class="dialog-close-btn" @click="cancelEditSubFactor(subFactorEditing._activeObj)">&times;</button>
      </div>
      <div class="dialog-body admin-dialog-body">
        <div class="admin-form-field">
          <label>Name</label>
          <input v-model="subFactorEditing[subFactorEditing._active].name" class="admin-input" />
        </div>
        <div class="admin-form-field">
          <label>Description</label>
          <textarea v-model="subFactorEditing[subFactorEditing._active].description" class="admin-input" rows="4"></textarea>
        </div>
        <div class="admin-form-row">
          <div class="admin-form-field">
            <label>Position <span class="admin-required">*</span></label>
            <input v-model.number="subFactorEditing[subFactorEditing._active].position" type="number" class="admin-input" :class="{ 'admin-input-error': subFactorErrors.position }" style="width: 80px;" />
            <span v-if="subFactorErrors.position" class="admin-field-error">{{ subFactorErrors.position }}</span>
          </div>
          <div class="admin-form-field">
            <label>Weighting</label>
            <input v-model.number="subFactorEditing[subFactorEditing._active].weighting" type="number" step="0.1" class="admin-input" style="width: 80px;" />
          </div>
        </div>
      </div>
      <div class="dialog-footer">
        <button class="dialog-btn dialog-btn-secondary" @click="cancelEditSubFactor(subFactorEditing._activeObj)">Cancel</button>
        <button class="dialog-btn dialog-btn-primary" @click="saveSubFactor(subFactorEditing._activeObj)" :disabled="subFactorSaving">Save</button>
      </div>
    </div>
  </div>

  <!-- Sub-factor level -->
  <div v-if="subFactorLevelEditing._active && subFactorLevelEditing[subFactorLevelEditing._active]" class="dialog-overlay dialog-overlay--form" @click="cancelEditSubFactorLevel(subFactorLevelEditing._activeObj)">
    <div class="dialog-content dialog-content--form" style="max-width: 560px;" @click.stop>
      <div class="dialog-header">
        <h3>{{ subFactorLevelEditing._activeObj?._isNew ? 'Add' : 'Edit' }} {{ levelling_framework?.sub_factor_label || 'Sub-Factor' }} Level</h3>
        <button class="dialog-close-btn" @click="cancelEditSubFactorLevel(subFactorLevelEditing._activeObj)">&times;</button>
      </div>
      <div class="dialog-body admin-dialog-body">
        <div class="admin-form-field">
          <label>Name</label>
          <input v-model="subFactorLevelEditing[subFactorLevelEditing._active].name" class="admin-input" />
        </div>
        <div class="admin-form-field">
          <label>Description</label>
          <textarea v-model="subFactorLevelEditing[subFactorLevelEditing._active].description" class="admin-input" rows="4"></textarea>
        </div>
        <div class="admin-form-row">
          <div class="admin-form-field">
            <label>Position <span class="admin-required">*</span></label>
            <input v-model.number="subFactorLevelEditing[subFactorLevelEditing._active].position" type="number" class="admin-input" :class="{ 'admin-input-error': subFactorLevelErrors.position }" style="width: 80px;" />
            <span v-if="subFactorLevelErrors.position" class="admin-field-error">{{ subFactorLevelErrors.position }}</span>
          </div>
          <div class="admin-form-field">
            <label>Weighting</label>
            <input v-model.number="subFactorLevelEditing[subFactorLevelEditing._active].weighting" type="number" step="0.1" class="admin-input" style="width: 80px;" />
          </div>
          <div class="admin-form-field">
            <label>Score</label>
            <input v-model.number="subFactorLevelEditing[subFactorLevelEditing._active].score" type="number" class="admin-input" style="width: 80px;" />
          </div>
        </div>
        <div class="admin-form-field">
          <label>Scope</label>
          <textarea v-model="subFactorLevelEditing[subFactorLevelEditing._active].scope" class="admin-input" rows="5"></textarea>
        </div>
      </div>
      <div class="dialog-footer">
        <button class="dialog-btn dialog-btn-secondary" @click="cancelEditSubFactorLevel(subFactorLevelEditing._activeObj)">Cancel</button>
        <button class="dialog-btn dialog-btn-primary" @click="saveSubFactorLevel(subFactorLevelEditing._activeObj)" :disabled="subFactorSaving">Save</button>
      </div>
    </div>
  </div>

  <!-- Level Function Track Dialog (Add/Edit) -->
  <div v-if="lftDialog.isOpen" class="dialog-overlay" @click="closeLftDialog">
    <div class="dialog-content" style="max-width: 500px;" @click.stop>
      <div class="dialog-header">
        <h3>{{  lftDialog.title }}</h3>
        <button class="dialog-close-btn" @click="closeLftDialog">&times;</button>
      </div>
      <div class="dialog-body" style="flex-direction: column; gap: 12px; padding: 16px;">
        <div class="admin-form-field">
          <label>Name</label>
          <input :readonly="!is_editable" v-model="lftDialog.form.name" class="admin-input" />
        </div>
        <div class="admin-form-field">
          <label>Abbreviation</label>
          <input :readonly="!is_editable" v-model="lftDialog.form.abbreviation" class="admin-input" style="width: 100px;" />
        </div>
        <div class="admin-form-field">
          <label>Evaluation Score Range</label>
          <input :readonly="!is_editable" v-model="lftDialog.form.evaluation_score_minimum" class="admin-input mr-4" style="width: 120px;" type="number" />
          <input :readonly="!is_editable" v-model="lftDialog.form.evaluation_score_maximum" class="admin-input" style="width: 120px;" type="number" />
        </div>
        <div class="admin-form-field">
          <label>Description</label>
          <textarea :readonly="!is_editable" v-model="lftDialog.form.description" class="admin-input" rows="4"></textarea>
        </div>
      </div>
      <div class="dialog-footer" v-if="is_editable">
        <button class="dialog-btn dialog-btn-secondary" @click="closeLftDialog">Cancel</button>
        <button class="dialog-btn dialog-btn-primary" @click="saveLftDialog" :disabled="lftDialog.saving || !lftDialog.form.name.trim()">Save</button>
      </div>
    </div>
  </div>

  <!-- Sub-Factor Level Edit Dialog -->
  <div v-if="sfdEditDialog.isOpen" class="dialog-overlay" @click="closeSfdEditDialog">
    <div class="dialog-content" style="max-width: 700px;" @click.stop>
      <div class="dialog-header">
        <div>
          <h3 class="dialog-title">Edit Sub-Factor Level</h3>
          <div style="font-size: 13px; color: #666; margin-top: 2px;">{{ sfdEditDialog.factorName }} &mdash; {{ sfdEditDialog.subFactorName }}</div>
        </div>
        <button class="dialog-close-btn" @click="closeSfdEditDialog">&times;</button>
      </div>
      <div class="dialog-body" style="flex-direction: column; gap: 16px; padding: 1.5rem; align-items: stretch;">
        <div>
          <label class="sfd-edit-label">Proficiency</label>
          <span
            outlined
          >{{ sfdEditDialog.level.position }}</span>
        </div>
        <div>
          <label class="sfd-edit-label">Name</label>
          <v-text-field
            v-model="sfdEditDialog.level.name"
            dense
            outlined
            hide-details
          ></v-text-field>
        </div>
        <div>
          <label class="sfd-edit-label">Description</label>
          <v-textarea
            v-model="sfdEditDialog.level.description"
            dense
            outlined
            hide-details
            rows="3"
            auto-grow
          ></v-textarea>
        </div>
        <div>
          <label class="sfd-edit-label">Scope</label>
          <v-textarea
            v-model="sfdEditDialog.level.scope"
            dense
            outlined
            hide-details
            rows="4"
            auto-grow
          ></v-textarea>
        </div>
      </div>
      <div class="dialog-footer">
        <button class="dialog-btn dialog-btn-secondary" @click="closeSfdEditDialog">Cancel</button>
        <button class="dialog-btn dialog-btn-primary" @click="saveSfdEditDialog">Save</button>
      </div>
    </div>
  </div>
  <!-- Level Descriptor Dialog -->
  <div v-if="levelDescriptorDialog.isOpen" class="dialog-overlay" @click="closeLevelDescriptorDialog">
    <div class="dialog-content" style="max-width: 600px;" @click.stop>
      <div class="dialog-header">
        <h3>{{ levelDescriptorDialog.title }}</h3>
        <button class="dialog-close-btn" @click="closeLevelDescriptorDialog">&times;</button>
      </div>
      <div class="dialog-body" style="max-height: 400px; overflow-y: auto; flex-direction: column;">
        <div
          v-for="(item, index) in levelDescriptorDialog.items"
          :key="index"
          class="level-descriptor-item"
        >
          <div class="descriptor-name">{{ item.function_name }}
            <div class="track-code-chip" :style="{ backgroundColor: item.background_colour || '#FFF9E6' }">{{ item.abbreviation }}</div>
            <span style="float: right">Score Range: {{ item.evaluation_score_minimum }} to {{ item. evaluation_score_maximum }} </span>
          </div>
          <div class="descriptor-text">
            <GridInlineEdit
              type="textarea"
              :rows="4"
              :editing="isGridInlineEditing('lf_level_function_track', 'description', item._source)"
              :value="gridInlineEdit.value"
              :editable="is_editable"
              @input="gridInlineEdit.value = $event"
              @save="saveGridInlineEdit(item._source, true)"
              @cancel="cancelGridInlineEdit"
              @start="startGridInlineEdit('lf_level_function_track', 'description', item._source)"
            >{{ item.description || 'No description available' }}</GridInlineEdit>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Tab Settings Dialog -->
  <div v-if="tabSettingsDialog.isOpen" class="dialog-overlay" @click="closeTabSettingsDialog">
    <div class="dialog-content" style="max-width: 700px;" @click.stop>
      <div class="dialog-header">
        <h3>Tab Settings</h3>
        <button class="dialog-close-btn" @click="closeTabSettingsDialog">&times;</button>
      </div>
      <div class="dialog-body" style="flex-direction: column; gap: 0; padding: 0;">
        <div class="tab-settings-item">
          <label class="tab-settings-toggle" style="font-weight: 600;">
            <input type="checkbox" v-model="tabSettingsDialog.hideAllSettings" />
            Hide All Settings
          </label>
        </div>
        <div
          v-for="(tab, ti) in tabSettingsDialog.tabs"
          :key="ti"
          class="tab-settings-item"
        >
          <div class="tab-settings-item-header">
            <input v-model="tab.name" class="tab-settings-name-input" />
            <label class="tab-settings-visible">
              <input type="checkbox" v-model="tab.visible" />
              Visible
            </label>
          </div>
          <div v-if="Object.keys(tab.settings).length" class="tab-settings-grid">
            <template v-for="(val, key) in tab.settings">
              <label v-if="typeof val === 'boolean'" :key="key" class="tab-settings-toggle">
                <input type="checkbox" v-model="tab.settings[key]" />
                {{ settingLabel(key) }}
              </label>
              <label v-else :key="key + '-num'" class="tab-settings-toggle">
                {{ settingLabel(key) }}
                <input type="number" v-model.number="tab.settings[key]" style="width: 70px; margin-left: 8px; border: 1px solid #ccc; border-radius: 4px; padding: 2px 4px;" />
              </label>
            </template>
          </div>
          <div v-else class="tab-settings-empty">No configurable settings</div>
        </div>
      </div>
      <div class="dialog-footer">
        <button class="track-dialog-btn track-dialog-btn--cancel" @click="closeTabSettingsDialog">Cancel</button>
        <button class="track-dialog-btn track-dialog-btn--save" @click="saveTabSettingsDialog">Save</button>
      </div>
    </div>
  </div>

  <!-- Track Dialog -->
  <div v-if="trackDialog.isOpen" class="dialog-overlay dialog-overlay--form" @click="closeTrackDialog">
    <div class="dialog-content dialog-content--form" style="max-width: 500px;" @click.stop>
      <div class="dialog-header">
        <h3>{{ trackDialog.mode === 'edit' ? 'Edit ' + levelling_framework.track_label  : levelling_framework.track_label + ' Details' }}</h3>
        <button class="dialog-close-btn" @click="closeTrackDialog">&times;</button>
      </div>
      <div class="dialog-body admin-dialog-body" style="flex-direction: column; gap: 12px; padding: 16px;">
        <div class="admin-form-field">
          <label>Name</label>
          <input
            v-if="trackDialog.mode === 'edit'"
            v-model="trackDialog.form.name"
            class="admin-input"
          />
          <div v-else class="track-dialog-value">{{ trackDialog.form.name }}</div>
        </div>
        <div class="admin-form-field">
          <label>Description</label>
          <textarea
            v-if="trackDialog.mode === 'edit'"
            v-model="trackDialog.form.description"
            class="admin-input"
            rows="3"
          ></textarea>
          <div v-else class="track-dialog-value">{{ trackDialog.form.description || 'No description' }}</div>
        </div>
        <div class="admin-form-row">
          <div class="admin-form-field">
            <label>Position</label>
            <input
              v-if="trackDialog.mode === 'edit'"
              v-model.number="trackDialog.form.position"
              type="number"
              class="admin-input"
              style="width: 80px;"
            />
            <div v-else class="track-dialog-value">{{ trackDialog.form.position }}</div>
          </div>
          <div class="admin-form-field">
            <label>Abbreviation</label>
            <input
              v-if="trackDialog.mode === 'edit'"
              v-model="trackDialog.form.abbreviation"
              class="admin-input"
              style="width: 120px;"
            />
            <div v-else class="track-dialog-value">{{ trackDialog.form.abbreviation }}</div>
          </div>
        </div>
        <div class="admin-form-field">
          <label>Colour</label>
          <div v-if="trackDialog.mode === 'edit'" class="admin-colour-picker">
            <div class="admin-colour-selected admin-input" @click="trackDialog.colourDropdownOpen = !trackDialog.colourDropdownOpen">
              <template v-if="trackDialog.form.display_colour_id">
                <div class="admin-colour-chip" :style="{ backgroundColor: (levelling_framework?.display_colours || []).find(c => c.display_colour_id === trackDialog.form.display_colour_id)?.background_colour }"></div>
                <span>{{ (levelling_framework?.display_colours || []).find(c => c.display_colour_id === trackDialog.form.display_colour_id)?.colour_name }}</span>
              </template>
              <span v-else class="admin-colour-none">None</span>
              <v-icon small class="admin-colour-arrow">mdi-chevron-down</v-icon>
            </div>
            <div v-if="trackDialog.colourDropdownOpen" class="admin-colour-dropdown">
              <div class="admin-colour-option" @click="trackDialog.form.display_colour_id = null; trackDialog.colourDropdownOpen = false">
                <span class="admin-colour-none">None</span>
              </div>
              <div v-for="dc in (levelling_framework?.display_colours || [])" :key="dc.display_colour_id"
                   class="admin-colour-option"
                   :class="{ 'admin-colour-option-active': trackDialog.form.display_colour_id === dc.display_colour_id }"
                   @click="trackDialog.form.display_colour_id = dc.display_colour_id; trackDialog.colourDropdownOpen = false">
                <div class="admin-colour-chip" :style="{ backgroundColor: dc.background_colour }"></div>
                <span>{{ dc.colour_name }}</span>
              </div>
            </div>
          </div>
          <div v-else class="track-dialog-value">
            <div v-if="trackDialog.form.display_colour_id" style="display: flex; align-items: center; gap: 8px;">
              <div class="admin-colour-chip" :style="{ backgroundColor: (levelling_framework?.display_colours || []).find(c => c.display_colour_id === trackDialog.form.display_colour_id)?.background_colour }"></div>
              <span>{{ (levelling_framework?.display_colours || []).find(c => c.display_colour_id === trackDialog.form.display_colour_id)?.colour_name }}</span>
            </div>
            <span v-else>None</span>
          </div>
        </div>
      </div>
      <div v-if="trackDialog.mode === 'edit'" class="dialog-footer">
        <button class="dialog-btn dialog-btn-secondary" @click="closeTrackDialog">Cancel</button>
        <button class="dialog-btn dialog-btn-primary" @click="saveTrackDialog" :disabled="trackDialog.saving">Save</button>
      </div>
    </div>
  </div>

  <div v-if="errorPopup.isOpen" class="dialog-overlay" @click="errorPopup.isOpen = false">
    <div class="dialog-content" style="max-width: 400px;" @click.stop>
      <div class="dialog-header">
        <h3>Error</h3>
        <button class="dialog-close-btn" @click="errorPopup.isOpen = false">&times;</button>
      </div>
      <div class="dialog-body" style="padding: 16px;">
        <p>{{ errorPopup.message }}</p>
      </div>
      <div class="dialog-footer">
        <button class="dialog-btn dialog-btn-primary" @click="errorPopup.isOpen = false">OK</button>
      </div>
    </div>
  </div>

  </div>
</template>

<script>
import { ref, reactive, computed, watch, set, markRaw, onBeforeUnmount, onMounted, getCurrentInstance } from 'vue';
import RmSelect from '@/components/common/RmSelect.vue';
import GridInlineEdit from '@/components/levelling/GridInlineEdit.vue';
import LevellingReports from '@/components/levelling/LevellingReports.vue';
import axios from "axios";
import { inject } from 'vue';
import { shortName } from '@rm-ui/levelling/levellingMeta';
import { levellingTour, LEVELLING_FRAMEWORK_TOUR, LEVELLING_FRAMEWORK_IN_USE_TOUR } from '@rm-ui/levelling/levellingTour';
import CcTour from '@/components/levelling/controlCentre/CcTour.vue';
import FrameworkInUseView from '@/components/levelling/FrameworkInUseView.vue';
import GradeAlignmentView from '@/components/levelling/GradeAlignmentView.vue';
import { levellingAnchors } from '@rm-ui/levelling/levellingAnchors';


/* ---- Chip tint (design: .lvl-grade-chip) ----
   Track colours are authored as pale cell washes, so knocking the chip's label
   out in white against the raw colour lands around 1.2:1. Darken to the
   lightest shade that still clears WCAG AA (4.5:1) on white, which is a
   relative luminance of 0.1833.

   The darkening runs in HSL: as lightness drops, the hue's chroma comes back,
   so a pastel yellow reads as amber. Scaling the sRGB channels instead would
   just slide every pastel toward grey, since their channels all start near
   255. */
const CHIP_MAX_LUMINANCE = 1.05 / 4.5 - 0.05;
const chipColourCache = new Map();

const srgbChannelToLinear = (c) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

const relativeLuminance = ([r, g, b]) =>
  0.2126 * srgbChannelToLinear(r) + 0.7152 * srgbChannelToLinear(g) + 0.0722 * srgbChannelToLinear(b);

const hexToRgbTriplet = (hex) => {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

const rgbToHsl = ([r, g, b]) => {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h, s, l];
};

const hslToRgb = (h, s, l) => {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)].map(v => Math.round(v * 255));
};

const rgbToHex = ([r, g, b]) =>
  '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');

const chipColour = (hex) => {
  const fallback = '#5b6472';
  const rgb = hexToRgbTriplet(hex);
  if (!rgb) return fallback;
  const key = rgbToHex(rgb);
  if (chipColourCache.has(key)) return chipColourCache.get(key);
  let result = key;
  if (relativeLuminance(rgb) > CHIP_MAX_LUMINANCE) {
    const [h, sat, l0] = rgbToHsl(rgb);
    // Largest lightness still under the luminance ceiling; luminance rises
    // monotonically with lightness, so bisection converges on it.
    let lo = 0, hi = l0;
    for (let i = 0; i < 16; i++) {
      const mid = (lo + hi) / 2;
      if (relativeLuminance(hslToRgb(h, sat, mid)) <= CHIP_MAX_LUMINANCE) lo = mid;
      else hi = mid;
    }
    result = rgbToHex(hslToRgb(h, sat, lo));
  }
  chipColourCache.set(key, result);
  return result;
};

/* ---- Levelling terms (design: LVL_GLOSSARY) ----
   These definitions otherwise exist only inside tooltips - invisible on touch,
   and unreachable once the page has been read once. This is the place a term
   can be looked up again. */
const LEVELLING_GLOSSARY = [
  { term: 'Anchor role', definition: 'An evaluated role the rest of the register is compared against. Anchors need a full, point-scored evaluation \u2014 a classification cannot be a reference.' },
  { term: 'CEO ceiling', definition: 'The anchor that fixes the top of the framework. Classification stops offering levels at or above it, and an evaluation landing there is flagged for review, never blocked.' },
  { term: 'Spine', definition: 'One job family anchored top to bottom, so every level has a reference above and below it. Easier to calibrate than isolated senior roles.' },
  { term: 'Coverage floor', definition: 'The minimum number of evaluated anchors a level needs before it can be trusted as a reference \u2014 2 for most organisations, 3 for large or dispersed ones.' },
  { term: 'Populated level or track', definition: 'A level or track that at least one role actually sits at. Empty ones are not gaps: a level nobody occupies does not need an anchor.' },
  { term: 'Read-across', definition: 'The dated, published link between your existing grades and RoleMapper levels. Classification uses it as a starting point. Transitional by design.' },
  { term: 'Divergence', definition: 'The same grade sitting at different levels depending on the function \u2014 usually a sign the grade means different things in different parts of the business.' },
  { term: 'Thin evidence', definition: 'Fewer evaluated roles behind a link than the floor requires. The link still works; it is just not yet dependable.' },
  { term: 'Overridden', definition: 'A link an admin set by hand, away from where the evidence points. The confidence shown still describes the evidence, because the override did not change it.' },
  { term: 'Track', definition: 'The kind of work a role does \u2014 Operational, Individual Contributor, Management, Executive. Tracks run alongside each other; they are not a ranking.' },
];

const TAB_VIEWS = [
   // Levels and Tracks absorbs the old "Level Descriptors" tab: same grid, with
   // the View segment in the filter bar switching showDescriptions on and off.
   { name: "Levels and Tracks", viewType: "level_track", visible: true, settings: { showDescriptions: false, fixedCellHeight: false } },
   /* The register plotted on the framework (PD-1292). Hidden until the client has
      anchor roles switched on AND the user is a framework admin — the tab is
      anchors, coverage and governance, none of which a non-admin can act on, and
      stripped of those it would only duplicate Levels and Tracks. Revealed in
      setup() once the anchors composable has answered. */
   { name: "Framework in Use", viewType: "framework_in_use", visible: false, settings: {},
     tip: "Your register plotted on the framework: which levels and tracks your roles actually occupy, which are anchored, and where coverage falls short." },
   /* Grade read-across (PD-1293). Revealed only when the client has the feature on AND
      a template attribute is marked LEGACY GRADE — with no grades captured there is
      nothing to line up, so the tab would open on an explanation of its own absence.
      Governing is gated separately, by canGovern: without it the panel is read-only. */
   { name: "Grade Alignment", viewType: "grade_alignment", visible: false, settings: {},
     tip: "Your existing grades lined up against the RoleMapper ladder, so you can read across in either language." },
   { name: "Factor Descriptors", viewType: "level_track_factors", visible: true, settings: { showDescriptions: false, fixedCellHeight: true, minColumnWidth: 200, maxColumnWidth: 350 } },
   { name: "Sub-Factor Descriptors", viewType: "sub_factor_descriptions", visible: false, settings: { showHeaderFactorColours: true, showHeaderSubFactorColours: false, showInfoIcons: true, showDetailDescription: false, minColumnWidth: 200, maxColumnWidth: 350 } },
   { name: "Factor Details", viewType: "sub_factors", visible: true, settings: {} },
   { name: "Reports", viewType: "reports", visible: false, settings: {} },
   { name: "Administration", viewType: "administration", visible: false, settings: {} },
  //  { name: "Factors", viewType: "factors", visible: true, settings: {} },
   
];

export default {
  name: "FrameworkVisualization",
  components: { RmSelect, GridInlineEdit, LevellingReports, CcTour, FrameworkInUseView, GradeAlignmentView },
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    // --- Reactive state ---
    const loginState = inject('loginState');
    const hideAllSettings = ref(true);
    /* Page functions (PD-1331). The tabs listed in the levellingframework route's
       meta.functions are granted per permission group in Page Permissions; any other
       tab (Levels and Tracks) comes with the page. The server has already dropped
       functions for users who cannot use the route itself. This is UX only - the
       endpoints re-check the same grants. */
    const setupInstance = getCurrentInstance();
    const frameworkRoute = ((setupInstance && setupInstance.proxy.$router && setupInstance.proxy.$router.getRoutes()) || [])
      .find(r => r.name === 'levellingframework');
    const permissionedTabs = frameworkRoute ? (frameworkRoute.meta.functions || []).map(f => f.name) : null;
    const grantedTabs = ((loginState && loginState.user && loginState.user.userPermittedFunctions) || {}).levellingframework || [];
    // Fails closed: if the route can't be found, only Levels and Tracks is shown.
    const canUseTab = (viewType) => grantedTabs.includes(viewType)
      || (permissionedTabs ? !permissionedTabs.includes(viewType) : viewType === 'level_track');
    // The Administration function is framework admin: the tab, import, settings and
    // editing Levels and Tracks. It replaces the levelling_framework_admin setting.
    const hasAdminPermission = ref(grantedTabs.includes('administration'));
    // Whether this framework COULD be edited by this user, and whether they have
    // actually asked to. The framework opens read-only for everyone; an admin
    // opts into editing from the page head, which is what gates every edit
    // affordance below (is_editable).
    const canEditFramework = ref(false);
    const editMode = ref(false);

    // --- Administration tab state ---
    const adminFrameworkList = ref([]);
    const adminSaving = ref(false);
    const adminActiveEntityTab = ref(0);
    const adminEntityDialog = reactive({
      isOpen: false,
      mode: 'add', // 'add' or 'edit'
      entityType: '', // 'level', 'function', 'track', 'factor'
      entityLabel: '',
      form: {},
      original: null,
      colourDropdownOpen: false,
      errors: {},
    });
    const adminFrameworkDialog = reactive({
      isOpen: false,
      mode: 'add',
      form: { name: '', description: '', level_label: 'Level', function_label: 'Function', track_label: 'Track', level_track_label: 'Job Level', factor_label: 'Factor', sub_factor_label: 'Sub-Factor' },
    });
    const errorPopup = reactive({
      isOpen: false,
      message: '',
    });

    const adminDeleteConfirm = reactive({
      isOpen: false,
      entityType: '',
      entityLabel: '',
      relatedLabel: '',
      item: null,
    });
    const adminCopyDialog = reactive({
      isOpen: false,
      item: null,
      form: { name: '', description: '' },
    });
    const importDialog = reactive({
      isOpen: false,
      loading: false,
      importing: false,
      clients: [],
      selectedFrameworkId: null,
      form: { name: '', description: '' },
    });
    const publishDialog = reactive({
      isOpen: false,
      loading: false,
      publishing: false,
      frameworks: [],
    });

    // Levels & tracks filters. Discipline is exclusive (primary), with an
    // optional second discipline opted into via "Compare with". Tracks and
    // levels are hide-lists / a level window rather than multi-selects, so the
    // grid shows everything until something is deliberately taken away.
    const primaryFunctionId = ref(null);
    const compareFunctionId = ref(null);
    const levelRange = ref('all');
    // Ascending (first level at the top) by default; the framework is authored
    // bottom-up, but a comp review usually reads a ladder senior-first, so the
    // Level header flips it. Shared by both grids - it reorders rows rather
    // than filtering them, so the two tabs agreeing is the lesser surprise.
    const levelSortDesc = ref(false);
    const hiddenLevelIds = ref([]);
    const hiddenTrackIds = ref([]);
    // Comparing two disciplines doubles the columns, so the track chips go
    // exclusive: one track, shown once per discipline. The hide-list the user
    // had built up is parked here and handed back when they drop the comparison.
    const comparisonTrackId = ref(null);
    const hiddenTracksBeforeCompare = ref([]);

    // --- Tab bar placement -------------------------------------------------
    // The design renders the framework's tabs inside the page header rather
    // than on a row of their own. LevellingPageShell exposes
    // #lvl-pagehead-slot for that; when it is there the tab row is moved into
    // it as soon as it exists, and when it is not (the legacy tabbed Levelling
    // view) the row simply stays where it was rendered.
    //
    // The row is a plain element with a single, never-toggled parent
    // (.rm-tabs-host), so moving it never leaves Vue looking for it as an
    // insertion anchor among changing siblings; patches to the tabs themselves
    // are relative to the row and work wherever it lives.
    //
    // What we DO have to do by hand is take the row away again. Once it has
    // been moved it is no longer a descendant of the card, so tearing the card
    // down (or this whole component) detaches everything EXCEPT the row — it
    // would sit on in the header showing dead tabs, and the replacement would
    // be appended alongside it. Hence `movedRow` + detach().
    const tabsRow = ref(null);
    const tabsInHead = ref(false);
    let movedRow = null;

    const detachMovedRow = () => {
      if (movedRow?.parentNode) movedRow.parentNode.removeChild(movedRow);
      movedRow = null;
    };

    // Driven off the ref rather than `onMounted`, because the tab row is inside
    // the `v-else-if` card and so does not exist until fetchData() returns —
    // long after mount. Watching the ref also covers the row being torn down and
    // rebuilt (switching framework), where a one-shot hook would only fire once.
    // The slot is looked up by walking UP from the row rather than via
    // document.getElementById: on the first patch the tree may not be attached
    // to the document yet, and an ancestor query works either way.
    watch(tabsRow, (row) => {
      detachMovedRow();               // anything lifted earlier is stale now
      if (!row) {
        tabsInHead.value = false;
        return;
      }
      const slot = row.closest('.lvl-main')
        ?.querySelector(':scope > .lvl-pagehead > .lvl-pagehead-slot');
      if (!slot) return;              // no page shell (the legacy tabbed view) — leave it inline
      slot.appendChild(row);
      movedRow = row;
      tabsInHead.value = true;
    }, { flush: 'post' });

    // The shell outlives this component on a route swap within it, so the row
    // has to be cleared out here too.
    onBeforeUnmount(detachMovedRow);

    // Factor descriptors reads one discipline x one track at a time - the grid is
    // levels x factor groups, so a second track would double the columns and
    // there is no read-across between them to justify it.
    const ltfFunctionId = ref(null);
    const ltfTrackId = ref(null);
    const ltfFactorId = ref(null);
    const ltfLevelRange = ref('all');
    // Which dimension is pinned, and therefore what the columns are:
    //   'track'  - one track, every factor group across  (read one ladder)
    //   'factor' - one factor group, every track across  (compare ladders)
    // Exactly one pill is selected across both groups, so picking a factor
    // releases the track and vice versa.
    const ltfPivot = ref('track');
    const subFactorLevels = ref([]);
    const ltfCellPopup = reactive({
      isOpen: false,
      text: '',
      factorName: '',
      levelPosition: '',
      groupLabel: '',
      top: 0,
      left: 0,
      trackFactor: null,
    });
    const factorsDialog = reactive({
      isOpen: false,
      saving: false,
      levelTrackTitle: '',
      subtitle: '',
      lf_level_function_track_id: null,
      factors: [],
      // Vertical accordion: one factor group open at a time, so the descriptor
      // being read always has the dialog's full width.
      expandedFactorId: null,
    });

    const lftDialog = reactive({
      isOpen: false,
      saving: false,
      mode: 'add', // 'add' or 'edit'
      func: null,
      level: null,
      track: null,
      levelFunctionTrack: null,
      title: null,
      form: {
        name: '',
        description: '',
        abbreviation: '',
        evaluation_score_minimum: 0,
        evaluation_score_maximum: 0,
      },
    });

    const sfdEditDialog = reactive({
      isOpen: false,
      subFactorName: '',
      factorName: '',
      level: null,
      originalLevel: null,
    });

    const levelDescriptorDialog = reactive({
      isOpen: false,
      title: '',
      items: [],
    });

    const trackDialog = reactive({
      isOpen: false,
      saving: false,
      mode: 'view', // 'view' or 'edit'
      track: null,
      colourDropdownOpen: false,
      form: {
        name: '',
        description: '',
        position: null,
        abbreviation: '',
        display_colour_id: null,
      },
    });

    const tabSettingsDialog = reactive({
      isOpen: false,
      hideAllSettings: false,
      tabs: [],
    });

    const openTabSettingsDialog = () => {
      tabSettingsDialog.hideAllSettings = hideAllSettings.value;
      // Only tabs this user is granted - the dialog must not be a way to reveal the others.
      tabSettingsDialog.tabs = tabViews.value.filter(tab => tab.permitted).map(tab => ({
        name: tab.name,
        viewType: tab.viewType,
        visible: tab.visible,
        settings: JSON.parse(JSON.stringify(tab.settings)),
      }));
      tabSettingsDialog.isOpen = true;
    };

    const closeTabSettingsDialog = () => {
      tabSettingsDialog.isOpen = false;
    };

    const saveTabSettingsDialog = () => {
      hideAllSettings.value = tabSettingsDialog.hideAllSettings;
      tabSettingsDialog.tabs.forEach((editedTab) => {
        const tab = tabViews.value.find(t => t.viewType === editedTab.viewType);
        if (!tab) return;
        tab.name = editedTab.name;
        tab.visible = editedTab.visible;
        Object.assign(tab.settings, editedTab.settings);
      });
      tabSettingsDialog.isOpen = false;
    };

    const settingLabel = (key) => {
      const labels = {
        showDescriptions: 'Show Descriptions',
        fixedCellHeight: 'Fixed Cell Height',
        showHeaderFactorColours: 'Header Factor Colours',
        showHeaderSubFactorColours: 'Header Sub-Factor Colours',
        showInfoIcons: 'Show Info Icons',
        showDetailDescription: 'Show Detail Description',
        minColumnWidth: 'Min Column Width (px)',
        maxColumnWidth: 'Max Column Width (px)',
      };
      return labels[key] || key;
    };

    const openLevelDescriptorDialog = (level) => {
      const functionIDs = selectedFunctions.value.map(x => x.lf_function_id);
      const trackIDs = filteredTracks.value.map(x => x.lf_track_id);
      const items = levelling_framework.value.level_function_tracks
        .filter(x => x.lf_level_id === level.lf_level_id
                      && functionIDs.some(f => f === x.lf_function_id)
                      && trackIDs.some(f => f === x.lf_track_id)
        )
        .map(x => {
          const func = levelling_framework.value.functions.find(f => f.lf_function_id === x.lf_function_id);
          const track = levelling_framework.value.tracks.find(f => f.lf_track_id === x.lf_track_id);
          return {
              function_name: func?.name || "",
              abbreviation: x.abbreviation,
              description: x.description || '',
              background_colour: track.background_colour,
              lf_level_function_track_id: x.lf_level_function_track_id,
              evaluation_score_minimum: x.evaluation_score_minimum,
              evaluation_score_maximum: x.evaluation_score_maximum,
              _source: x,
            };
        });
      levelDescriptorDialog.title = `${levelling_framework.value.level_label} ${level.name} Descriptors`;
      levelDescriptorDialog.items = items;
      levelDescriptorDialog.isOpen = true;
    };

    const closeLevelDescriptorDialog = () => {
      levelDescriptorDialog.isOpen = false;
      levelDescriptorDialog.items = [];
    };

    // Reactive state â€” populated by fetchData
    const levelling_framework = ref();
    const tabViews = ref(JSON.parse(JSON.stringify(TAB_VIEWS)).map(t => ({ ...t, permitted: canUseTab(t.viewType) })));
    if (hasAdminPermission.value) {
      const adminTab = tabViews.value.find(t => t.viewType === 'administration');
      if (adminTab) adminTab.visible = true;
    }
    const selectedTab = ref(tabViews.value[0]);

    /* Which tabs have ever been opened. Their components mount on first visit and
       then stay mounted (see the template) — so nothing is paid for a tab that is
       never opened, and nothing is torn down once it has been. */
    const visitedTabs = reactive({});
    watch(selectedTab, (t) => {
      if (t && t.viewType && !visitedTabs[t.viewType]) set(visitedTabs, t.viewType, true);
    }, { immediate: true });

    /* Anchor roles (PD-1258) — loaded HERE, not in the child, for two reasons: the
       Framework in Use tab cannot be revealed until we know the feature is on, and
       the grid's confirm modal needs the same composable instance that owns the
       write path. markRaw because this `self` object is passed as a prop and Vue
       2.7's reactive() would otherwise deep-wrap its refs.

       getUser is NOT optional despite its default: `enabled` falls back to the
       client setting on the user until the server answers, and load() SHORT-CIRCUITS
       when it reads false — so leaving getUser out means the fetch never happens,
       serverEnabled stays null, and the tab can never appear. */
    const anchors = markRaw(levellingAnchors({
      http: axios,
      getUser: () => loginState?.user,
      onToast: (msg) => console.warn(msg),
    }));
    onMounted(() => {
      anchors.load().then(() => {
        const tab = tabViews.value.find(t => t.viewType === 'framework_in_use');
        if (tab) tab.visible = anchors.enabled.value;
        if (!anchors.enabled.value || !canUseTab('grade_alignment')) return;
        /* Grade Alignment is nested under anchors, but has its own flag AND needs the
           register to actually capture a grade. `gradeMapping` answers both at once: it
           returns enabled:false when the flag is off, and an empty scheme list when no
           template attribute is marked LEGACY GRADE. */
        axios.post('document/gradeMapping', {}).then((resp) => {
          const data = (resp && resp.data && resp.data.Data) || {};
          const gradeTab = tabViews.value.find(t => t.viewType === 'grade_alignment');
          if (gradeTab) gradeTab.visible = !!data.enabled && !!(data.schemes || []).length;
        }).catch(() => {});
      });
    });

    /* An anchor is only ever set from a completed full evaluation, so the grid's
       routes OUT of itself land in the journey — never a write from the grid.
       `anchor=1` pre-sets the intent there, which locks it to a full evaluation. */
    const instance = getCurrentInstance();
    const goEvaluateRole = (role) => {
      const router = instance && instance.proxy && instance.proxy.$router;
      if (router && role && role.reference) {
        router.push({ name: 'evaluate', params: { docReference: role.reference }, query: { anchor: '1' } })
          .catch(() => {});
      }
    };

    // Edit mode is a Levels and Tracks concept - its toggle lives in that
    // grid's header. The other tabs keep the editability they always had, so
    // Administration is not silently locked behind a control it never shows.
    const is_editable = computed(() => {
      if (!canEditFramework.value) return false;
      if (selectedTab.value?.viewType !== 'level_track') return true;
      return editMode.value;
    });
    const setEditMode = (on) => { editMode.value = !!on && canEditFramework.value; };

    // --- Helper functions (no reactive dependencies) ---
    const sortedFunction_tracks = () => {
      if (!levelling_framework.value?.tracks) return [];
      return [...levelling_framework.value.tracks].sort((a, b) => a.position - b.position);
    };

    const sortedframework_levels = () => {
      if (!levelling_framework.value?.levels) return [];
      return [...levelling_framework.value.levels].sort((a, b) => a.position - b.position);
    };

    const getFunctionTracks = () => {
      return sortedFunction_tracks();
    };

    const getFunctionLevelTrack = (func, level, track) => {
      return getFunctionLevelTrackForID(func.lf_function_id, level.lf_level_id, track.lf_track_id);
    };
    const getGroupLevel = (group, level) => {
      return getFunctionLevelTrackForID(group.lf_function_id, level.lf_level_id, group.lf_track_id);
    };

    const getFunctionLevelTrackForID = (lf_function_id, lf_level_id, lf_track_id) => {
      const target = levelling_framework.value.level_function_tracks.find(x =>
        x.lf_function_id === lf_function_id &&
        x.lf_level_id === lf_level_id &&
        x.lf_track_id === lf_track_id
      )
      return target || null;
    };

    const getFunctionLevelTrackForKey = (lf_level_function_track_id) => {
      return levelling_framework.value.level_function_tracks.find(x =>
        x.lf_level_function_track_id === lf_level_function_track_id
      );
    };

    const getCellKey = (levelId, trackId) => {
      return `${levelId}-${trackId}`;
    };

    const hexToRgba = (hex, opacity) => {
      if (!hex) return `rgba(153, 153, 153, ${opacity})`;
      hex = hex.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const getNextId = (items, idField) => {
      if (!items || items.length === 0) return 1;
      const maxId = Math.max(...items.map(item => item[idField] || 0));
      return maxId + 1;
    };

    // --- Computed properties ---
    const selectedFunctions = computed(() => {
      const functions = levelling_framework.value?.functions || [];
      if (functions.length === 0) return [];
      const primary = functions.find(f => f.lf_function_id === primaryFunctionId.value) || functions[0];
      const compare = compareFunctionId.value === null
        ? null
        : functions.find(f => f.lf_function_id === compareFunctionId.value);
      return compare && compare !== primary ? [primary, compare] : [primary];
    });

    // Everything except the primary — the primary can't be compared with itself.
    const compareFunctionOptions = computed(() => {
      const functions = levelling_framework.value?.functions || [];
      return functions.filter(f => f.lf_function_id !== (selectedFunctions.value[0] || {}).lf_function_id);
    });

    // Two disciplines on the grid. Everything that switches the track chips
    // between hide-list and exclusive keys off this, so the mode can't drift
    // from what the grid is actually rendering.
    const isComparing = computed(() => selectedFunctions.value.length > 1);

    const setPrimaryFunction = (functionId) => {
      primaryFunctionId.value = functionId;
      // The new primary can't also be the comparison.
      if (compareFunctionId.value === functionId) compareFunctionId.value = null;
    };

    // Clicking the active comparison again clears it, so the pill reads as a toggle.
    const setCompareFunction = (functionId) => {
      compareFunctionId.value = compareFunctionId.value === functionId ? null : functionId;
    };

    // The level windows offered as pills are derived from the framework, not
    // hard-coded: each track spans the levels it actually has cells on, and
    // tracks sharing a span collapse into one pill (so an IC/management/exec
    // framework offers its three real windows). Tracks whose spans are all
    // identical give nothing to choose between, so fall back to three even
    // slices of the ladder.
    const trackLevelSpans = computed(() => {
      const levels = sortedframework_levels();
      const tracks = sortedFunction_tracks();
      const lfts = levelling_framework.value?.level_function_tracks || [];
      const positionOf = new Map(levels.map((l, i) => [l.lf_level_id, i]));
      const spans = [];
      tracks.forEach((track) => {
        const indexes = lfts
          .filter(x => x.lf_track_id === track.lf_track_id && positionOf.has(x.lf_level_id))
          .map(x => positionOf.get(x.lf_level_id));
        if (!indexes.length) return;
        const lo = Math.min(...indexes);
        const hi = Math.max(...indexes);
        const same = spans.find(sp => sp.lo === lo && sp.hi === hi);
        if (same) { same.tracks.push(track); return; }
        spans.push({ lo, hi, tracks: [track] });
      });
      return spans.sort((a, b) => a.lo - b.lo || a.hi - b.hi);
    });

    const levelRangeOptions = computed(() => {
      const levels = sortedframework_levels();
      const all = [{ value: 'all', label: 'All ' + (levelling_framework.value?.level_label || 'Level') + 's', lo: 0, hi: levels.length - 1 }];
      if (!levels.length) return all;
      const label = (lo, hi) => (levels[lo]?.name ?? lo + 1) + '\u2013' + (levels[hi]?.name ?? hi + 1);
      const spans = trackLevelSpans.value;
      if (spans.length > 1) {
        return all.concat(spans.map(sp => ({
          value: sp.lo + '-' + sp.hi,
          label: label(sp.lo, sp.hi),
          title: sp.tracks.map(t => t.name).join(' + '),
          lo: sp.lo,
          hi: sp.hi,
        })));
      }
      const step = Math.ceil(levels.length / 3);
      for (let lo = 0; lo < levels.length; lo += step) {
        const hi = Math.min(levels.length - 1, lo + step - 1);
        all.push({ value: lo + '-' + hi, label: label(lo, hi), lo, hi });
      }
      return all;
    });

    const activeLevelRange = computed(() => {
      const options = levelRangeOptions.value;
      return options.find(r => r.value === levelRange.value) || options[0];
    });

    const filteredLevels = computed(() => {
      const sorted = sortedframework_levels();
      const range = activeLevelRange.value;
      const rows = sorted.filter((level, i) =>
        (!range || (i >= range.lo && i <= range.hi))
        && !hiddenLevelIds.value.includes(level.lf_level_id));
      return levelSortDesc.value ? [...rows].reverse() : rows;
    });

    const sortedTracks = computed(() => sortedFunction_tracks());

    // Watched rather than folded into setCompareFunction so every route into
    // and out of comparison is covered - including picking a primary that was
    // the comparison, which clears it as a side effect.
    watch(isComparing, (comparing) => {
      if (comparing) {
        hiddenTracksBeforeCompare.value = hiddenTrackIds.value.slice();
        const tracks = sortedFunction_tracks();
        const first = tracks.find(t => !hiddenTrackIds.value.includes(t.lf_track_id)) || tracks[0];
        comparisonTrackId.value = first ? first.lf_track_id : null;
      } else {
        hiddenTrackIds.value = hiddenTracksBeforeCompare.value.slice();
        hiddenTracksBeforeCompare.value = [];
        comparisonTrackId.value = null;
      }
    });

    const isTrackHidden = (trackId) => (isComparing.value
      ? comparisonTrackId.value !== trackId
      : hiddenTrackIds.value.includes(trackId));

    const toggleTrack = (trackId) => {
      // Exclusive while comparing - one track always stays on, so clicking the
      // selected chip again is a no-op rather than emptying the grid.
      if (isComparing.value) {
        comparisonTrackId.value = trackId;
        return;
      }
      hiddenTrackIds.value = isTrackHidden(trackId)
        ? hiddenTrackIds.value.filter(id => id !== trackId)
        : hiddenTrackIds.value.concat(trackId);
    };

    // While editing, a cell is a way into its edit dialog. Read-only, it does
    // what the design's matrix does: a compact cell expands the descriptor for
    // every level, and a descriptor cell collapses back to codes only.
    const onTrackCellClick = (func, level, track) => {
      if (is_editable.value) {
        openLftDialog(func, level, track, 'edit');
        return;
      }
      selectedTab.value.settings.showDescriptions = !selectedTab.value.settings.showDescriptions;
    };

    // How many cells this discipline actually defines, across the whole
    // framework rather than the current window - the header describes the
    // discipline, not the filtered view.
    const cellsDefinedFor = (func) => (levelling_framework.value?.level_function_tracks || [])
      .filter(x => x.lf_function_id === func.lf_function_id).length;

    const gridScopeTitle = computed(() => {
      const funcs = selectedFunctions.value;
      if (!funcs.length) return '';
      if (funcs.length > 1) {
        // Comparing: one track is shown, so name it - the disciplines alone
        // would not say which ladder is on screen.
        const track = filteredTracks.value[0];
        return funcs.map(f => f.name).join(' vs ') + (track ? ' \u00b7 ' + track.name : '');
      }
      return funcs[0].name + ' ' + (levelling_framework.value?.function_label || 'Discipline').toLowerCase();
    });

    const gridScopeMeta = computed(() => {
      const funcs = selectedFunctions.value;
      if (!funcs.length) return '';
      const counts = funcs.map(f => cellsDefinedFor(f)).join(' vs ');
      return counts + ' cells defined' + (editMode.value ? '' : ' \u00b7 read-only');
    });

    // Hidden in the hide-list sense reads as a flat grey chip. While comparing,
    // an unpicked track keeps its own colour and CSS desaturates it - it is not
    // hidden, just not the one on screen.
    const trackChipTint = (track) => (!isComparing.value && isTrackHidden(track.lf_track_id)
      ? 'var(--cc-text-3, #97a0af)'
      : chipColour(track.background_colour));

    // The Administration tab's four registers, labelled with the framework's own
    // terms. Order matches the panels below it.
    // Index order of the register pills and their panels.
    const ADMIN_REGISTER_TYPES = ['level', 'function', 'track', 'factor'];

    const adminActiveRegisterType = computed(() =>
      ADMIN_REGISTER_TYPES[adminActiveEntityTab.value] || ADMIN_REGISTER_TYPES[0]);

    const adminAddLabel = computed(() => {
      const lf = levelling_framework.value || {};
      const type = adminActiveRegisterType.value;
      return 'Add ' + (lf[type + '_label'] || type).toLowerCase();
    });

    const adminRegisters = computed(() => {
      const lf = levelling_framework.value || {};
      return [
        (lf.level_label || 'Level') + 's',
        (lf.function_label || 'Function') + 's',
        (lf.track_label || 'Track') + 's',
        (lf.factor_label || 'Factor') + 's',
      ];
    });

    /* Onboarding tour for Levels and tracks. markRaw'd like the control centre's,
       because Vue 2.7 would otherwise walk the composable's internals when it
       crosses the CcTour prop boundary. Replay-only for now: no auto-run, since
       the button is the way in. */
    const tour = markRaw(levellingTour({
      steps: LEVELLING_FRAMEWORK_TOUR,
      storageKey: `lvl.fwTourDone:${loginState?.user?.user_id || 'default'}`,
    }));

    /* Framework in Use has its own tour: its own targets, and its own completion key,
       so finishing one tab's walkthrough doesn't silently mark the other's as seen. */
    const tourInUse = markRaw(levellingTour({
      steps: LEVELLING_FRAMEWORK_IN_USE_TOUR,
      storageKey: `lvl.fwInUseTourDone:${loginState?.user?.user_id || 'default'}`,
    }));

    /* Which walkthrough the header button runs. Null on the tabs that have none, which
       is what hides the button rather than offering a tour that points at nothing. */
    const activeTour = computed(() => {
      const view = selectedTab.value?.viewType;
      if (view === 'level_track') return tour;
      if (view === 'framework_in_use') return tourInUse;
      return null;
    });
    const activeTourTip = computed(() => (selectedTab.value?.viewType === 'framework_in_use'
      ? 'Walk through this view: the grid, what an empty cell means, anchors and coverage.'
      : 'Walk through this framework: the tabs, the ladder, the filters and where the terms are explained.'));

    // Levelling terms dialog. Escape closes it, captured on the document so it
    // works wherever focus happens to be inside the modal.
    const glossaryOpen = ref(false);
    const closeGlossary = () => { glossaryOpen.value = false; };

    const onGlossaryKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); closeGlossary(); }
    };

    watch(glossaryOpen, (open) => {
      if (open) document.addEventListener('keydown', onGlossaryKey, true);
      else document.removeEventListener('keydown', onGlossaryKey, true);
    });

    onBeforeUnmount(() => document.removeEventListener('keydown', onGlossaryKey, true));

    // Factor details: which sub-factor the right-hand drill panel is showing.
    const factorDrill = reactive({ isOpen: false, factor: null, subFactor: null });

    const openFactorDrill = (factor, subFactor) => {
      factorDrill.factor = factor;
      factorDrill.subFactor = subFactor;
      factorDrill.isOpen = true;
    };

    const closeFactorDrill = () => {
      factorDrill.isOpen = false;
      factorDrill.factor = null;
      factorDrill.subFactor = null;
    };

    // `scope` is stored as one newline-separated string; the design shows it as
    // bullets, so split it and drop blank lines.
    const scopeLines = (scope) => String(scope || '')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    // The two standing facts about this framework, shown under the filters on
    // every tab that reads it. Defined once here so the three render sites
    // cannot drift apart.
    const frameworkNotes = computed(() => {
      const lf = levelling_framework.value;
      if (!lf) return [];
      const factorLabel = (lf.factor_label || 'factor').toLowerCase();
      const levelLabel = (lf.level_label || 'Level').toLowerCase();
      const trackLabel = (lf.track_label || 'track').toLowerCase();
      return [
        {
          icon: 'mdi-tune',
          text: 'One ' + factorLabel + ' set, organisation-wide',
          tip: (lf.function_label || 'Discipline') + 's change the wording of the levelling questions, '
            + 'not the ' + factorLabel + 's or their weightings. Every role is scored against this one set.',
        },
        {
          icon: 'mdi-cog-outline',
          text: 'This organisation\u2019s framework \u00b7 '
            + (lf.levels || []).length + ' ' + levelLabel + 's \u00b7 '
            + (lf.tracks || []).length + ' ' + trackLabel + 's',
          tip: (lf.level_label || 'Level') + 's, ' + trackLabel + 's and grade codes are configured per '
            + 'organisation \u2014 this framework is one configuration, not a product limit.',
        },
      ];
    });

    const trackChipTitle = (track) => {
      if (isComparing.value) return 'Compare ' + track.name;
      return (isTrackHidden(track.lf_track_id) ? 'Show ' : 'Hide ') + track.name;
    };

    const filteredTracks = computed(() => {
      // const tracks = getFunctionTracks();
      const tracks = sortedFunction_tracks();
      if (isComparing.value) {
        const selected = tracks.find(t => t.lf_track_id === comparisonTrackId.value);
        return selected ? [selected] : tracks.slice(0, 1);
      }
      return tracks.filter(track => !hiddenTrackIds.value.includes(track.lf_track_id));
    });

    const totalTrackColumns = computed(() => {
      return selectedFunctions.value.reduce((total, f) => {
        return total + filteredTracks.value.length;
      }, 0);
    });

    // Same windows as Levels and tracks (levelRangeOptions), kept in its own
    // state so switching tabs does not silently re-filter the other one.
    const ltfActiveLevelRange = computed(() => {
      const options = levelRangeOptions.value;
      return options.find(r => r.value === ltfLevelRange.value) || options[0];
    });

    // Only the levels the selected discipline + track actually defines. A level
    // the ladder does not reach has no descriptors to show, so the row would be
    // six empty cells.
    const ltfLevels = computed(() => {
      const sorted = sortedframework_levels();
      const range = ltfActiveLevelRange.value;
      const f = ltfFunction.value;
      const track = ltfTrack.value;
      // A row survives if any column on screen defines that level - one track
      // when pinned to a track, any of them when pinned to a factor.
      const trackIds = isLtfByFactor.value
        ? sortedFunction_tracks().map(t => t.lf_track_id)
        : (track ? [track.lf_track_id] : []);
      const rows = sorted.filter((l, i) => {
        if (range && (i < range.lo || i > range.hi)) return false;
        if (!f) return false;
        return trackIds.some(tid =>
          !!getFunctionLevelTrackForID(f.lf_function_id, l.lf_level_id, tid));
      });
      return levelSortDesc.value ? [...rows].reverse() : rows;
    });

    // Exclusive discipline + track, with the first of each as the fallback so
    // the grid is never empty before a choice is made.
    const ltfFunction = computed(() => {
      const functions = levelling_framework.value?.functions || [];
      return functions.find(f => f.lf_function_id === ltfFunctionId.value) || functions[0] || null;
    });

    const ltfTrack = computed(() => {
      const tracks = sortedFunction_tracks();
      return tracks.find(t => t.lf_track_id === ltfTrackId.value) || tracks[0] || null;
    });

    const isLtfByFactor = computed(() => ltfPivot.value === 'factor');

    /* Describes the grid in the terms the framework itself uses, and pivots with
       it: pinned to a track the columns are factors, pinned to a factor they are
       tracks. Suppressed when there are no rows - the empty-state row below says
       it better. */
    const ltfScopeNote = computed(() => {
      const lf = levelling_framework.value;
      const count = ltfLevels.value.length;
      if (!lf || !count) return '';
      const func = ltfFunction.value;
      if (!func) return '';
      const levels = count + ' ' + (lf.level_label || 'Level').toLowerCase() + (count === 1 ? '' : 's');
      const discipline = func.name + ' ' + (lf.function_label || 'Discipline').toLowerCase();
      const trackLabel = (lf.track_label || 'Track').toLowerCase();
      const factorLabel = (lf.factor_label || 'Factor').toLowerCase();

      let note;
      if (isLtfByFactor.value) {
        const factor = ltfFactor.value;
        note = 'Showing ' + (factor ? factor.name + ' ' : '') + 'descriptors covering the '
          + levels + ' across every ' + trackLabel + ' within ' + discipline + '.';
      } else {
        const track = ltfTrack.value;
        /* "every <label>" rather than "<label>s": the label is the tenant's own
           word, and appending an s turns "Criterion" into "criterions". The
           level count has to pluralise, but level/grade/band are all regular. */
        note = 'Showing ' + (track ? track.name + ' ' + trackLabel : '') + ' descriptors covering the '
          + levels + ' across every ' + factorLabel + ' within ' + discipline + '.';
      }

      /* Name the factors left out rather than counting them: a count would have
         to pluralise the tenant's own label, and "5 criterions" is worse than
         saying which one is missing. */
      const unweighted = (lf.factors || []).filter(f => !(f.weighting > 0));
      if (unweighted.length) {
        const names = unweighted.map(f => f.name);
        const list = names.length === 1
          ? names[0]
          : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
        note += ' ' + list
          + ' records no per-level descriptor in this framework, so ' + (names.length === 1 ? 'it is' : 'they are') + ' not shown.';
      }
      return note;
    });

    const ltfEmptyMessage = computed(() => {
      const jobLevels = (levelling_framework.value?.level_track_label || 'job level').toLowerCase() + 's';
      const funcName = (ltfFunction.value || {}).name || '';
      if (isLtfByFactor.value) {
        return 'No ' + jobLevels + ' in this range for ' + funcName + '.';
      }
      return ((ltfTrack.value || {}).name || '') + ' has no ' + jobLevels
        + ' in this range for ' + funcName + '.';
    });

    /* A 0-weighted factor contributes nothing to a score, and the classification
       journey skips it outright (EvaluationHolder's classificationFactors uses
       the same `weighting > 0` test). Showing its descriptors here would offer
       copy no evaluation ever reads. `> 0` also excludes null/undefined. */
    const ltfWeightedFactors = computed(() =>
      (levelling_framework.value?.factors || []).filter(f => f.weighting > 0));

    const ltfFactor = computed(() => {
      const factors = ltfWeightedFactors.value;
      return factors.find(f => f.lf_factor_id === ltfFactorId.value) || factors[0] || null;
    });

    // Pinned track only counts while the track is the pivot; picking a factor
    // un-selects it, so the row always shows exactly one selection.
    const isLtfTrackSelected = (trackId) =>
      !isLtfByFactor.value && (ltfTrack.value || {}).lf_track_id === trackId;

    const isLtfFactorSelected = (factorId) =>
      isLtfByFactor.value && (ltfFactor.value || {}).lf_factor_id === factorId;

    const selectLtfTrack = (trackId) => {
      ltfPivot.value = 'track';
      ltfTrackId.value = trackId;
    };

    const selectLtfFactor = (factorId) => {
      ltfPivot.value = 'factor';
      ltfFactorId.value = factorId;
    };

    const ltfTrackChipTint = (track) => chipColour(track.background_colour);

    // Track-coloured, run through chipColour so it clears AA against the white
    // cell - the raw pastel would be unreadable as text.
    const ltfAbbrColour = computed(() => {
      const track = ltfTrack.value;
      return track ? chipColour(track.background_colour) : 'var(--cc-text, #1f242d)';
    });

    // The selected ladder's code for this level (e.g. S1) - sits under the level
    // number so a row can be read against the grid on the other tab.
    const ltfLevelAbbreviation = (level) => {
      const f = ltfFunction.value;
      const track = ltfTrack.value;
      // Pinned to a factor there are several tracks on screen and so no single
      // code for the row.
      if (isLtfByFactor.value || !f || !track) return '';
      const cell = getFunctionLevelTrackForID(f.lf_function_id, level.lf_level_id, track.lf_track_id);
      return cell?.abbreviation || '';
    };

    const ltfFactorTypes = computed(() => {
      const factors = ltfWeightedFactors.value;
      if (!isLtfByFactor.value) return factors;
      const one = ltfFactor.value;
      return one ? [one] : [];
    });

    // The template renders one column per group x factor, so the pivot is just a
    // matter of which of the two lists is narrowed to one.
    const ltfColumnGroups = computed(() => {
      const f = ltfFunction.value;
      if (!f) return [];
      const asGroup = (track) => ({
        lf_function_id: f.lf_function_id,
        lf_function_name: f.name,
        lf_track_id: track.lf_track_id,
        lf_track_name: track.name,
      });
      if (isLtfByFactor.value) return sortedFunction_tracks().map(asGroup);
      const track = ltfTrack.value;
      return track ? [asGroup(track)] : [];
    });

    // Sub-Factor Descriptions tab computed
    const sfdSelectedFactorIds = ref([]);

    const sfdFactorOptions = computed(() => {
      return levelling_framework.value.factors.map(f => ({ text: f.name, value: f.lf_factor_id }));
    });

    const sfdFilteredFactorTypes = computed(() => {
      if (!sfdSelectedFactorIds.value || sfdSelectedFactorIds.value.length === 0) return levelling_framework.value.factors;
      return levelling_framework.value.factors.filter(f => sfdSelectedFactorIds.value.includes(f.lf_factor_id));
    });

    const sfdGetLevel = (subFactor, levelNumber) => {
      if (!subFactor || !subFactor.sub_factor_levels) return null;
      return subFactor.sub_factor_levels.find(l => l.position === levelNumber) || null;
    };

    const openSfdEditDialog = (subFactor, factorName, levelNumber) => {
      const level = sfdGetLevel(subFactor, levelNumber);
      if (!level) return;
      sfdEditDialog.subFactorName = subFactor.name;
      sfdEditDialog.factorName = factorName;
      sfdEditDialog.originalLevel = level;
      sfdEditDialog.level = JSON.parse(JSON.stringify(level));
      sfdEditDialog.isOpen = true;
    };

    const saveSfdEditDialog = () => {
      if (sfdEditDialog.originalLevel && sfdEditDialog.level) {
        Object.assign(sfdEditDialog.originalLevel, sfdEditDialog.level);
      }
      closeSfdEditDialog();
    };

    const closeSfdEditDialog = () => {
      sfdEditDialog.isOpen = false;
      sfdEditDialog.level = null;
      sfdEditDialog.originalLevel = null;
    };

    const getFactorScores = (levelTrack) => {
      if (!levelTrack || !levelTrack.track_factors) return [];

      return levelling_framework.value.factors.map(factorType => {
        const trackFactor = levelTrack.track_factors.find(tf => tf.lf_factor_id === factorType.lf_factor_id);
        if (!trackFactor || !trackFactor.track_sub_factors || trackFactor.track_sub_factors.length === 0) {
          return { factorName: factorType.name, avgLevel: 0 };
        }

        let totalLevel = 0;
        let count = 0;

        trackFactor.track_sub_factors.forEach(tsf => {
          const subFactor = factorType.sub_factors?.find(sf => sf.lf_sub_factor_id === tsf.lf_sub_factor_id);
          if (subFactor && subFactor.sub_factor_levels) {
            const levelInfo = subFactor.sub_factor_levels.find(l => l.lf_sub_factor_level_id === tsf.lf_sub_factor_level_id);
            if (levelInfo) {
              totalLevel += levelInfo.position;
              count++;
            }
          }
        });

        return {
          factorName: factorType.name,
          avgLevel: count > 0 ? totalLevel / count : 0
        };
      });
    };

    const getFactorColor = (index) => {
      if (levelling_framework.value.factors[index]) {
        return levelling_framework.value.factors[index].background_colour || '#999999';
      }
      return '#999999';
    };

    const incompleteFactorCount = (func, level, track) => {
      const functionLevelTrack = getFunctionLevelTrack(func, level, track);
      if (!functionLevelTrack) return 0;
      let missing = 0;
      const frameworkFactors = (levelling_framework.value.factors || []).filter(x => Number(x.weighting) > 0);
      for (const factor of frameworkFactors) {
          const val = functionLevelTrack.factors.find(x => factor.lf_factor_id === x.lf_factor_id);
        if (!val.lft_factor_description) missing++;
        // for (const sf of (factor.sub_factors || [])) {
        //   const lftFactor = (levelTrack.factors || []).find(f => f.lf_factor_id === factor.lf_factor_id);
        //   const lftSf = lftFactor && (lftFactor.sub_factors || []).find(s => s.lf_sub_factor_id === sf.lf_sub_factor_id);
        //   if (!lftSf || !lftSf.lf_sub_factor_level_id) missing++;
        // }
      }
      return missing;
    };

    // A track's levels must stay contiguous, so "+ Add" is only offered on a
    // level directly adjacent to one the track already occupies - extending the
    // run at either end, or closing a gap inside it.
    //
    // Measured against the whole ladder rather than filteredLevels: with a
    // level window like 1-4 selected, a track that actually runs 5-14 reads as
    // empty inside that window, and the old check then fell through to its
    // "empty track" case and offered Add on every visible row.
    const shouldShowAddButtonForFramework = (func, level, track) => {
      if (!func || !is_editable.value) return false;

      const levels = sortedframework_levels();
      const currentLevelIndex = levels.findIndex(l => l.lf_level_id === level.lf_level_id);
      if (currentLevelIndex === -1) return false;

      const occupied = levels.map(l =>
        !!getFunctionLevelTrackForID(func.lf_function_id, l.lf_level_id, track.lf_track_id));
      if (occupied[currentLevelIndex]) return false;

      // A track with no cells anywhere has to start somewhere.
      if (!occupied.some(Boolean)) return true;

      return !!occupied[currentLevelIndex - 1] || !!occupied[currentLevelIndex + 1];
    };

    const openLftDialog = (func, level, track, mode = 'add') => {
      const lf = levelling_framework.value;
      lftDialog.mode = mode;
      lftDialog.func = func;
      lftDialog.level = level;
      lftDialog.track = track;
      let title = `${func.name} - ${lf.level_label} ${level.name} - ${track.name}`;
      if (mode === 'add') {
        const levelLabel = lf.level_label || 'Level';
        const initials = track.name.split(/\s+/).map(w => w.charAt(0)).join('');
        lftDialog.title = `Add ${title}`;
        lftDialog.levelFunctionTrack = null;
        lftDialog.form.name = track.name + ' - ' + levelLabel + ' ' + level.name;
        lftDialog.form.abbreviation = initials + level.name;
        lftDialog.form.description = '';
        lftDialog.form.evaluation_score_minimum = 0;
        lftDialog.form.evaluation_score_maximum = 0;
      } else {
        const existing = getFunctionLevelTrack(func, level, track);
        lftDialog.title = title;
        lftDialog.levelFunctionTrack = existing;
        lftDialog.form.name = existing.name || '';
        lftDialog.form.abbreviation = existing.abbreviation || '';
        lftDialog.form.description = existing.description || '';
        lftDialog.form.evaluation_score_minimum = existing.evaluation_score_minimum || 0;
        lftDialog.form.evaluation_score_maximum = existing.evaluation_score_maximum || 0;
      }
      lftDialog.isOpen = true;
    };

    const closeLftDialog = () => {
      lftDialog.isOpen = false;
      lftDialog.func = null;
      lftDialog.level = null;
      lftDialog.track = null;
      lftDialog.levelFunctionTrack = null;
    };

    const saveLftDialog = () => {
      const { func, level, track, form, mode, levelFunctionTrack } = lftDialog;
      lftDialog.saving = true;
      let data = {
          name: form.name,
          description: form.description,
          abbreviation: form.abbreviation,
          evaluation_score_minimum: form.evaluation_score_minimum,
          evaluation_score_maximum: form.evaluation_score_maximum,
        };

      if (mode === 'edit') {
        data.lf_level_function_track_id = levelFunctionTrack.lf_level_function_track_id;
        axios.post('Levelling/updateLevelFunctionTrackDetails', data).then(resp => {
          if (resp.data.Status === 'OK') {
            levelFunctionTrack.name = form.name;
            levelFunctionTrack.description = form.description;
            levelFunctionTrack.abbreviation = form.abbreviation;
            levelFunctionTrack.evaluation_score_minimum = form.evaluation_score_minimum;
            levelFunctionTrack.evaluation_score_maximum = form.evaluation_score_maximum;
            closeLftDialog();
          }
        }).finally(() => { lftDialog.saving = false; });
        return;
      }

      data.lf_level_id = level.lf_level_id;
      data.lf_function_id = func.lf_function_id;
      data.lf_track_id = track.lf_track_id;

      axios.post('Levelling/addLevelFunctionTrack', data).then(resp => {
        if (resp.data.Status === 'OK') {
          const newId = resp.data.Data?.[0]?.[0]?.lf_level_function_track_id;
          const lft = {
            lf_level_function_track_id: newId,
            lf_level_id: level.lf_level_id,
            lf_function_id: func.lf_function_id,
            lf_track_id: track.lf_track_id,
            name: form.name,
            description: form.description,
            abbreviation: form.abbreviation,
            evaluation_score_minimum: form.evaluation_score_minimum,
            evaluation_score_maximum: form.evaluation_score_maximum,
            notes: null,
            factors: levelling_framework.value.factors.map(f => ({ lf_factor_id: f.lf_factor_id, sub_factors: [] })),
          };
          levelling_framework.value.level_function_tracks.push(lft);
          closeLftDialog();
        }
      }).finally(() => { lftDialog.saving = false; });
    };

    // "47 words" / "No descriptor" - enough to see at a glance which groups are
    // still empty without opening every row.
    const factorDescriptorSummary = (factor) => {
      const text = (factor.lft_factor_description || '').trim();
      if (!text) return 'No descriptor';
      const words = text.split(/\s+/).length;
      return words + (words === 1 ? ' word' : ' words');
    };

    const isFactorExpanded = (factor) => factorsDialog.expandedFactorId === factor.lf_factor_id;

    const toggleFactorExpanded = (factor) => {
      factorsDialog.expandedFactorId = isFactorExpanded(factor) ? null : factor.lf_factor_id;
    };

    const openFactorsDialog = (func, level, track, mode = 'view') => {
      const functionLevelTrack = getFunctionLevelTrack(func, level, track);
      if (functionLevelTrack) {
        factorsDialog.isOpen = true;
        factorsDialog.lf_level_function_track_id = functionLevelTrack.lf_level_function_track_id;
        factorsDialog.levelTrackTitle = functionLevelTrack.name;
        // Which cell this is, spelled out: the dialog is reached from a grid
        // cell and the title alone does not say which one.
        const lf = levelling_framework.value;
        factorsDialog.subtitle = [
          func.name,
          track.name,
          (lf.level_label || 'Level').toLowerCase() + ' ' + level.name,
          functionLevelTrack.abbreviation,
        ].filter(Boolean).join(' \u00b7 ')
          + '. One descriptor per ' + (lf.factor_label || 'factor').toLowerCase()
          + ' group, for this ' + (lf.level_track_label || 'job level').toLowerCase() + ' only.';
        factorsDialog.expandedFactorId = null;
        const factors = JSON.parse(JSON.stringify(levelling_framework.value.factors)).map(f => {
          const val = functionLevelTrack.factors.find(x => f.lf_factor_id === x.lf_factor_id);
          f.lf_level_function_track_factor_id = val?.lf_level_function_track_factor_id;
          f.lft_factor_description = val?.lft_factor_description;
          f.lf_level_function_track_id = functionLevelTrack.lf_level_function_track_id;
          f.sub_factors.forEach(sf => {
            const valSF = val ? val.sub_factors.find(x => x.lf_sub_factor_id === sf.lf_sub_factor_id) : null;
            sf.lf_level_function_track_sub_factor_level_id = valSF?.lf_level_function_track_sub_factor_level_id;
            sf.lf_sub_factor_level_id = valSF?.lf_sub_factor_level_id;
            sf.notes = valSF?.sfl_notes;
          });
          return f;
        })
        factorsDialog.factors = factors;
      }
    };

    // "Hide Level" takes one level out on top of whichever level window is
    // selected; the window pills stay the coarse control.
    const hideLevelTrack = (framework, level) => {
      const levelId = level.lf_level_id;
      if (!hiddenLevelIds.value.includes(levelId)) {
        hiddenLevelIds.value = hiddenLevelIds.value.concat(levelId);
      }
    };

    const openTrackDialog = (framework, track, mode = 'view') => {
      trackDialog.mode = mode;
      trackDialog.track = track;
      trackDialog.form = {
        lf_track_id: track.lf_track_id,
        name: track.name,
        description: track.description || '',
        position: track.position,
        abbreviation: track.abbreviation || '',
        display_colour_id: track.display_colour_id || null,
      };
      trackDialog.colourDropdownOpen = false;
      trackDialog.isOpen = true;
    };

    const closeTrackDialog = () => {
      trackDialog.isOpen = false;
      trackDialog.track = null;
    };

    const saveTrackDialog = () => {
      if (!trackDialog.track) return;
      trackDialog.saving = true;
      axios.post('Levelling/updateTrack', { ...trackDialog.form }).then(resp => {
        if (resp.data.Status === 'OK') {
          const track = trackDialog.track;
          track.name = trackDialog.form.name;
          track.description = trackDialog.form.description;
          track.position = trackDialog.form.position;
          track.abbreviation = trackDialog.form.abbreviation;
          track.display_colour_id = trackDialog.form.display_colour_id;
          if (trackDialog.form.display_colour_id) {
            const dc = (levelling_framework.value.display_colours || []).find(c => c.display_colour_id === trackDialog.form.display_colour_id);
            if (dc) {
              track.background_colour = dc.background_colour;
              track.text_colour = dc.text_colour;
            }
          } else {
            track.background_colour = null;
            track.text_colour = null;
          }
          closeTrackDialog();
        }
      }).finally(() => { trackDialog.saving = false; });
    };

    // --- Grid inline cell editing (Name / Abbreviation) ---
    const gridInlineEdit = reactive({
      field: null,
      entity: null,
      id: null,
      value: '',
      saving: false,
    });

    const startGridInlineEdit = (entity, field, instance, key) => {
      if (!is_editable.value) return;
      if (!key) key = entity + "_id";
      cancelGridInlineEdit();
      gridInlineEdit.field = field;
      gridInlineEdit.entity = entity;
      gridInlineEdit.id = instance[key];
      gridInlineEdit.value = instance[field] || '';
    };

    const saveGridInlineEdit = (instance, derived) => {
      const field = gridInlineEdit.field;
      const id = gridInlineEdit.id;
      const newValue = gridInlineEdit.value.trim();
      const oldValue = instance[field] || '';
      gridInlineEdit.field = null;
      gridInlineEdit.id = null;
      if (newValue === oldValue) return;
      instance[field] = newValue;
      gridInlineEdit.saving = true;
      if (gridInlineEdit.entity === "lf_level_function_track")
        axios.post('Levelling/updateLevelFunctionTrack', {
          lf_level_function_track_id: id,
          field: field,
          value: newValue,
        }).finally(() => {
          gridInlineEdit.saving = false;
          if (derived) 
            getFunctionLevelTrackForID(instance.lf_function_id, instance.lf_level_id, instance.lf_track_id)[field] = newValue;
        });
      else if (gridInlineEdit.entity === "lf_level_function_track_factor")
        axios.post('Levelling/updateLevelFunctionTrackFactor', {
          lf_level_function_track_id: instance.lf_level_function_track_id,
          lf_factor_id: id,
          description: newValue,
        }).finally(() => {
          gridInlineEdit.saving = false;
        });
    };

    const cancelGridInlineEdit = () => {
      gridInlineEdit.field = null;
      gridInlineEdit.entity = null;
      gridInlineEdit.id = null;
      gridInlineEdit.value = '';
    };

    const isGridInlineEditing = (entity, field, instance, key) => {
      if (!key) key = entity + "_id";
      return gridInlineEdit.field === field
        && gridInlineEdit.entity === entity
        && gridInlineEdit.id === instance[key];
    };

    // Same action as switching the track's chip off in the filter bar. While
    // comparing, one track must stay on, so "hide" advances to the next one.
    const hideTrack = (framework, track) => {
      const trackId = track.lf_track_id;
      if (isComparing.value) {
        const tracks = sortedFunction_tracks();
        const i = tracks.findIndex(t => t.lf_track_id === trackId);
        const next = tracks[(i + 1) % tracks.length];
        if (next) comparisonTrackId.value = next.lf_track_id;
        return;
      }
      if (!hiddenTrackIds.value.includes(trackId)) {
        hiddenTrackIds.value = hiddenTrackIds.value.concat(trackId);
      }
    };

    const closeFactorsDialog = () => {
      factorsDialog.isOpen = false;
      factorsDialog.lf_level_function_track_id = null;
      factorsDialog.levelTrackTitle = '';
      factorsDialog.subtitle = '';
      factorsDialog.expandedFactorId = null;
      factorsDialog.factors = [];
    };

    const saveFactorsDialog = () => {
      const subFactorLevels = [];
      factorsDialog.factors.forEach(factor => {
        (factor.sub_factors || []).forEach(sf => {
          if (sf.lf_sub_factor_level_id) {
            subFactorLevels.push({
              lf_sub_factor_id: sf.lf_sub_factor_id,
              lf_sub_factor_level_id: sf.lf_sub_factor_level_id,
              lf_factor_id: factor.lf_factor_id,
            });
          }
        });
      });
      factorsDialog.saving = true;
      axios.post('Levelling/updateLevelFunctionTrackFactors', {
        lf_level_function_track_id: factorsDialog.lf_level_function_track_id,
        sub_factor_levels: subFactorLevels,
      }).then(resp => {
        if (resp.data.Status === 'OK') {
          const orig = getFunctionLevelTrackForKey(factorsDialog.lf_level_function_track_id);
          subFactorLevels.forEach(sfl => {
            const fac = orig.factors.find(x => x.lf_factor_id === sfl.lf_factor_id);
            if (fac) {
              const subfac = fac.sub_factors.find(x => x.lf_sub_factor_id === sfl.lf_sub_factor_id);
              if (subfac)
                subfac.lf_sub_factor_level_id = sfl.lf_sub_factor_level_id;
              else 
                fac.sub_factors.push({
                  lf_level_function_track_sub_factor_level_id: null,
                  lf_sub_factor_id: sfl.lf_sub_factor_id,
                  lf_sub_factor_level_id: sfl.lf_sub_factor_level_id,
                  notes: null,
                });
            }
          });
          closeFactorsDialog();
        }
      }).finally(() => { factorsDialog.saving = false; });
    };

    const cancelFactorsDialog = () => {
      closeFactorsDialog();
    };

    const closeFactorsDialogWithoutSaving = () => {
      closeFactorsDialog();
    };

    const handleDialogClose = () => {
      closeFactorsDialog();
    };

    const handleDialogOutsideClick = () => {
      closeFactorsDialog();
    };

    const getFactorName = (factorTypeId) => {
      const factor = levelling_framework.value.factors.find(f => f.lf_factor_id === factorTypeId);
      return factor ? factor.name : `Factor ${factorTypeId}`;
    };

    const openLtfCellPopup = (event, group, level, factor) => {
      const cell = event.currentTarget;
      const section = cell.closest('.function-section');
      const sectionRect = section.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      const functionLevelTrack = getGroupLevel(group, level);
      const trackFactor = functionLevelTrack?.factors.find(tf => tf.lf_factor_id === factor.lf_factor_id) || null;
      if (!trackFactor) return;
      trackFactor.lf_level_function_track_id = functionLevelTrack.lf_level_function_track_id;
      Object.assign(ltfCellPopup, {
        isOpen: true,
        text: trackFactor?.lft_factor_description || '',
        factorName: factor.name,
        levelPosition: level.position,
        groupLabel: group.lf_function_name + ' \u2014 ' + group.lf_track_name,
        top: cellRect.top - sectionRect.top,
        left: cellRect.left - sectionRect.left,
        trackFactor,
      });
    };

    const closeLtfCellPopup = () => {
      cancelGridInlineEdit();
      ltfCellPopup.isOpen = false;
    };

    const getLtfSubFactorLevelNames = (group, level, factorType) => {
      const functionLevelTrack = getGroupLevel(group, level);
      if (!functionLevelTrack) return [];
      const trackFactor = functionLevelTrack.factors.find(tf => tf.lf_factor_id === factorType.lf_factor_id);
      if (!trackFactor || !trackFactor.sub_factors) return [];
      return trackFactor.sub_factors.map(tsf => {
        const subFactor = factorType.sub_factors?.find(sf => sf.lf_sub_factor_id === tsf.lf_sub_factor_id);
        if (!subFactor) return null;
        const levelInfo = subFactor.sub_factor_levels?.find(l => l.lf_sub_factor_level_id === tsf.lf_sub_factor_level_id);
        return levelInfo ? levelInfo.name : null;
      }).filter(Boolean);
    };

    const getLtfDescription = (group, level, factorType) => {
      const functionLevelTrack = getGroupLevel(group, level);
      if (!functionLevelTrack) return "";
      const trackFactor = functionLevelTrack.factors.find(tf => tf.lf_factor_id === factorType.lf_factor_id);
      return trackFactor?.lft_factor_description;
      // if (!trackFactor || !trackFactor.sub_factors) return [];
      // return trackFactor.sub_factors.map(tsf => {
      //   const subFactor = factorType.sub_factors?.find(sf => sf.lf_sub_factor_id === tsf.lf_sub_factor_id);
      //   if (!subFactor) return null;
      //   const levelInfo = subFactor.sub_factor_levels?.find(l => l.lf_sub_factor_level_id === tsf.lf_sub_factor_level_id);
      //   return levelInfo ? levelInfo.name : null;
      // }).filter(Boolean);
    };

    const getLevelTrackFactor = (templateFactor, lf_sub_factor_id) => {
      if (!factorsDialog.factors) {
        return { track_factor_id: -1, lf_factor_id: templateFactor.lf_factor_id, sub_factors: [] };
      }

      let factor = factorsDialog.factors.find(f => f.lf_factor_id === templateFactor.lf_factor_id);

      if (!factor) {
        factor = { track_factor_id: -1, lf_factor_id: templateFactor.lf_factor_id, sub_factors: [] };
        factorsDialog.factors.push(factor);
      }

      if (!factor.sub_factors) {
        factor.sub_factors = [];
      }
      let tsf = factor.sub_factors.find(x => x.lf_sub_factor_id === lf_sub_factor_id);
      if (!tsf) {
        tsf = { lf_level_function_track_sub_factor_level_id : -1, lf_sub_factor_id: lf_sub_factor_id, lf_sub_factor_level_id: null};
        factor.sub_factors.push(tsf);
      }
      return tsf;
    };

    const getSubFactorLevelName = (templateFactor, subFactor, levelId) => {
      return templateFactor.sub_factors.find(x => x.lf_sub_factor_id === subFactor.lf_sub_factor_id)
        .sub_factor_levels.find(x => x.lf_sub_factor_level_id === levelId).name;
    };

    // --- Administration tab functions ---

    const fetchFrameworkList = () => {
      axios.get('Levelling/frameworkList').then(resp => {
        if (resp.data.Data) {
          adminFrameworkList.value = resp.data.Data;
        }
      });
    };

    const switchFramework = (id) => {
      adminSaving.value = true;
      axios.get(`Levelling/frameworkDetails/${id}`).then(resp => {
        if (resp.data.Data) {
          populateFramework(resp.data.Data);
          fetchFrameworkList();
        }
      }).finally(() => { adminSaving.value = false; });
    };

    const openAdminFrameworkDialog = (mode) => {
      adminFrameworkDialog.mode = mode;
      if (mode === 'edit' && levelling_framework.value) {
        const lf = levelling_framework.value;
        adminFrameworkDialog.form = {
          levelling_framework_id: lf.levelling_framework_id,
          name: lf.name || '',
          description: lf.description || '',
          level_label: lf.level_label || 'Level',
          function_label: lf.function_label || 'Function',
          track_label: lf.track_label || 'Track',
          level_track_label: lf.level_track_label || 'Job Level',
          factor_label: lf.factor_label || 'Factor',
          sub_factor_label: lf.sub_factor_label || 'Sub-Factor',
          is_client_default: lf.is_client_default || 0,
        };
      } else {
        adminFrameworkDialog.form = {
          name: '',
          description: '',
          level_label: 'Level',
          function_label: 'Function',
          track_label: 'Track',
          level_track_label: 'Job Level',
          factor_label: 'Factor',
          sub_factor_label: 'Sub-Factor',
        };
      }
      adminFrameworkDialog.isOpen = true;
    };

    const closeAdminFrameworkDialog = () => {
      adminFrameworkDialog.isOpen = false;
    };

    const saveAdminFrameworkDialog = () => {
      adminSaving.value = true;
      const endpoint = adminFrameworkDialog.mode === 'add' ? 'Levelling/createFramework' : 'Levelling/updateFramework';
      axios.post(endpoint, adminFrameworkDialog.form).then(resp => {
        if (resp.data.Status === 'OK') {
          if (adminFrameworkDialog.mode === 'add' && resp.data.Data?.[0]?.[0]?.levelling_framework_id) {
            switchFramework(resp.data.Data[0][0].levelling_framework_id);
          } else if (adminFrameworkDialog.mode === 'edit') {
            const lf = levelling_framework.value;
            Object.assign(lf, {
              name: adminFrameworkDialog.form.name,
              description: adminFrameworkDialog.form.description,
              level_label: adminFrameworkDialog.form.level_label,
              function_label: adminFrameworkDialog.form.function_label,
              track_label: adminFrameworkDialog.form.track_label,
              level_track_label: adminFrameworkDialog.form.level_track_label,
              factor_label: adminFrameworkDialog.form.factor_label,
              sub_factor_label: adminFrameworkDialog.form.sub_factor_label,
              is_client_default: adminFrameworkDialog.form.is_client_default,
            });
            fetchFrameworkList();
          }
          closeAdminFrameworkDialog();
        }
      }).finally(() => { adminSaving.value = false; });
    };

    const deleteFramework = () => {
      if (!levelling_framework.value) return;
      adminSaving.value = true;
      axios.post('Levelling/deleteFramework', {
        levelling_framework_id: levelling_framework.value.levelling_framework_id,
      }).then(resp => {
        if (resp.data.Status === 'OK') {
          // Reload the default framework
          axios.get('Levelling/frameworkDefault').then(r => {
            if (r.data.Data) {
              populateFramework(r.data.Data);
              fetchFrameworkList();
            }
          });
        }
      }).finally(() => { adminSaving.value = false; });
    };

    const adminEntityFields = {
      level: { idField: 'lf_level_id', collection: 'levels', endpoint: 'Level', fields: ['name', 'description', 'position', 'level_group'] },
      function: { idField: 'lf_function_id', collection: 'functions', endpoint: 'Function', fields: ['name', 'description', 'position', 'is_core'] },
      track: { idField: 'lf_track_id', collection: 'tracks', endpoint: 'Track', fields: ['name', 'description', 'position', 'abbreviation', 'display_colour_id'] },
      factor: { idField: 'lf_factor_id', collection: 'factors', endpoint: 'Factor', fields: ['name', 'description', 'position', 'weighting', 'display_colour_id', 'curve_factor'] },
    };

    const openAdminEntityDialog = (entityType, mode, item) => {
      if (!is_editable.value) return;
      clearSubFactorEdit();
      const config = adminEntityFields[entityType];
      const lf = levelling_framework.value;
      const label = lf[entityType + '_label'] || entityType;
      adminEntityDialog.entityType = entityType;
      adminEntityDialog.entityLabel = label;
      adminEntityDialog.mode = mode;
      adminEntityDialog.original = item || null;
      if (mode === 'edit' && item) {
        const form = {};
        form[config.idField] = item[config.idField];
        config.fields.forEach(f => { form[f] = item[f] ?? ''; });
        adminEntityDialog.form = form;
      } else {
        const form = {};
        config.fields.forEach(f => { form[f] = ''; });
        const collection = lf[config.collection] || [];
        form.position = collection.length + 1;
        if (entityType === 'function') form.is_core = 0;
        if (entityType === 'factor') form.weighting = 1;
        if (entityType === 'factor') form.curve_factor = 1;
        adminEntityDialog.form = form;
      }
      adminEntityDialog.colourDropdownOpen = false;
      adminEntityDialog.errors = {};
      adminEntityDialog.isOpen = true;
    };

    const closeAdminEntityDialog = () => {
      adminEntityDialog.isOpen = false;
      adminEntityDialog.original = null;
    };

    const saveAdminEntityDialog = () => {
      const entityType = adminEntityDialog.entityType;
      const config = adminEntityFields[entityType];
      const errors = {};
      if (adminEntityDialog.form.position === '' || adminEntityDialog.form.position === null || adminEntityDialog.form.position === undefined) {
        errors.position = 'Position is required';
      }
      if ((entityType === 'track' || entityType === 'factor') && !adminEntityDialog.form.display_colour_id) {
        errors.display_colour_id = 'Colour is required';
      }
      adminEntityDialog.errors = errors;
      if (Object.keys(errors).length > 0) return;

      const mode = adminEntityDialog.mode;
      const endpoint = mode === 'add'
        ? `Levelling/add${config.endpoint}`
        : `Levelling/update${config.endpoint}`;
      const payload = { ...adminEntityDialog.form };
      if (mode === 'add') {
        payload.levelling_framework_id = levelling_framework.value.levelling_framework_id;
      }
      adminSaving.value = true;
      axios.post(endpoint, payload).then(resp => {
        if (resp.data.Status === 'OK') {
          const collection = levelling_framework.value[config.collection];
          let doSort = false;
          if (mode === 'add') {
            const newItem = { ...payload };
            doSort = true;
            if (resp.data.Data?.[0]?.[0]) {
              newItem[config.idField] = resp.data.Data[0][0][config.idField];
            }
            // For tracks, resolve display colour
            if (entityType === 'track' && newItem.display_colour_id) {
              const dc = (levelling_framework.value.display_colours || []).find(c => c.display_colour_id === newItem.display_colour_id);
              if (dc) {
                newItem.background_colour = dc.background_colour;
                newItem.text_colour = dc.text_colour;
              }
            }
            if (entityType === 'factor') {
              newItem.sub_factors = [];
              if (newItem.display_colour_id) {
                const dc = (levelling_framework.value.display_colours || []).find(c => c.display_colour_id === newItem.display_colour_id);
                if (dc) {
                  newItem.background_colour = dc.background_colour;
                  newItem.text_colour = dc.text_colour;
                }
              }
            }
            collection.push(newItem);
          } else {
            const item = adminEntityDialog.original;
            if (item) {
              config.fields.forEach(f => {
                if (f === "position" && item[f] !== adminEntityDialog.form[f]) doSort = true;
                item[f] = adminEntityDialog.form[f]; 
              });
              if ((entityType === 'track' || entityType === 'factor') && adminEntityDialog.form.display_colour_id) {
                const dc = (levelling_framework.value.display_colours || []).find(c => c.display_colour_id === adminEntityDialog.form.display_colour_id);
                if (dc) {
                  item.background_colour = dc.background_colour;
                  item.text_colour = dc.text_colour;
                }
              }
            }
          }
          if (doSort) {
            collection.sort((a,b) => a.position > b.position ? 1 : a.position < b.position ? -1 : 0);
          }
          closeAdminEntityDialog();
        }
      }).finally(() => { adminSaving.value = false; });
    };

    const openAdminDeleteConfirm = (entityType, item) => {
      const lf = levelling_framework.value;
      adminDeleteConfirm.entityType = entityType;
      adminDeleteConfirm.entityLabel = lf[entityType + '_label'] || entityType;
      adminDeleteConfirm.item = item;
      adminDeleteConfirm.relatedLabel = entityType === "factor" ? "level/track factor descriptors" : "level/track combinations";
      adminDeleteConfirm.isOpen = true;
    };

    const closeAdminDeleteConfirm = () => {
      adminDeleteConfirm.isOpen = false;
      adminDeleteConfirm.item = null;
    };

    const confirmAdminDelete = () => {
      const entityType = adminDeleteConfirm.entityType;
      const config = adminEntityFields[entityType];
      const item = adminDeleteConfirm.item;
      if (!item) return;
      adminSaving.value = true;
      const payload = {};
      payload[config.idField] = item[config.idField];
      axios.post(`Levelling/delete${config.endpoint}`, payload).then(resp => {
        if (resp.data.Status === 'OK') {
          const collection = levelling_framework.value[config.collection];
          const idx = collection.findIndex(x => x[config.idField] === item[config.idField]);
          if (idx !== -1) collection.splice(idx, 1);
          closeAdminDeleteConfirm();
        }
      }).finally(() => { adminSaving.value = false; });
    };

    const openCopyFunctionDialog = (item) => {
      adminCopyDialog.item = item;
      adminCopyDialog.form.name = item.name + ' (Copy)';
      adminCopyDialog.form.description = item.description || '';
      adminCopyDialog.isOpen = true;
    };

    const confirmCopyFunction = () => {
      const item = adminCopyDialog.item;
      if (!item) return;
      adminSaving.value = true;
      axios.post('Levelling/copyFunction', {
        lf_function_id: item.lf_function_id,
        name: adminCopyDialog.form.name,
        description: adminCopyDialog.form.description,
      }).then(resp => {
        if (resp.data.Status === 'OK') {
          adminCopyDialog.isOpen = false;
          fetchData();
        }
      }).finally(() => { adminSaving.value = false; });
    };

    const selectImportFramework = (fw) => {
      importDialog.selectedFrameworkId = fw.levelling_framework_id;
      importDialog.form.name = fw.name;
      importDialog.form.description = fw.description || '';
    };

    const openImportFrameworkDialog = () => {
      importDialog.selectedFrameworkId = null;
      importDialog.form.name = '';
      importDialog.form.description = '';
      importDialog.clients = [];
      importDialog.loading = true;
      importDialog.isOpen = true;
      axios.get('Levelling/frameworkListAll').then(resp => {
        if (resp.data.Status === 'OK') {
          const grouped = {};
          (resp.data.Data || []).forEach(row => {
            if (!grouped[row.client_id]) {
              grouped[row.client_id] = { client_id: row.client_id, client_name: row.client_name, frameworks: [] };
            }
            grouped[row.client_id].frameworks.push({
              levelling_framework_id: row.levelling_framework_id,
              name: row.name,
              description: row.description,
            });
          });
          importDialog.clients = Object.values(grouped);
        }
      }).finally(() => { importDialog.loading = false; });
    };

    const confirmImportFramework = () => {
      if (!importDialog.selectedFrameworkId) return;
      importDialog.importing = true;
      adminSaving.value = true;
      axios.post('Levelling/importFramework', {
        source_levelling_framework_id: importDialog.selectedFrameworkId,
        name: importDialog.form.name,
        description: importDialog.form.description,
      }).then(resp => {
        if (resp.data.Status === 'OK') {
          importDialog.isOpen = false;
          fetchData();
          if (hasAdminPermission.value) fetchFrameworkList();
        }
      }).finally(() => { importDialog.importing = false; adminSaving.value = false; });
    };

    const openPublishDialog = () => {
      publishDialog.frameworks = [];
      publishDialog.loading = true;
      publishDialog.publishing = false;
      publishDialog.isOpen = true;
      axios.get(`Levelling/publishTargets/${levelling_framework.value.levelling_framework_id}`).then(resp => {
        if (resp.data.Status === 'OK') {
          publishDialog.frameworks = (resp.data.Data || []).map(fw => ({
            levelling_framework_id: fw.levelling_framework_id,
            name: fw.name,
            entities: {
              levels: { count: fw.level_count, copied: fw.level_count_copied, selected: fw.level_count_copied > 0 },
              functions: { count: fw.function_count, copied: fw.function_count_copied, selected: fw.function_count_copied > 0 },
              tracks: { count: fw.track_count, copied: fw.track_count_copied, selected: fw.track_count_copied > 0 },
              factors: { count: fw.factor_count, copied: fw.factor_count_copied, selected: fw.factor_count_copied > 0 },
              lfts: { count: fw.lft_count, copied: fw.lft_count_copied, selected: fw.lft_count_copied > 0 },
              lftfs: { count: fw.lftf_count, copied: fw.lftf_count_copied, selected: fw.lftf_count_copied > 0 },
            },
          }));
        }
      }).finally(() => { publishDialog.loading = false; });
    };

    const confirmPublish = () => {
      const targets = publishDialog.frameworks
        .filter(fw => Object.values(fw.entities).some(e => e.selected))
        .map(fw => ({
          levelling_framework_id: fw.levelling_framework_id,
          entities: Object.entries(fw.entities)
            .filter(([, e]) => e.selected)
            .map(([key]) => key),
        }));
      if (!targets.length) return;
      publishDialog.publishing = true;
      axios.post('Levelling/publishFramework', {
        source_levelling_framework_id: levelling_framework.value.levelling_framework_id,
        targets,
      }).then(resp => {
        if (resp.data.Status === 'OK') {
          publishDialog.isOpen = false;
        }
      }).finally(() => { publishDialog.publishing = false; });
    };

    const deleteLevelFunctionTrack = (lft) => {
      if (!lft?.lf_level_function_track_id) return;
      if (!confirm('Are you sure you want to delete this level track and its associated factor assignments?')) return;
      axios.post('Levelling/deleteLevelFunctionTrack', {
        lf_level_function_track_id: lft.lf_level_function_track_id,
      }).then(resp => {
        if (resp.data.Status === 'OK') {
          const lfts = levelling_framework.value.level_function_tracks;
          const idx = lfts.findIndex(x => x.lf_level_function_track_id === lft.lf_level_function_track_id);
          if (idx !== -1) lfts.splice(idx, 1);
        }
      });
    };

    // --- Sub-Factor & Sub-Factor Level management (inside factor edit) ---

    const subFactorExpanded = ref([]);
    const subFactorEditing = reactive({});
    const subFactorLevelEditing = reactive({});
    const subFactorSaving = ref(false);
    const subFactorErrors = ref({});
    const subFactorLevelErrors = ref({});

    const addSubFactor = (factor) => {
      if (!factor) return;
      const sfs = factor.sub_factors || [];
      const tempId = '_new_sf_' + Date.now();
      const newSf = {
        lf_sub_factor_id: tempId,
        _isNew: true,
        _parentFactor: factor,
        name: '',
        description: '',
        position: sfs.length + 1,
        weighting: 1,
        sub_factor_levels: [],
      };
      if (!factor.sub_factors) set(factor, 'sub_factors', []);
      factor.sub_factors.push(newSf);
      subFactorErrors.value = {};
      set(subFactorEditing, tempId, { name: newSf.name, description: newSf.description, position: newSf.position, weighting: newSf.weighting });
      set(subFactorEditing, '_active', tempId);
      set(subFactorEditing, '_activeObj', newSf);
    };

    const clearSubFactorLevelEdit = () => {
      if (subFactorLevelEditing._active) {
        delete subFactorLevelEditing[subFactorLevelEditing._active];
      }
      set(subFactorLevelEditing, '_active', null);
      set(subFactorLevelEditing, '_activeObj', null);
    };

    const clearSubFactorEdit = () => {
      if (subFactorEditing._active) {
        delete subFactorEditing[subFactorEditing._active];
      }
      set(subFactorEditing, '_active', null);
      set(subFactorEditing, '_activeObj', null);
    };

    const startEditSubFactor = (sf) => {
      // Clear any previous sub-factor edit
      if (subFactorEditing._active && subFactorEditing._active !== sf.lf_sub_factor_id) {
        delete subFactorEditing[subFactorEditing._active];
      }
      // Close factor edit panel and sub-factor level edit
      adminEntityDialog.isOpen = false;
      adminEntityDialog.original = null;
      clearSubFactorLevelEdit();
      subFactorErrors.value = {};
      set(subFactorEditing, sf.lf_sub_factor_id, {
        name: sf.name,
        description: sf.description,
        position: sf.position,
        weighting: sf.weighting,
      });
      set(subFactorEditing, '_active', sf.lf_sub_factor_id);
      set(subFactorEditing, '_activeObj', sf);
    };

    const cancelEditSubFactor = (sf) => {
      delete subFactorEditing[sf.lf_sub_factor_id];
      if (subFactorEditing._active === sf.lf_sub_factor_id) {
        set(subFactorEditing, '_active', null);
        set(subFactorEditing, '_activeObj', null);
      }
      if (sf._isNew) {
        const factor = sf._parentFactor;
        if (factor?.sub_factors) {
          const idx = factor.sub_factors.indexOf(sf);
          if (idx !== -1) factor.sub_factors.splice(idx, 1);
        }
      }
    };

    const saveSubFactor = (sf) => {
      const edit = subFactorEditing[sf.lf_sub_factor_id];
      if (!edit) return;
      const errors = {};
      if (edit.position === '' || edit.position === null || edit.position === undefined) {
        errors.position = 'Position is required';
      }
      subFactorErrors.value = errors;
      if (Object.keys(errors).length > 0) return;
      subFactorSaving.value = true;
      if (sf._isNew) {
        const factor = sf._parentFactor;
        axios.post('Levelling/addSubFactor', {
          lf_factor_id: factor.lf_factor_id,
          name: edit.name,
          description: edit.description,
          position: edit.position,
          weighting: edit.weighting,
        }).then(resp => {
          if (resp.data.Status === 'OK') {
            const newId = resp.data.Data?.[0]?.[0]?.lf_sub_factor_id;
            delete subFactorEditing[sf.lf_sub_factor_id];
            sf.lf_sub_factor_id = newId;
            delete sf._isNew;
            delete sf._parentFactor;
            sf.name = edit.name;
            sf.description = edit.description;
            sf.position = edit.position;
            sf.weighting = edit.weighting;
            set(subFactorEditing, '_active', null);
            set(subFactorEditing, '_activeObj', null);
          }
        }).finally(() => { subFactorSaving.value = false; });
      } else {
        axios.post('Levelling/updateSubFactor', {
          lf_sub_factor_id: sf.lf_sub_factor_id,
          name: edit.name,
          description: edit.description,
          position: edit.position,
          weighting: edit.weighting,
        }).then(resp => {
          if (resp.data.Status === 'OK') {
            sf.name = edit.name;
            sf.description = edit.description;
            sf.position = edit.position;
            sf.weighting = edit.weighting;
            delete subFactorEditing[sf.lf_sub_factor_id];
            set(subFactorEditing, '_active', null);
            set(subFactorEditing, '_activeObj', null);
          }
        }).finally(() => { subFactorSaving.value = false; });
      }
    };

    const deleteSubFactor = (sf, factor) => {
      if (!factor) return;
      subFactorSaving.value = true;
      axios.post('Levelling/deleteSubFactor', {
        lf_sub_factor_id: sf.lf_sub_factor_id,
      }).then(resp => {
        if (resp.data.Status === 'OK') {
          const links = Number(resp.data.Data[0][0].eval_links_count);
            if (links > 0) {
              errorPopup.message = `This sub-factor could not be deleted as it is currently referenced in ${links} evaluation${links > 1 ? 's' : ''}.`;
              errorPopup.isOpen = true;
              subFactorSaving.value = false;
              return;
            }
          const idx = factor.sub_factors.findIndex(x => x.lf_sub_factor_id === sf.lf_sub_factor_id);
          if (idx !== -1) factor.sub_factors.splice(idx, 1);
        }
      }).finally(() => { subFactorSaving.value = false; });
    };

    const addSubFactorLevel = (sf) => {
      const sfls = sf.sub_factor_levels || [];
      const tempId = '_new_sfl_' + Date.now();
      const newSfl = {
        lf_sub_factor_level_id: tempId,
        _isNew: true,
        _parentSubFactor: sf,
        name: '',
        description: '',
        position: sfls.length + 1,
        weighting: 1,
        scope: '',
        score: 0,
      };
      if (!sf.sub_factor_levels) set(sf, 'sub_factor_levels', []);
      sf.sub_factor_levels.push(newSfl);
      subFactorLevelErrors.value = {};
      set(subFactorLevelEditing, tempId, { name: newSfl.name, description: newSfl.description, position: newSfl.position, weighting: newSfl.weighting, scope: newSfl.scope, score: newSfl.score });
      set(subFactorLevelEditing, '_active', tempId);
      set(subFactorLevelEditing, '_activeObj', newSfl);
    };

    const startEditSubFactorLevel = (sfl) => {
      // Clear any previous sub-factor level edit
      if (subFactorLevelEditing._active && subFactorLevelEditing._active !== sfl.lf_sub_factor_level_id) {
        delete subFactorLevelEditing[subFactorLevelEditing._active];
      }
      subFactorLevelErrors.value = {};
      set(subFactorLevelEditing, sfl.lf_sub_factor_level_id, {
        name: sfl.name,
        description: sfl.description,
        position: sfl.position,
        weighting: sfl.weighting,
        scope: sfl.scope,
        score: sfl.score,
      });
      set(subFactorLevelEditing, '_active', sfl.lf_sub_factor_level_id);
      set(subFactorLevelEditing, '_activeObj', sfl);
    };

    const cancelEditSubFactorLevel = (sfl) => {
      delete subFactorLevelEditing[sfl.lf_sub_factor_level_id];
      if (subFactorLevelEditing._active === sfl.lf_sub_factor_level_id) {
        set(subFactorLevelEditing, '_active', null);
        set(subFactorLevelEditing, '_activeObj', null);
      }
      if (sfl._isNew) {
        const sf = sfl._parentSubFactor;
        if (sf?.sub_factor_levels) {
          const idx = sf.sub_factor_levels.indexOf(sfl);
          if (idx !== -1) sf.sub_factor_levels.splice(idx, 1);
        }
      }
    };

    const saveSubFactorLevel = (sfl) => {
      const edit = subFactorLevelEditing[sfl.lf_sub_factor_level_id];
      if (!edit) return;
      const errors = {};
      if (edit.position === '' || edit.position === null || edit.position === undefined) {
        errors.position = 'Position is required';
      }
      subFactorLevelErrors.value = errors;
      if (Object.keys(errors).length > 0) return;
      subFactorSaving.value = true;
      if (sfl._isNew) {
        const sf = sfl._parentSubFactor;
        axios.post('Levelling/addSubFactorLevel', {
          lf_sub_factor_id: sf.lf_sub_factor_id,
          name: edit.name,
          description: edit.description,
          position: edit.position,
          weighting: edit.weighting,
          scope: edit.scope,
          score: edit.score,
        }).then(resp => {
          if (resp.data.Status === 'OK') {
            const newId = resp.data.Data?.[0]?.[0]?.lf_sub_factor_level_id;
            delete subFactorLevelEditing[sfl.lf_sub_factor_level_id];
            sfl.lf_sub_factor_level_id = newId;
            delete sfl._isNew;
            delete sfl._parentSubFactor;
            sfl.name = edit.name;
            sfl.description = edit.description;
            sfl.position = edit.position;
            sfl.weighting = edit.weighting;
            sfl.scope = edit.scope;
            sfl.score = edit.score;
            set(subFactorLevelEditing, '_active', null);
            set(subFactorLevelEditing, '_activeObj', null);
          }
        }).finally(() => { subFactorSaving.value = false; });
      } else {
        axios.post('Levelling/updateSubFactorLevel', {
          lf_sub_factor_level_id: sfl.lf_sub_factor_level_id,
          name: edit.name,
          description: edit.description,
          position: edit.position,
          weighting: edit.weighting,
          scope: edit.scope,
          score: edit.score,
        }).then(resp => {
          if (resp.data.Status === 'OK') {
            sfl.name = edit.name;
            sfl.description = edit.description;
            sfl.position = edit.position;
            sfl.weighting = edit.weighting;
            sfl.scope = edit.scope;
            sfl.score = edit.score;
            delete subFactorLevelEditing[sfl.lf_sub_factor_level_id];
            set(subFactorLevelEditing, '_active', null);
            set(subFactorLevelEditing, '_activeObj', null);
          }
        }).finally(() => { subFactorSaving.value = false; });
      }
    };

    const deleteSubFactorLevel = (sf, sfl) => {
      subFactorSaving.value = true;
      axios.post('Levelling/deleteSubFactorLevel', {
        lf_sub_factor_level_id: sfl.lf_sub_factor_level_id,
      }).then(resp => {
        if (resp.data.Status === 'OK') {
          const idx = sf.sub_factor_levels.findIndex(x => x.lf_sub_factor_level_id === sfl.lf_sub_factor_level_id);
          if (idx !== -1) sf.sub_factor_levels.splice(idx, 1);
        }
      }).finally(() => { subFactorSaving.value = false; });
    };

    const populateFramework = (data) => {
      let min_subFactorLevel = 10000;
      let max_subFactorLevel = 0;
      const lf = data.levelling_framework;
      if (!lf) return;
      canEditFramework.value = Number(lf.editable) > 0 && hasAdminPermission.value;
      editMode.value = false;   // a freshly loaded framework always opens read-only
      lf.factors = data.factors.map(f => ({
          lf_factor_id: f.lf_factor_id,
          name: f.name,
          description: f.description,
          position: f.position,
          weighting: f.weighting,
          curve_factor: f.curve_factor,
          display_colour_id: f.display_colour_id,
          background_colour: f.background_colour,
          text_colour: f.text_colour,
          sub_factors: (f.sub_factors || []).map(sf => ({
            lf_sub_factor_id: sf.lf_sub_factor_id,
            name: sf.name,
            description: sf.description,
            position: sf.position,
            weighting: sf.weighting,
            sub_factor_levels: (sf.sub_factor_levels || [])
              .filter(sl => sl.lf_sub_factor_level_id != null)
              .map(sl => {
                const pos = sl.position;
                min_subFactorLevel = pos < min_subFactorLevel ? pos : min_subFactorLevel;
                max_subFactorLevel = pos > max_subFactorLevel ? pos : max_subFactorLevel;
                return sl;
              })
          }))
        }));
      const posSort = (rows) => rows.sort((a, b) => a.position - b.position);
      lf.functions = posSort(data.functions);
      lf.levels = posSort(data.levels);
      lf.tracks = posSort(data.tracks);
      lf.level_function_tracks = data.level_function_tracks;
      lf.display_colours = data.display_colours || [];
      // Object.assign(levelling_framework, lf);
      levelling_framework.value = lf;

      for (let i = min_subFactorLevel; i <= max_subFactorLevel; i++) {
        subFactorLevels.value.push(i);
      }
      const defaultFunction = data.functions.find(x => x.is_core) || lf.functions[0];
      if (defaultFunction) {
        ltfFunctionId.value = defaultFunction.lf_function_id;
        // Exclusive discipline: the core one is the primary, nothing compared yet.
        primaryFunctionId.value = defaultFunction.lf_function_id;
      }
    }

    const fetchData = () => {
      axios
        .get("levelling/frameworkDefault")
        .then((resp) => {
          if (resp.data.Data) {
            // this.template_data.push({ tmpl_id: Number(resp.data.Data.template) , data: resp.data.Data});
            populateFramework(resp.data.Data);
          }
        })
        .catch((err) => {        
          console.log(err);
        });
    };

    // --- Initialization ---
    fetchData();
    if (hasAdminPermission.value) fetchFrameworkList();

    // --- Return ---
    return {
      is_editable,
      canEditFramework,
      editMode,
      setEditMode,
      primaryFunctionId,
      compareFunctionId,
      compareFunctionOptions,
      setPrimaryFunction,
      setCompareFunction,
      levelRange,
      levelRangeOptions,
      levelSortDesc,
      sortedTracks,
      isTrackHidden,
      toggleTrack,
      trackChipTitle,
      frameworkNotes,
      LEVELLING_GLOSSARY,
      glossaryOpen,
      tour,
      closeGlossary,
      adminRegisters,
      adminActiveRegisterType,
      adminAddLabel,
      factorDrill,
      openFactorDrill,
      closeFactorDrill,
      scopeLines,
      shortName,
      trackChipTint,
      gridScopeTitle,
      gridScopeMeta,
      onTrackCellClick,
      isComparing,
      tabsRow,
      tabsInHead,
      tabViews,
      visitedTabs,
      tourInUse,
      activeTour,
      activeTourTip,
      anchors,
      goEvaluateRole,
      selectedTab,
      ltfFunctionId,
      ltfTrackId,
      ltfLevelRange,
      ltfFunction,
      ltfTrack,
      isLtfTrackSelected,
      isLtfFactorSelected,
      selectLtfTrack,
      selectLtfFactor,
      isLtfByFactor,
      ltfWeightedFactors,
      ltfEmptyMessage,
      ltfScopeNote,
      ltfFactor,
      ltfFactorId,
      ltfPivot,
      ltfLevelAbbreviation,
      ltfAbbrColour,
      ltfTrackChipTint,
      ltfCellPopup,
      factorsDialog,
      factorDescriptorSummary,
      isFactorExpanded,
      toggleFactorExpanded,
      levelling_framework,
      selectedFunctions,
      filteredLevels,
      filteredTracks,
      totalTrackColumns,
      ltfLevels,
      ltfFactorTypes,
      ltfColumnGroups,
      sfdSelectedFactorIds,
      sfdFactorOptions,
      sfdFilteredFactorTypes,
      subFactorLevels,
      sfdGetLevel,
      sfdEditDialog,
      openSfdEditDialog,
      saveSfdEditDialog,
      closeSfdEditDialog,
      levelDescriptorDialog,
      openLevelDescriptorDialog,
      closeLevelDescriptorDialog,
      getFactorScores,
      getFactorColor,
      hexToRgba,
      chipColour,
      getFunctionTracks,
      sortedframework_levels,
      sortedFunction_tracks,
      getFunctionLevelTrack,
      getGroupLevel,
      incompleteFactorCount,
      shouldShowAddButtonForFramework,
      lftDialog,
      openLftDialog,
      closeLftDialog,
      saveLftDialog,
      getCellKey,
      openFactorsDialog,
      hideLevelTrack,
      trackDialog,
      hideAllSettings,
      tabSettingsDialog,
      openTabSettingsDialog,
      closeTabSettingsDialog,
      saveTabSettingsDialog,
      settingLabel,
      openTrackDialog,
      closeTrackDialog,
      saveTrackDialog,
      hideTrack,
      saveFactorsDialog,
      cancelFactorsDialog,
      closeFactorsDialogWithoutSaving,
      handleDialogClose,
      handleDialogOutsideClick,
      closeFactorsDialog,
      getFactorName,
      openLtfCellPopup,
      closeLtfCellPopup,
      getLtfSubFactorLevelNames,
      getLtfDescription,
      getLevelTrackFactor,
      getSubFactorLevelName,
      formatDate,
      getNextId,
      hasAdminPermission,
      adminFrameworkList,
      adminSaving,
      adminActiveEntityTab,
      adminEntityDialog,
      adminFrameworkDialog,
      adminDeleteConfirm,
      switchFramework,
      openAdminFrameworkDialog,
      closeAdminFrameworkDialog,
      saveAdminFrameworkDialog,
      deleteFramework,
      openAdminEntityDialog,
      closeAdminEntityDialog,
      saveAdminEntityDialog,
      openAdminDeleteConfirm,
      closeAdminDeleteConfirm,
      confirmAdminDelete,
      adminCopyDialog,
      openCopyFunctionDialog,
      confirmCopyFunction,
      importDialog,
      selectImportFramework,
      openImportFrameworkDialog,
      confirmImportFramework,
      publishDialog,
      openPublishDialog,
      confirmPublish,
      deleteLevelFunctionTrack,
      gridInlineEdit,
      startGridInlineEdit,
      saveGridInlineEdit,
      cancelGridInlineEdit,
      isGridInlineEditing,
      subFactorExpanded,
      subFactorEditing,
      subFactorLevelEditing,
      subFactorSaving,
      subFactorErrors,
      subFactorLevelErrors,
      addSubFactor,
      startEditSubFactor,
      cancelEditSubFactor,
      saveSubFactor,
      deleteSubFactor,
      addSubFactorLevel,
      startEditSubFactorLevel,
      cancelEditSubFactorLevel,
      saveSubFactorLevel,
      deleteSubFactorLevel,
      errorPopup,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/vars";

/* ============================================================================
   Levelling framework — restyled to the ClaudeDesign framework browser
   (ClaudeDesign/lvl/framework.jsx + lvl/lvl.css, already ported for the
   evaluation wizard in levellingEvaluation/evaluationTheme.scss).

   Presentation only: every class, selector and behaviour the template relies on
   is kept — the tabs, grids, dialogs and admin registers are the same, they
   just read in the design's language now.

   Tokens: the app-wide `--cc-*` ramp (assets/styles/theme.scss), which is the
   same palette the design's `--v3-*` set describes and which already adapts to
   the portal's dark theme. Geometry follows lvl.css: 12px cards, 10px panels,
   8px controls, 6px chips, 999px segmented pills.
============================================================================ */

$fw-r-card: 12px;
$fw-r-panel: 10px;
$fw-r-ctl: 8px;
$fw-r-chip: 6px;

.framework-container {
  padding: 1.5rem;
  width: 100%;
  height: calc(100vh - 64px); /* Account for app navbar */
  display: flex;
  flex-direction: column;
  font-family: var(--cc-font, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  color: var(--cc-text, #1f242d);
  -webkit-font-smoothing: antialiased;
}

.v-application.theme--light {
  .framework-container {
    color: var(--cc-text, #{$input-text-color});
  }
}

/* The framework browser sits directly on the canvas in the design — the grid
   and the admin registers are the cards, not the page. */
.framework-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: transparent !important;
  border-radius: 0;
  box-shadow: none !important;
}

.framework-content > .v-card__text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0;
  color: inherit;

  > .row {
    flex: 0 0 auto;
  }
}

.framework-content ::v-deep .page-title {
  padding: 0 0 14px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.35;
  color: var(--cc-text, #1f242d);
  word-break: normal;
}

/* ---- Tab bar (design: .lvl-tabs / .lvl-tab) ----
   Plain buttons rather than `v-tabs`: the design's bar has no sliding
   indicator, and a Vuetify tab strip re-measures its slider and overflow
   arrows against its container — which this row leaves when it is lifted into
   the page head. Buttons keyed off `selectedTab` also keep the highlight and
   the rendered view in step, which the un-modelled `v-tabs` could not. */
.rm-tabs-host.is-inhead {
  display: none;
}

.rm-tabs-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  min-width: 0;
  border-bottom: 1px solid var(--cc-border, #e5e7eb);
  margin-bottom: 4px;
}

/* Vuetify renders the activator as a SIBLING of <span class="v-tooltip">, not
   inside it, so each tooltip leaves an empty span in the row. Left alone those
   spans are flex items in their own right, adding a phantom gap before every
   tooltipped button. `display: contents` takes them out of layout. */
.rm-tabs-row > .v-tooltip,
.lvl-tabs > .v-tooltip {
  display: contents;
}

.lvl-tabs {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.lvl-tab {
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  padding: 12px 16px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: 0.005em;
  line-height: 1.2;
  color: var(--cc-text-3, #97a0af);
  white-space: nowrap;
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;

  &:hover {
    color: var(--cc-text-2, #525c6b);
  }

  &.on {
    color: var(--cc-text, #1f242d);
    border-bottom-color: var(--cc-accent, #4f46e5);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: -2px;
  }
}

/* Lifted into the page head: the row fills the head's height so the active
   tab's underline lands on the head's bottom edge (the negative margin cancels
   the head's 14px bottom padding), and the head's own bottom rule serves as the
   tab strip's baseline. */
.rm-tabs-row.lvl-tabs--inhead {
  flex: 1 1 auto;
  align-self: stretch;
  align-items: stretch;
  border-bottom: 0;
  margin: 0 0 -14px;

  .lvl-tabs {
    align-items: stretch;
  }

  .lvl-tab {
    display: inline-flex;
    align-items: center;
    padding: 0 14px;
    border-bottom-width: 3px;
  }

  .tabs-settings-icon {
    align-self: center;
    margin-bottom: 0;
  }
}

.tabs-settings-icon {
  color: var(--cc-text-3, #97a0af);
  cursor: pointer;
  flex-shrink: 0;
  margin-bottom: 12px;

  &:hover {
    color: var(--cc-text, #1f242d);
  }
}

/* Ported from the prototype's .v3-pagehead-edit — a quiet bordered button rather than a
   primary one: it is chrome for the page, not an action on the register, and the two
   should not read as equals. */
.lvl-pagehead-tut {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 7px 13px;
  margin: 8px 0 8px 0;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 8px;
  background: var(--cc-surface, #fff);
  color: var(--cc-text, #1f242d);
  font: inherit;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
  transition: background 120ms, border-color 120ms;
}
.lvl-pagehead-tut .ms { font-size: 16px; color: var(--cc-text-2, #525c6b); }
.lvl-pagehead-tut:hover {
  background: var(--cc-canvas, #f6f7f9);
  border-color: var(--cc-border-strong, #d3d8e0);
}

/* ---- Levelling terms (design: .lvl-tab--gloss / .lvl-gloss-list) ---- */
/* `margin-left: auto` is what pushes it to the far right of the tab row. */
.lvl-tab--gloss {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--cc-text-2, #525c6b);

  .v-icon {
    color: var(--cc-text-3, #97a0af) !important;
  }

  &:hover {
    color: var(--cc-accent, #4f46e5);

    .v-icon {
      color: var(--cc-accent, #4f46e5) !important;
    }
  }
}

/* .dialog-body is a horizontal flex row shared with the other dialogs; a
   definition list needs normal flow. */
.lvl-gloss-body {
  display: block;
  max-height: min(62vh, 560px);
  overflow: auto;
}

.lvl-gloss-list {
  margin: 0;
  display: grid;
  gap: 12px;
  padding: 0;

  dt {
    font-size: 13px;
    font-weight: 700;
    color: var(--cc-text, #1f242d);
  }

  dd {
    margin: 3px 0 0;
    font-size: 12.5px;
    line-height: 1.5;
    color: var(--cc-text-2, #525c6b);
    text-wrap: pretty;
  }
}

/* ---- Factor details: group list + right-hand drill (design LvlFactorList /
       LvlFactorDrill) ---- */
.sf-groups {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* design: .lvl-group-tag, minus its uppercase - the DS voice rule is no
   all-caps labels, so the eyebrow reads through size/weight/tracking. */
.sf-group-tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.015em;
  color: var(--cc-text-2, #525c6b);
}

.sf-group-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: 0 0 auto;
}

/* design: .lvl-card */
.sf-card {
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-card;
  overflow: hidden;
}

/* design: .lvl-frow, as a button - the whole row opens the drill. */
.sf-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 14px;
  border: 0;
  border-top: 1px solid var(--cc-border-soft, #f0f0f0);
  background: transparent;
  text-align: left;
  font-family: inherit;
  cursor: pointer;

  &:first-child {
    border-top: 0;
  }

  &:hover {
    background: var(--cc-surface-2, #f7f9fb);
  }

  &.is-open {
    background: var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: -2px;
  }
}

.sf-row--empty {
  display: block;
  cursor: default;
  color: var(--cc-text-3, #97a0af);
  font-size: 12.5px;

  &:hover {
    background: transparent;
  }
}

.sf-row-main {
  min-width: 0;
}

.sf-row-name {
  display: block;
  font-size: 13.5px;
  font-weight: 550;
  color: var(--cc-text, #1f242d);
}

.sf-row-blurb {
  display: block;
  margin-top: 2px;
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--cc-text-3, #97a0af);
}

.sf-row-val {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;

  .v-icon {
    color: var(--cc-text-3, #97a0af) !important;
  }
}

.sf-row-count {
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
  white-space: nowrap;
}

/* design: .lvl-drill - a right-hand panel over a scrim. */
.sf-drill {
  position: fixed;
  inset: 0;
  z-index: 1600;
  display: flex;
  justify-content: flex-end;
}

.sf-drill-scrim {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.34);
}

.sf-drill-panel {
  position: relative;
  width: min(560px, 100%);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--cc-surface, #ffffff);
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, 0.14));
}

.sf-drill-head {
  flex: 0 0 auto;
  padding: 20px 22px;
  border-bottom: 1px solid var(--cc-border, #e5e7eb);

  .sf-group-tag {
    margin-bottom: 0;
  }

  h3 {
    margin: 6px 0 0;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    color: var(--cc-text, #1f242d);
  }
}

.sf-drill-head-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.sf-drill-close {
  flex: 0 0 auto;
  border: 0;
  background: var(--cc-surface-2, #f7f9fb);
  border-radius: $fw-r-ctl;
  width: 30px;
  height: 30px;
  font-size: 20px;
  line-height: 1;
  color: var(--cc-text-2, #525c6b);
  cursor: pointer;

  &:hover {
    color: var(--cc-text, #1f242d);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 1px;
  }
}

.sf-drill-blurb {
  margin: 10px 0 0;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--cc-text-2, #525c6b);
}

.sf-drill-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sf-drill-empty {
  font-size: 12.5px;
  color: var(--cc-text-3, #97a0af);
}

/* design: .lvl-level - one proficiency level. */
.sf-level {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 13px 16px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-panel;
  background: var(--cc-surface, #ffffff);
}

.sf-level-n {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: $fw-r-ctl;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-surface-2, #f7f9fb);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: var(--cc-text-2, #525c6b);
}

.sf-level-main {
  flex: 1 1 auto;
  min-width: 0;
}

.sf-level-t {
  display: block;
  font-size: 14px;
  font-weight: 650;
  color: var(--cc-text, #1f242d);
}

.sf-level-d {
  display: block;
  margin-top: 2px;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--cc-text-2, #525c6b);
}

/* The stored `scope` lines already carry their own bullet glyph, so no marker
   is added here - one would read as a double bullet. The hanging indent keeps a
   wrapped line aligned with the text rather than under the bullet. */
.sf-level-scope {
  margin: 6px 0 0;
  padding-left: 0;
  list-style: none;
  font-size: 12px;
  line-height: 1.5;
  color: var(--cc-text-2, #525c6b);

  li {
    padding-left: 1em;
    text-indent: -1em;
  }
}

/* design: .lvl-note--info - accent-tinted, distinct from the amber caution the
   edit banner uses. */
.fw-infonote {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 12px 15px;
  border-radius: $fw-r-card;
  background: var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
  color: var(--cc-text-2, #525c6b);
  font-size: 13px;
  line-height: 1.5;

  strong {
    color: var(--cc-text, #1f242d);
    font-weight: 700;
  }

  .fw-infonote-ic {
    flex: 0 0 auto;
    margin-top: 1px;
    color: var(--cc-accent, #4f46e5) !important;
  }
}

.admin-entity-seg {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0;
  flex: 0 0 auto;
}

.admin-entity-seg-spacer {
  flex: 1;
}

/* design: the primary action for the visible register, beside the pills rather
   than hidden in the table's header cell. */
.admin-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  cursor: pointer;
  padding: 8px 15px 8px 12px;
  border: 1px solid var(--cc-accent, #4f46e5);
  border-radius: $fw-r-ctl;
  background: var(--cc-accent, #4f46e5);
  color: #fff;
  font-family: inherit;
  font-weight: 600;
  font-size: 12.5px;
  line-height: 1.2;
  white-space: nowrap;

  .v-icon {
    color: #fff !important;
  }

  &:hover {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 2px;
  }
}

.admin-th-actions {
  text-align: right;
}

/* ---- Grid card header + edit-mode note ---- */
/* design: amber note above the card - a caution, not an info tip, because the
   consequences it lists are not reversible from this screen. */
.fw-editnote {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  flex: 0 0 auto;
  padding: 12px 15px;
  margin-bottom: 10px;
  border: 1px solid var(--cc-warn-bd, #f3e6bd);
  border-radius: $fw-r-card;
  background: var(--cc-warn-bg, #fbf4e0);
  font-size: 13px;
  line-height: 1.5;

  .fw-editnote-ic {
    flex: 0 0 auto;
    margin-top: 1px;
    color: var(--cc-warn-fg, #8a6a00) !important;
  }

  .fw-editnote-main {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .t {
    font-weight: 700;
    color: var(--cc-text, #1f242d);
  }

  .s {
    font-size: 12.5px;
    color: var(--cc-text-2, #525c6b);
  }
}

/* design: tinted strip capping the grid card. Shares the card's top corners,
   so the two read as one surface. */
.fw-tablehead {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-bottom: 0;
  border-radius: $fw-r-card $fw-r-card 0 0;
  background: var(--cc-accent-tint, #e9f0fd);
  flex-shrink: 0;
}

.fw-tablehead-title {
  font-weight: 700;
  font-size: 13px;
  color: var(--cc-text, #1f242d);
  white-space: nowrap;
}

.fw-tablehead-meta {
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fw-tablehead-spacer {
  flex: 1;
}

.fw-tablehead-state {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--cc-text-2, #525c6b);

  .v-icon {
    color: var(--cc-text-3, #97a0af) !important;
  }

  &.is-editing {
    color: var(--cc-warn-fg, #8a6a00);

    .v-icon {
      color: var(--cc-warn-fg, #8a6a00) !important;
    }
  }
}

.fw-tablehead-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  cursor: pointer;
  padding: 6px 13px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  background: var(--cc-surface, #ffffff);
  color: var(--cc-text, #1f242d);
  font-family: inherit;
  font-weight: 600;
  font-size: 12.5px;
  line-height: 1.2;
  white-space: nowrap;

  .v-icon {
    color: var(--cc-text-2, #525c6b) !important;
  }

  &:hover {
    background: var(--cc-surface-2, #f7f9fb);
    border-color: var(--cc-border-strong, #d4dae6);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 1px;
  }
}

/* ---- Filter bar (design: .lvl-fw-filter / .lvl-fwf-lbl / .lvl-fwseg) ---- */
.filter-row {
  border-radius: 0;
  padding: 0;
  margin: 14px 0 12px;
  row-gap: 10px;
}

.filter-col {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding-top: 0;
  padding-bottom: 0;

  &:empty {
    display: none;
  }
}

.filter-label {
  font-size: 12px;
  font-family: inherit;
  font-weight: 600;
  color: var(--cc-text-2, #525c6b);
  letter-spacing: 0.015em;
  line-height: 1.4;
  white-space: nowrap;
}

.filter-select {
  min-width: 180px;
  max-width: 280px;
}

/* Segmented pills - discipline, Compare with, View, level window.
   Ported from the design's .lvl-fwseg onto the app's --cc-* tokens. */
.lvl-fwseg {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  background: var(--cc-surface-2, #f7f9fb);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 999px;
}

.lvl-fwseg-btn {
  border: 0;
  cursor: pointer;
  border-radius: 999px;
  padding: 5px 13px;
  font-family: inherit;
  font-weight: 600;
  font-size: 12.5px;
  line-height: 1.2;
  white-space: nowrap;
  background: transparent;
  color: var(--cc-text-2, #525c6b);

  &:hover {
    color: var(--cc-text, #1f242d);
  }

  &.on {
    background: var(--cc-surface, #ffffff);
    color: var(--cc-text, #1f242d);
    box-shadow: var(--cc-shadow, 0 4px 14px rgba(0, 0, 0, 0.07));
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 1px;
  }
}

/* Track chips - on by default, struck through when switched off. */
.lvl-fw-trackchips {
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
}

.lvl-fw-trackchip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-surface, #ffffff);
  border-radius: 999px;
  padding: 4px 11px 4px 5px;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 12.5px;
  line-height: 1.2;
  white-space: nowrap;
  color: var(--cc-text, #1f242d);

  .tk {
    width: 17px;
    height: 17px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 9.5px;
    font-weight: 800;
    color: #fff;
  }

  /* Hide-list mode: struck out, because the track is taken off the grid. */
  &.is-off {
    color: var(--cc-text-3, #97a0af);
    background: var(--cc-surface-2, #f7f9fb);
    text-decoration: line-through;
  }

  /* Compare mode: the chips are a radio group, so an unpicked track has not
     been removed - it is simply not the one being compared. Desaturated rather
     than struck through, which would read as deleted. */
  &.is-muted {
    color: var(--cc-text-3, #97a0af);
    background: var(--cc-surface-2, #f7f9fb);
    filter: saturate(0.2);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 1px;
  }
}

/* design: .lvl-fw-wnote / .lvl-fw-cfgnote */
.fw-filter-notes {
  display: flex;
  align-items: center;
  gap: 6px 18px;
  flex-wrap: wrap;
  margin: -4px 12px 24px 12px;
}

.fw-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  font-family: inherit;
  color: var(--cc-text-2, #525c6b);
  cursor: help;

  &:hover {
    color: var(--cc-text, #1f242d);
  }

  .v-icon {
    color: var(--cc-text-3, #97a0af) !important;
  }
}

/* Function picker as the design's segmented pill. */
.function-tabs {
  margin-bottom: 0;
  padding: 3px;
  gap: 4px;
  height: auto !important;
  background: var(--cc-surface-2, #f7f9fb);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 999px;
  box-shadow: none !important;

  ::v-deep .v-btn {
    text-transform: none;
    font-family: inherit;
    font-weight: 600;
    font-size: 12.5px;
    letter-spacing: 0.005em;
    line-height: 1.2;
    height: 26px !important;
    min-width: 0 !important;
    padding: 0 13px !important;
    border: 0 !important;
    border-radius: 999px !important;
    color: var(--cc-text-2, #525c6b);
    background-color: transparent !important;
    box-shadow: none !important;
  }

  ::v-deep .v-btn--active {
    background-color: var(--cc-surface, #ffffff) !important;
    color: var(--cc-text, #1f242d) !important;
    box-shadow: var(--cc-shadow, 0 4px 14px rgba(0, 0, 0, 0.07)) !important;

    &:before {
      opacity: 0;
    }
  }
}

.settings-btn {
  border-color: var(--cc-border, #e5e7eb) !important;
  border-radius: $fw-r-ctl;
  background: var(--cc-surface, #ffffff);
  color: var(--cc-text-2, #525c6b) !important;
  min-width: 0 !important;
  padding: 0 8px !important;

  &:hover {
    background: var(--cc-surface-2, #f7f9fb);
  }

  ::v-deep .v-icon {
    color: var(--cc-text-3, #97a0af);
  }
}

.tab-grid-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  > .row {
    flex: 0 0 auto;
  }
}

.function-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 2px;
}

/* ---- The grid card (design: .lvl-matrix / .lvl-fgrid) ---- */
/* `flex: 0 1 auto` rather than `1`: the card is only as tall as its rows, so a
   short framework leaves no empty white below the last one, but it can still
   shrink when the rows overflow - at which point `overflow: auto` takes over
   and the sticky header engages. */
.framework-grid-wrapper {
  overflow: auto;
  flex: 0 1 auto;
  min-height: 0;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-card;
}

/* Only the Levels and tracks grid is capped by a header strip, which owns the
   top corners there. Keyed off the strip rather than a modifier class so the
   other grids (Factor descriptors, Sub-factor descriptors) keep a fully rounded
   card without having to opt in. */
.fw-tablehead + .framework-grid-wrapper {
  border-radius: 0 0 $fw-r-card $fw-r-card;
}

.framework-grid {
  width: 100%;
}

/* Fixed layout, so the grid always fits its container and never scrolls
   sideways. The level gutter takes a set width and every track column shares
   what is left equally - four tracks give four equal columns - which is what
   the filter bar's track chips are for: fewer columns, each wider. */
.grid-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.header-row, .level-row, .framework-name-row {
  background-color: transparent;
}

.framework-name-row th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.framework-name-row th.level-header {
  left: 0;
  z-index: 4;
  border-right: none;
  border-bottom: none;
  height: 36px;
  padding: 6px 12px;
}

.header-row th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.header-row th.level-header {
  left: 0;
  z-index: 3;
}

.framework-name-row + .header-row th {
  top: 36px; /* Height of framework-name-row */
}

.framework-name-row + .header-row th.level-header {
  z-index: 3;
}

.framework-name-row th.framework-name-header:nth-child(2) {
  border-left: 1px solid var(--cc-border-soft, #f0f0f0);
}

.header-cell {
  font-weight: 700;
  font-size: 12px;
  font-family: inherit;
  text-align: left;
  letter-spacing: 0.015em;
  line-height: 1.4;
  color: var(--cc-text, #1f242d);
  padding: 10px 12px;
  border-bottom: 1px solid var(--cc-border, #e5e7eb);
  border-right: 1px solid var(--cc-border-soft, #f0f0f0);
  vertical-align: middle;
  background-color: var(--cc-surface-2, #f7f9fb);
}

/* The function grouping row reads as an eyebrow — size and tracking, no caps
   (design directive: no all-caps labels). */
.framework-name-header {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--cc-text-3, #97a0af) !important;
  background-color: var(--cc-surface-2, #f7f9fb) !important;
  padding: 6px 12px;
  height: 36px;
  text-transform: none;
  letter-spacing: 0.015em;
  border-radius: 0;
}

.ltf-factor-header {
  /* design: factor names centred over their column, on the plain head fill -
     the per-factor tint is gone, so nothing else distinguishes them. */
  text-align: center;
  background-color: var(--cc-surface-2, #f7f9fb);
  border-right: none;
}

.ltf-empty {
  padding: 22px 12px;
  text-align: center;
  font-size: 12.5px;
  color: var(--cc-text-3, #97a0af);
  border-bottom: 1px solid var(--cc-border-soft, #f0f0f0);
}

.ltf-factor-last {
  border-right: 1px solid var(--cc-border-soft, #f0f0f0);
}

.level-header {
  text-align: center;
  /* Under table-layout: fixed this is the only column with a stated width, so
     the track columns split the remainder between them. */
  width: 92px;
  background-color: var(--cc-surface-2, #f7f9fb) !important;
  border-top: none;
  font-weight: 700;
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
  position: sticky;
  left: 0;
  z-index: 3;
}

.track-header {
  /* Columns are narrow now, so a long track name has to wrap beside its chip
     rather than dropping onto a line below it and shunting the header out of
     line with its neighbours. */
  .track-header-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .track-code-chip {
    flex: 0 0 auto;
  }

  .track-header-content {
    flex: 1 1 auto;
  }

  .track-header-menu {
    flex: 0 0 auto;
    margin-left: auto;
  }
}

/* design: .lvl-matrix-sort - the Level header doubles as the sort toggle. */
.level-sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  border-radius: $fw-r-chip;

  .v-icon {
    color: var(--cc-text-3, #97a0af) !important;
  }

  &:hover {
    color: var(--cc-accent, #4f46e5);

    .v-icon {
      color: var(--cc-accent, #4f46e5) !important;
    }
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 3px;
  }
}

/* design: the ladder's code for this level, under the level number. */
.ltf-level-abbr {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--cc-border-soft, #f0f0f0);
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.01em;
}

.level-cell {
  padding: 10px 8px;
  vertical-align: middle;
  border-right: 1px solid var(--cc-border-soft, #f0f0f0);
  border-bottom: 1px solid var(--cc-border-soft, #f0f0f0);
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  font-family: inherit !important;
  line-height: 1.4;
  color: var(--cc-text-3, #97a0af);
  position: sticky;
  left: 0;
  background-color: var(--cc-surface, #ffffff);
  z-index: 1;
}

.level-row:last-child .level-cell,
.level-row:last-child .track-cell,
.level-row:last-child .ltf-cell {
  border-bottom: 0;
}

.track-cell {
  padding: 8px;
  vertical-align: top;
  border-right: 1px solid var(--cc-border-soft, #f0f0f0);
  border-bottom: 1px solid var(--cc-border-soft, #f0f0f0);
}

.track-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow-wrap: anywhere;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.015em;
  color: var(--cc-text, #1f242d);
}

.track-header-menu {
  color: var(--cc-text-3, #97a0af);
  cursor: pointer;

  &:hover {
    color: var(--cc-text, #1f242d);
  }
}

/* One grade cell (design: .lvl-grade). */
.track-content {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  padding: 8px 10px;
  background: var(--cc-surface, #ffffff);
  transition: filter 120ms ease, border-color 120ms ease;

  &:hover {
    border-color: var(--cc-border-strong, #d4dae6);
  }
}

.track-content-fixed {
  position: relative;
  max-height: 100px;
  overflow: hidden;

  .track-description {
    max-height: 80px;
    overflow: hidden;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to bottom, transparent, var(--cc-surface, #ffffff));
    pointer-events: none;
    border-radius: 0 0 $fw-r-ctl $fw-r-ctl;
  }
}

.track-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.track-code-chip-visual {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

/* design: .lvl-grade-chip */
.track-code-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 7px;
  background-color: transparent;
  color: var(--cc-text, #1f242d);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-chip;

  /* design: .lvl-grade-chip - the track tint lives here and nowhere else in
     the grid, so the label is knocked out white and the border removed. */
  &.is-tinted {
    color: #fff;
    border-color: transparent;
  }
  font-size: 11px;
  flex-shrink: 0;
  font-weight: 800;
  font-family: inherit;
  letter-spacing: 0.01em;
  line-height: 1.5;
  width: fit-content;
  min-width: 28px;
  margin-top: 0;
}

.track-code-visual {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 30%;
  min-width: 60px;
}

.factor-bar-container {
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  cursor: help;
  display: flex;
  justify-content: center;
}

.factor-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
  min-width: 2px;
}

.expand-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  color: var(--cc-text-3, #97a0af);
  font-size: 12px;
  transition: all 0.15s ease;
  border-radius: $fw-r-ctl;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  flex-shrink: 0;

  &:hover {
    background-color: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text, #1f242d);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 1px;
  }

  span {
    display: block;
    line-height: 1;
  }
}

/* design: .lvl-grade--btn stacks the code over the name; .lvl-grade--desc sits
   the chip and name on one row with the descriptor beneath. */
.track-title {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  /* Always actionable: the edit dialog while editing, expand/collapse otherwise. */
  cursor: pointer;
  font-size: 13px;
  font-family: inherit;
  color: var(--cc-text, #1f242d);
  line-height: 1.4;
  flex: 1;
  letter-spacing: 0;
  min-width: 0;
  font-weight: 550;

  &.track-title--inline {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
}

.track-name {
  min-width: 0;
  overflow-wrap: anywhere;
}

.track-cell-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.track-cell-icons {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.track-menu-icon,
.track-expand-icon {
  color: var(--cc-text-3, #97a0af);
  cursor: pointer;

  &:hover {
    color: var(--cc-text, #1f242d);
  }
}

/* design: .lvl-grade-desc */
.track-description {
  font-size: 12px;
  font-family: inherit;
  font-weight: 400;
  letter-spacing: 0;
  color: var(--cc-text-2, #525c6b);
  line-height: 1.5;
  padding-top: 2px;
}

.track-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  background-color: transparent;
  height: 100%;

  &.track-empty-edge {
    background-color: var(--cc-surface-2, #f7f9fb);
    border: 1px dashed var(--cc-border, #e5e7eb);
    border-radius: $fw-r-ctl;
  }
}

.add-track-btn {
  padding: 6px 12px;
  background-color: transparent;
  border: 1px dashed var(--cc-border-strong, #d4dae6);
  border-radius: $fw-r-ctl;
  color: var(--cc-text-3, #97a0af);
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
    border-color: var(--cc-accent, #4f46e5);
    color: var(--cc-accent, #4f46e5);
  }
}

.show-factors-btn {
  padding: 6px 12px;
  background-color: var(--cc-accent, #4f46e5);
  color: #fff;
  border: none;
  border-radius: $fw-r-ctl;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  transition: filter 0.15s ease;
  width: 80px;
  margin-top: 4px;

  &:hover {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 2px;
  }
}

.track-description-container {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--cc-border-soft, #f0f0f0);
}

.track-description-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-text-2, #525c6b);
  margin-bottom: 4px;
}

.track-description-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.45;
  resize: vertical;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--cc-accent, #4f46e5);
    box-shadow: 0 0 0 3px var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
  }

  &::placeholder {
    color: var(--cc-text-3, #97a0af);
  }
}

.track-description-readonly {
  width: 100%;
  padding: 8px 10px;
  background-color: var(--cc-surface-2, #f7f9fb);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  font-size: 13px;
  line-height: 1.5;
  color: var(--cc-text-2, #525c6b);
  white-space: pre-wrap;
  word-wrap: break-word;
  min-height: 3em;
}

/* ---- Factor-descriptor grid cells (design: .lvl-fgrid-cell) ---- */
.ltf-cell {
  padding: 10px 12px;
  vertical-align: top;
  font-family: inherit;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.5;
  letter-spacing: 0;
  color: var(--cc-text-2, #525c6b);
  border-bottom: 1px solid var(--cc-border-soft, #f0f0f0);
  cursor: pointer;
  transition: background-color 120ms ease;

  &:hover {
    background-color: var(--cc-surface-2, #f7f9fb);
  }
}

.ltf-cell-fixed {
  padding: 0;
}

.ltf-cell-fixed-inner {
  max-height: 120px;
  overflow: hidden;
  position: relative;
  padding: 10px 12px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to bottom, transparent, var(--cc-surface, #ffffff));
    pointer-events: none;
  }
}

.sfd-cell {
  position: relative;
}

.sfd-cell-info-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  color: var(--cc-text-3, #97a0af);
  cursor: pointer;

  &:hover {
    color: var(--cc-text, #1f242d);
  }
}

.sfd-cell-name {
  font-size: 12px;
  line-height: 1.5;
  color: var(--cc-text, #1f242d);
  font-weight: 550;
  display: block;
  padding-right: 16px;
}

.sfd-cell-description {
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--cc-text-2, #525c6b);
  margin-top: 4px;
}

.sfd-header-cell {
  position: relative;
}

.sfd-edit-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-text-2, #525c6b);
  letter-spacing: 0.015em;
  margin-bottom: 4px;
}

/* ---- Level-descriptor list (dialog) ---- */
.level-descriptor-item {
  padding-bottom: 12px;
  border-bottom: 1px solid var(--cc-border-soft, #f0f0f0);
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
}

.descriptor-name, .descriptor-text {
  font-size: 13.5px;
  font-family: inherit;
  line-height: 1.5;
  letter-spacing: 0;
  color: var(--cc-text, #1f242d);
}

.descriptor-text {
  font-weight: 400;
  color: var(--cc-text-2, #525c6b);
}

.descriptor-name {
  font-weight: 700;
}

.descriptor-text-outline {
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-panel;
  padding: 10px 12px;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  letter-spacing: 0;
  color: var(--cc-text-2, #525c6b);
  font-weight: 400;
  background: var(--cc-surface, #ffffff);
}

/* ---- Factor-descriptor cell popup (design: the ltfCellPopup card) ---- */
.ltf-dimmed {
  opacity: 0.4;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.ltf-popup-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
}

.ltf-popup-card {
  position: absolute;
  background: var(--cc-surface, #ffffff);
  border-radius: $fw-r-card;
  border: 1px solid var(--cc-border, #e5e7eb);
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, 0.14));
  min-width: 320px;
  max-width: 500px;
  max-height: 60%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ltf-popup-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 18px 12px;
  border-bottom: 1px solid var(--cc-border, #e5e7eb);
}

.ltf-popup-title {
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: -0.005em;
  color: var(--cc-text, #1f242d);
}

.ltf-popup-subtitle {
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
  margin-top: 2px;
}

.ltf-popup-close {
  background: none;
  border: none;
  font-size: 22px;
  color: var(--cc-text-3, #97a0af);
  cursor: pointer;
  line-height: 1;
  padding: 0 0 0 12px;

  &:hover {
    color: var(--cc-text, #1f242d);
  }
}

.ltf-popup-body {
  padding: 14px 18px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.55;
  color: var(--cc-text-2, #525c6b);
}

.ltf-popup-list {
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
  font-size: 13px;
  line-height: 1.7;

  li {
    color: var(--cc-text-2, #525c6b);
  }
}

.ltf-popup-empty {
  color: var(--cc-text-3, #97a0af);
  font-size: 13px;
}

/* ---- Factor / sub-factor descriptor tabs ---- */
.descriptor-tab-content {
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  padding-top: 4px !important;
}

.factor-descriptor-card {
  border-radius: $fw-r-card !important;
  border-color: var(--cc-border, #e5e7eb) !important;
  background: var(--cc-surface, #ffffff);
  transition: box-shadow 0.15s ease;

  ::v-deep .v-card__title {
    font-family: inherit;
    font-size: 14px !important;
    font-weight: 700;
    letter-spacing: -0.005em;
    color: var(--cc-text, #1f242d);
  }

  ::v-deep .v-card__text {
    font-family: inherit;
    font-size: 13px;
    line-height: 1.55;
    color: var(--cc-text-2, #525c6b);
  }

  ::v-deep .caption {
    font-family: inherit !important;
    font-size: 11.5px !important;
    letter-spacing: 0.015em !important;
    color: var(--cc-text-3, #97a0af);
  }

  ::v-deep .v-chip {
    font-family: inherit;
    font-size: 11.5px;
    border-radius: $fw-r-chip;
    border-color: var(--cc-border, #e5e7eb) !important;
    color: var(--cc-text-2, #525c6b) !important;
  }

  &:hover {
    box-shadow: var(--cc-shadow, 0 4px 14px rgba(0, 0, 0, 0.07));
  }
}

.descriptor-tab-content ::v-deep .v-data-table,
.descriptor-tab-content ::v-deep table {
  background: transparent;

  th {
    font-family: inherit;
    font-size: 12px !important;
    font-weight: 700;
    letter-spacing: 0.015em;
    text-transform: none;
    color: var(--cc-text-3, #97a0af) !important;
    background: var(--cc-surface-2, #f7f9fb);
    border-bottom: 1px solid var(--cc-border, #e5e7eb) !important;
  }

  td {
    font-family: inherit;
    font-size: 12.5px;
    color: var(--cc-text-2, #525c6b);
    border-bottom: 1px solid var(--cc-border-soft, #f0f0f0) !important;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  font-size: 13px;
  color: var(--cc-text-2, #525c6b);

  .loader {
    width: 34px;
    height: 34px;
    border: 3px solid var(--cc-border, #e5e7eb);
    border-top-color: var(--cc-accent, #4f46e5);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
}

.framework-header {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  h2.framework-name {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.01em;
    text-transform: capitalize;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.copy-framework-icon {
  color: var(--cc-text-3, #97a0af);
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: var(--cc-accent, #4f46e5);
  }
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.read-only-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--cc-accent, #4f46e5);
  }

  span {
    color: var(--cc-text-2, #525c6b);
  }

  &:hover span {
    color: var(--cc-text, #1f242d);
  }
}

.expand-all-btn {
  padding: 7px 13px;
  background-color: var(--cc-accent, #4f46e5);
  color: #fff;
  border: none;
  border-radius: $fw-r-ctl;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;

  &:hover {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 2px;
  }
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--cc-text-3, #97a0af);
  font-size: 13px;
}

/* ============================================================================
   Dialogs (design: .lvl-modal — surface card, soft scrim, token borders)
============================================================================ */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgb(16 24 40 / 0.34);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.dialog-content {
  background-color: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 14px;
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, 0.14));
  max-width: 95vw;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  font-family: inherit;
  color: var(--cc-text, #1f242d);
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid var(--cc-border, #e5e7eb);

  h3, .page-title {
    margin: 0;
    font-size: 15.5px;
    font-family: inherit;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--cc-text, #1f242d);
  }
}

.dialog-body {
  padding: 16px 20px;
  overflow-x: auto;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: start;
  font-size: 13px;
  line-height: 1.55;
  color: var(--cc-text-2, #525c6b);
}

.dialog-header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-all-descriptions-btn {
  padding: 7px 13px;
  background-color: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  cursor: pointer;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--cc-text-2, #525c6b);
  transition: all 0.15s ease;

  &:hover {
    background-color: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text, #1f242d);
  }
}

.dialog-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: var(--cc-text-3, #97a0af);
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $fw-r-ctl;
  transition: all 0.15s ease;

  &:hover {
    background-color: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text, #1f242d);
  }
}

/* ---- Factors dialog: vertical accordion (design) ---- */
/* .dialog-body is a horizontal flex row shared with the other dialogs, which
   laid the factor groups out side by side. This one stacks. */
/* The colour picker's dropdown is absolutely positioned inside this box. A
   scroll container here clips it at the body's own edge, so it could never
   reach down over the footer - hence overflow: visible, with the scrolling
   moved out to .dialog-content--form. */
.admin-dialog-body {
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  overflow: visible;
}

/* Nothing between the dropdown and the viewport may be a scroll container, or
   it clips. So the card itself does not scroll or cap its height; the overlay
   scrolls instead, which means the only thing that can clip the dropdown is
   the viewport edge. */
.dialog-content--form {
  overflow: visible;
  max-height: none;
  flex: 0 0 auto;
  /* `margin: auto` rather than the overlay's `align-items: center`: it centres
     the card the same way but, when the card is taller than the overlay, keeps
     its top reachable instead of overflowing past the scroll origin. Avoids the
     `safe` alignment keyword, which older browsers drop entirely - and dropping
     it would fall back to `stretch`. */
  margin: auto;
}

.dialog-overlay--form {
  overflow-y: auto;
  align-items: flex-start;
}

.factors-body {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.factors-header {
  align-items: flex-start;
}

.factors-heading {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.factors-subtitle {
  font-size: 12.5px;
  font-weight: 400;
  color: var(--cc-text-2, #525c6b);
  line-height: 1.45;
}

.factor-acc {
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-panel;
  background: var(--cc-surface-2, #f7f9fb);
  overflow: hidden;

  /* The open row is the one being read: lift it onto the surface colour and
     mark it with the accent border. */
  &.is-open {
    border-color: var(--cc-accent, #4f46e5);
    background: var(--cc-surface, #ffffff);
  }
}

.factor-acc-head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  &:hover .factor-acc-chev {
    color: var(--cc-text, #1f242d) !important;
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: -2px;
  }
}

.factor-acc-pill {
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--cc-text, #1f242d);
}

.factor-acc-spacer {
  flex: 1 1 auto;
}

.factor-acc-count {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  color: var(--cc-text-2, #525c6b);
  white-space: nowrap;

  &.is-empty {
    color: var(--cc-text-3, #97a0af);
  }
}

.factor-acc-chev {
  flex: 0 0 auto;
  color: var(--cc-text-3, #97a0af) !important;
}

.factor-acc-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 12px 12px;
}

.factor-acc-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-text, #1f242d);
  letter-spacing: 0.005em;
}

.factor-expand-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  color: var(--cc-text-3, #97a0af);
  font-size: 12px;
  transition: all 0.15s ease;
  border-radius: $fw-r-ctl;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  flex-shrink: 0;

  &:hover {
    background-color: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text, #1f242d);
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 1px;
  }

  span {
    display: block;
    line-height: 1;
  }
}

.sub-factor-expand-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  font-size: 11.5px;
  color: var(--cc-text-3, #97a0af);
  transition: color 0.15s ease;

  &:hover {
    color: var(--cc-accent, #4f46e5);
  }

  &:focus {
    outline: none;
  }

  span {
    display: inline-block;
  }
}

.factor-text-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.45;
  resize: vertical;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--cc-accent, #4f46e5);
    box-shadow: 0 0 0 3px var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
  }

  &::placeholder {
    color: var(--cc-text-3, #97a0af);
  }
}

.factor-text-readonly {
  width: 100%;
  padding: 8px 10px;
  background-color: var(--cc-surface-2, #f7f9fb);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  font-size: 13px;
  line-height: 1.5;
  color: var(--cc-text-2, #525c6b);
  white-space: pre-wrap;
  word-wrap: break-word;
  min-height: 2em;
}

.no-factors {
  text-align: center;
  color: var(--cc-text-3, #97a0af);
  font-size: 13px;
  padding: 2rem;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid var(--cc-border, #e5e7eb);
}

.dialog-btn {
  &.dialog-btn-primary {
    background-color: var(--cc-accent, #4f46e5);
    border-color: var(--cc-accent, #4f46e5);
    color: #fff;

    &:hover {
      background-color: var(--cc-accent, #4f46e5);
      filter: brightness(1.08);
    }
  }

  &.dialog-btn-secondary {
    background-color: var(--cc-surface, #ffffff);
    border-color: var(--cc-border, #e5e7eb);
    color: var(--cc-text-2, #525c6b);

    &:hover {
      background-color: var(--cc-surface-2, #f7f9fb);
      color: var(--cc-text, #1f242d);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--cc-accent, #4f46e5);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.track-dialog-value {
  font-size: 13px;
  color: var(--cc-text, #1f242d);
  padding: 6px 0;
}

.track-dialog-btn {
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: $fw-r-ctl;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  &--cancel {
    background-color: var(--cc-surface, #ffffff);
    border-color: var(--cc-border, #e5e7eb);
    color: var(--cc-text-2, #525c6b);

    &:hover {
      background-color: var(--cc-surface-2, #f7f9fb);
      color: var(--cc-text, #1f242d);
    }
  }

  &--save {
    background-color: var(--cc-accent, #4f46e5);
    border-color: var(--cc-accent, #4f46e5);
    color: #fff;

    &:hover {
      filter: brightness(1.08);
    }
  }
}

/* ---- Tab settings dialog ---- */
.tab-settings-item {
  padding: 14px 20px;
  border-bottom: 1px solid var(--cc-border-soft, #f0f0f0);

  &:last-child {
    border-bottom: none;
  }
}

.tab-settings-item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.tab-settings-name-input {
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 10px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  outline: none;
  flex: 1;
  color: var(--cc-text, #1f242d);

  &:focus {
    border-color: var(--cc-accent, #4f46e5);
    box-shadow: 0 0 0 3px var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
  }
}

.tab-settings-visible {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--cc-text-2, #525c6b);
  white-space: nowrap;
  cursor: pointer;
}

.tab-settings-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.tab-settings-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--cc-text-2, #525c6b);
  cursor: pointer;
}

.tab-settings-empty {
  font-size: 12.5px;
  color: var(--cc-text-3, #97a0af);
}

/* ============================================================================
   Administration tab (design: .lvl-adm-bar / .lvl-adm-tbl registers)
============================================================================ */
/* The table scrolls inside its own card, as on the first two tabs - so this
   column only distributes height and never scrolls itself. */
.admin-tab {
  position: relative;
  padding: 14px 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

.admin-saving-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--cc-surface, #ffffff);
  opacity: 0.72;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

/* design: .lvl-adm-bar */
.admin-section {
  flex: 0 0 auto;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-card;
  padding: 12px 16px;
  margin-bottom: 14px;
}

.admin-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-section-label {
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  font-weight: 700;
  letter-spacing: 0.015em;
  color: var(--cc-text-3, #97a0af);
}

.admin-section-actions {
  display: flex;
  gap: 8px;
}

.admin-framework-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
}

.admin-framework-name {
  font-size: 13.5px;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: -0.005em;
  color: var(--cc-text, #1f242d);
}

.admin-framework-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.admin-entity-tabs-items {
  background: transparent !important;
}

/* A free-standing card, like the grids on the first two tabs. It used to butt
   up against the v-tabs bar - hence no top border and bottom-only corners -
   but the register picker is a detached pill now, so it closes on all sides.
   `overflow: hidden` is what clips the sticky header fill to the corners. */
/* align-items: flex-start so the panel is sized by its rows rather than
   stretched to fill - the card should not leave white space under a short
   register. max-height on the panel puts the ceiling back, so a long one still
   scrolls internally. */
.admin-entity-panels {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: flex-start;
}

/* v-show sets display:none inline, which wins over this. */
.admin-entity-panel {
  flex: 1;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  display: flex;
}

.admin-entity-split {
  display: flex;
  gap: 0;
  flex: 1;
  min-height: 0;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-card;
  overflow: hidden;
}

.admin-entity-list {
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 0;
  /* The scroll container: the header sticks to its top edge. */
  overflow: auto;
}

/* A quiet line above the grid, not a boxed banner - it describes the table
   rather than warning about anything. */
.ltf-scope-note {
  flex: 0 0 auto;
  margin: 0 0 8px 10px;
  padding: 0 2px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--cc-text-2, #525c6b);
}

/* Sticky header for the register table, as on the first two tabs. Scoped to the
   direct child so the nested sub-factor tables (which share .admin-table inside
   a cell) do not also stick and float over this one. The inset shadow draws the
   bottom rule: under `border-collapse: collapse` a sticky cell's own border is
   not painted while it is stuck. */
.admin-entity-list > .admin-table > thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: inset 0 -1px 0 var(--cc-border, #e5e7eb);
}

/* design: .lvl-adm-tbl */
.admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: inherit;

  th {
    text-align: left;
    padding: 10px 14px;
    border-bottom: 1px solid var(--cc-border, #e5e7eb);
    background: var(--cc-surface-2, #f7f9fb);
    font-weight: 700;
    color: var(--cc-text-3, #97a0af);
    font-size: 12px;
    text-transform: none;
    letter-spacing: 0.015em;
    white-space: nowrap;
  }

  td {
    padding: 10px 14px;
    border-bottom: 1px solid var(--cc-border-soft, #f0f0f0);
    vertical-align: middle;
    color: var(--cc-text-2, #525c6b);
  }

  tbody tr:hover {
    background-color: var(--cc-surface-2, #f7f9fb);
  }
}

.admin-td-pos {
  width: 44px;
  text-align: center;
  color: var(--cc-text-3, #97a0af);
}

.admin-td-desc {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--cc-text-2, #525c6b);
}

.admin-td-actions {
  width: 60px;
  text-align: right;
  white-space: nowrap;

  .v-icon {
    cursor: pointer;
    margin-left: 4px;
    opacity: 0.65;

    &:hover {
      opacity: 1;
    }
  }
}

.admin-td-empty {
  text-align: center;
  color: var(--cc-text-3, #97a0af);
  padding: 16px !important;
}

/* Sub-Factor expansion in factor table */
.admin-td-expand {
  width: 28px;
  text-align: center;
  cursor: pointer;
  padding: 4px !important;
}

.admin-sf-expansion-row > td {
  padding: 0 !important;
  background: var(--cc-surface-2, #f7f9fb);
}

.admin-sf-expansion-cell {
  padding: 0 !important;
}

.admin-sf-expansion {
  padding: 8px 12px 12px 56px;
}

.admin-sub-factors {
  border-top: 1px solid var(--cc-border, #e5e7eb);
  padding-top: 8px;
}

.admin-sf-expansion .admin-table th {
  background: var(--cc-surface-3, #eef1f4);
}

.admin-sfl-table {
  font-size: 12px;

  th, td {
    padding: 7px 12px;
  }
}

.admin-sfl-input {
  font-size: 12px;
  padding: 2px 6px;
  width: 100%;
}

.admin-colour-chip {
  width: 20px;
  height: 20px;
  border-radius: $fw-r-chip;
  border: 1px solid var(--cc-border, #e5e7eb);
  display: inline-block;
  flex-shrink: 0;
}

.admin-colour-picker {
  position: relative;
}

.admin-colour-selected {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.admin-colour-arrow {
  margin-left: auto;
  color: var(--cc-text-3, #97a0af) !important;
}

.admin-colour-none {
  color: var(--cc-text-3, #97a0af);
}

.admin-colour-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-panel;
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, 0.14));
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.admin-colour-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--cc-text-2, #525c6b);

  &:hover {
    background-color: var(--cc-surface-2, #f7f9fb);
  }
}

.admin-colour-option-active {
  background-color: var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
  color: var(--cc-text, #1f242d);
}

.admin-form-field {
  margin-bottom: 12px;
  width: 100%;

  label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--cc-text-2, #525c6b);
    letter-spacing: 0.015em;
    margin-bottom: 5px;
    font-family: inherit;
  }
}

.admin-form-row {
  display: flex;
  gap: 16px;

  .admin-form-field {
    flex: 1;
  }
}

.admin-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.45;
  color: var(--cc-text, #1f242d);
  background: var(--cc-surface, #ffffff);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--cc-accent, #4f46e5);
    box-shadow: 0 0 0 3px var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
  }

  &::placeholder {
    color: var(--cc-text-3, #97a0af);
  }

  &[readonly] {
    background: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text-2, #525c6b);
  }
}

.admin-input-error {
  border-color: var(--cc-danger, #b3261e) !important;
  box-shadow: 0 0 0 3px rgba(179, 38, 30, 0.1) !important;
}

.admin-required {
  color: var(--cc-danger, #b3261e);
  font-weight: 700;
}

.admin-field-error {
  display: block;
  color: var(--cc-danger, #b3261e);
  font-size: 11.5px;
  margin-top: 3px;
}

textarea.admin-input {
  resize: vertical;
}

select.admin-input {
  appearance: auto;
}

.admin-checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--cc-accent, #4f46e5);
  }
}

/* design: the neutral outlined button used across the framework chrome */
.action-button, .action-button-small, .dialog-btn {
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: $fw-r-ctl;
  background-color: var(--cc-surface, #ffffff);
  color: var(--cc-text-2, #525c6b);
  height: 32px;
  padding: 0 12px;
  font-family: inherit;
  font-weight: 600;
  font-size: 12.5px;
  letter-spacing: 0.005em;
  line-height: 1.2;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;

  ::v-deep .v-icon {
    color: currentColor;
    font-size: 16px;
  }

  &:hover {
    background-color: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text, #1f242d);
  }
}

.action-button-small {
  height: 26px;
  padding: 0 8px;
}

/* ---- Import framework dialog ---- */
.import-framework-list {
  max-height: 400px;
  overflow-y: auto;
}

.import-client-group {
  margin-bottom: 10px;
}

.import-client-name {
  font-weight: 700;
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
  padding: 6px 4px 4px;
  text-transform: none;
  letter-spacing: 0.015em;
}

.import-framework-item {
  padding: 9px 12px;
  cursor: pointer;
  border-radius: $fw-r-ctl;
  border: 1px solid transparent;
}

.import-framework-item:hover {
  background: var(--cc-surface-2, #f7f9fb);
}

.import-framework-selected {
  background: var(--cc-accent-soft, rgba(29, 91, 214, 0.08));
  border-color: var(--cc-accent, #4f46e5);
}

.import-framework-name {
  font-weight: 600;
  font-size: 13px;
  color: var(--cc-text, #1f242d);
}

.import-framework-desc {
  font-size: 12px;
  color: var(--cc-text-3, #97a0af);
  margin-top: 2px;
}
</style>

<!-- Vuetify moves tooltip content to the [data-app] root, outside this
     component's scoped tree, so it cannot be reached from the scoped block
     above. Mirrors the design's .rm-tip: a white card rather than Vuetify's
     dark chip. -->
<style lang="scss">
.v-tooltip__content.fw-tip {
  max-width: 320px;
  padding: 9px 12px;
  background: var(--cc-surface, #ffffff) !important;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, 0.14));
  color: var(--cc-text-2, #525c6b);
  font-family: inherit;
  font-weight: 400;
  font-size: 12.5px;
  line-height: 1.5;
  letter-spacing: 0;
  text-wrap: pretty;
  /* Vuetify fades its chip to .9; the design's card is fully opaque. */
  opacity: 1 !important;
}
</style>
